import get from 'lodash.get';
import React from 'react';
import calulateTextWidth from 'string-pixel-width';
import * as dateFns from 'date-fns';
import deepmerge from 'deepmerge';
import * as dateFnsTz from 'date-fns-tz';
import { jsonDateParser } from 'json-date-parser';
import queryString from 'query-string';
import urlJoin from 'url-join';
import generateEmojiRegex from 'emoji-regex';
import Joi from 'joi';
import joiPasswordComplexity from 'joi-password-complexity';

const flatten = (object, seperator, options = {}) => {
    const finalOptions = {
        prefix: "",
        checkIsNested: (value) => typeof value === "object" && value !== null,
        ...options,
    };
    return Object.entries(object).reduce((xAccumulator, [xKey, xValue]) => {
        const name = `${
            finalOptions.prefix === "" ? "" : `${finalOptions.prefix}${seperator}`
        }${xKey}`;
        if (finalOptions.checkIsNested(xValue)) {
            return {
                ...xAccumulator,
                ...flatten(xValue, seperator, {
                    prefix: name,
                    checkIsNested: finalOptions.checkIsNested,
                }),
            };
        }
        // eslint-disable-next-line no-param-reassign
        xAccumulator[name] = xValue;
        return xAccumulator;
    }, {});
};

const EventFormat = {
    CONTEST: "CONTEST",
    COMPETITION: "COMPETITION",
};

const formatEventFormat = (eventFormat) => {
    switch (eventFormat) {
        case EventFormat.CONTEST:
            return "Wettbewerb";
        case EventFormat.COMPETITION:
            return "Wettkampf";
        default:
            throw new Error(`Invalid event format: ${eventFormat}`);
    }
};

const eventFormatToAgeGroupBoundariesMap = {
    [EventFormat.CONTEST]: {
        min: 6,
        max: 25,
    },
    [EventFormat.COMPETITION]: {
        min: 6,
        max: 25,
    },
};

const generateAgeGroups = (ageGroupBoundaries) =>
    Array.from(
        { length: ageGroupBoundaries.max - ageGroupBoundaries.min + 1 },
        (_, xIndex) => ageGroupBoundaries.min + xIndex,
    );

const eventFormatToAgeGroupsMap = {
    [EventFormat.CONTEST]: generateAgeGroups(
        eventFormatToAgeGroupBoundariesMap[EventFormat.CONTEST],
    ),
    [EventFormat.COMPETITION]: generateAgeGroups(
        eventFormatToAgeGroupBoundariesMap[EventFormat.COMPETITION],
    ),
};

const AppErrorTypeGroupNames = {
    EXTERNAL: "EXTERNAL",
    INTERNAL: "INTERNAL",
    INVALID: "INVALID",
    NOT_FOUND: "NOT_FOUND",
    UNAUTHENTICATED: "UNAUTHENTICATED",
    UNAUTHORIZED: "UNAUTHORIZED",
};

const AppErrorType = {
    [AppErrorTypeGroupNames.EXTERNAL]: {
        E_SIGNATURES: "EXTERNAL_E_SIGNATURES",
        GCLOUD: "EXTERNAL_GCLOUD",
        MAILER_LITE: "EXTERNAL_MAILER_LITE",
        MAILER_SEND: "EXTERNAL_MAILER_SEND",
        STRIPE: "EXTERNAL_STRIPE",
    },
    [AppErrorTypeGroupNames.INTERNAL]: {
        NOT_IMPLEMENTED: "INTERNAL_NOT_IMPLEMENTED",
        MAINTENANCE_MODE: "INTERNAL_MAINTENANCE_MODE",
        OTHER: "INTERNAL_OTHER",
    },
    [AppErrorTypeGroupNames.INVALID]: {
        AUTHORIZATION_REFRESH_TOKEN: "INVALID_AUTHORIZATION_REFRESH_TOKEN",
        AUTOMATIC_CRON_INVOCATION_PROTECTION: "INVALID_AUTOMATIC_CRON_INVOCATION_PROTECTION",
        CERT_TEMPLATE_PRESET: "INVALID_CERT_TEMPLATE_PRESET",
        DISCOUNT_CODE: "INVALID_DISCOUNT_CODE",
        EVENT_FORMAT: "INVALID_EVENT_FORMAT",
        EVENT_IS_IN_ACTIVE: "INVALID_EVENT_IS_IN_ACTIVE",
        EVENT_LIMIT_REACHED: "INVALID_EVENT_LIMIT_REACHED",
        EVENT_NAME_ALREADY_USED: "INVALID_EVENT_NAME_ALREADY_USED",
        GROUP_LIMIT_REACHED: "INVALID_GROUP_LIMIT_REACHED",
        GROUP_NAME_ALREADY_USED: "INVALID_GROUP_NAME_ALREADY_USED",
        HELPER_IS_IN_ACTIVE: "INVALID_HELPER_IS_IN_ACTIVE",
        HELPER_LIMIT_REACHED: "INVALID_HELPER_LIMIT_REACHED",
        HELPER_USERNAME_ALREADY_USED: "INVALID_HELPER_USERNAME_ALREADY_USED",
        MAIL_ADDRESS_ALREADY_USED: "INVALID_MAIL_ADDRESS_ALREADY_USED",
        MAIL_ADDRESS_NOT_VERIFIED: "INVALID_MAIL_ADDRESS_NOT_VERIFIED",
        VERIFIABLE_MAIL_ADDRESS_EQUALS_MAIL_ADDRESS:
            "INVALID_VERIFIABLE_MAIL_ADDRESS_EQUALS_MAIL_ADDRESS",
        MAIL_ADDRESS_VERIFICATION_TOKEN: "INVALID_MAIL_ADDRESS_VERIFICATION_TOKEN",
        NEW_PASSWORD_EQUALS_OLD_PASSWORD: "NEW_PASSWORD_EQUALS_OLD_PASSWORD",
        ORIGIN: "INVALID_ORIGIN",
        PARTICIPANT_LIMIT_REACHED: "INVALID_PARTICIPANT_LIMIT_REACHED",
        PASSWORD: "INVALID_PASSWORD",
        PASSWORD_CHANGE_TOKEN: "INVALID_PASSWORD_CHANGE_TOKEN",
        REMOVAL_TOKEN: "INVALID_REMOVAL_TOKEN",
        REQUEST_BODY: "INVALID_REQUEST_BODY",
        REQUEST_PARAMS: "INVALID_REQUEST_PARAMS",
        REQUEST_QUERY_PARAMS: "INVALID_REQUEST_QUERY_PARAMS",
        RESULT_LIMIT_REACHED: "INVALID_RESULT_LIMIT_REACHED",
        SCHOOL_DPA_STATE: "INVALID_SCHOOL_DPA_STATE",
        SPECIAL_DISCOUNT_CODE: "INVALID_SPECIAL_DISCOUNT_CODE",
        VERSION_OUTDATED: "INVALID_VERSION_OUTDATED",
        VERSION_HEADER_FORMAT: "INVALID_VERSION_HEADER_FORMAT",
        VERSION_HEADER_MISSING: "INVALID_VERSION_HEADER_MISSING",
    },
    NETWORK: "NETWORK",
    [AppErrorTypeGroupNames.NOT_FOUND]: {
        CERT_TEMPLATE_SET: "NOT_FOUND_CERT_TEMPLATE_SET",
        CERT: "NOT_FOUND_CERT",
        DISCIPLINE_PRESELECTION: "NOT_FOUND_DISCIPLINE_PRESELECTION",
        DISCIPLINE: "NOT_FOUND_DISCIPLINE",
        EVENT: "NOT_FOUND_EVENT",
        GENERATED_CERT: "NOT_FOUND_GENERATED_CERT",
        GROUP: "NOT_FOUND_GROUP",
        HELPER: "NOT_FOUND_HELPER",
        MIGRATION: "NOT_FOUND_MIGRATION",
        PARTICIPANT: "NOT_FOUND_PARTICIPANT",
        RESULT: "NOT_FOUND_RESULT",
        ROUTE: "NOT_FOUND_ROUTE",
        SCHOOL: "NOT_FOUND_SCHOOL",
        SPECIAL_DISCOUNT: "NOT_FOUND_SPECIAL_DISCOUNT",
    },
    OTHER: "OTHER",
    [AppErrorTypeGroupNames.UNAUTHENTICATED]: {
        PERMISSION: "UNAUTHENTICATED_PERMISSION",
        ROLE: "UNAUTHENTICATED_ROLE",
        SCHOOL_IS_NOT_VERIFIED: "UNAUTHENTICATED_SCHOOL_IS_NOT_VERIFIED",
    },
    [AppErrorTypeGroupNames.UNAUTHORIZED]: {
        AUTHORIZATION_HEADER_FORMAT: "UNAUTHORIZED_AUTHORIZATION_HEADER_FORMAT",
        AUTHORIZATION_HEADER_MISSING: "UNAUTHORIZED_AUTHORIZATION_HEADER_MISSING",
        CREDENTIALS: "UNAUTHORIZED_CREDENTIALS",
        E_SIGNATURES_CLIENT: "UNAUTHORIZED_E_SIGNATURES_CLIENT",
        GCLOUD_OAUTH2_CLIENT: "UNAUTHORIZED_GCLOUD_OAUTH2_CLIENT",
        JWT_FORMAT: "UNAUTHORIZED_JWT_FORMAT",
        SCHOOL_CREATED: "UNAUTHORIZED_SCHOOL_CREATED",
        SCHOOL_MAIL_ADDRESS: "UNAUTHORIZED_SCHOOL_MAIL_ADDRESS",
        STRIPE_CLIENT: "UNAUTHORIZED_STRIPE_CLIENT",
    },
};

const FlatAppErrorType = flatten(AppErrorType, "_");

const formatAppErrorType = (appErrorType) => {
    switch (appErrorType) {
        case AppErrorType.EXTERNAL.E_SIGNATURES:
            return "Es ist ein externer Fehler mit dem Vertragsversand aufgetreten.";
        case AppErrorType.EXTERNAL.GCLOUD:
            return "Es ist ein externer Fehler mit dem Datenspeicher aufgetreten.";
        case AppErrorType.EXTERNAL.MAILER_LITE:
            return "Es ist ein externer Fehler mit dem Mailversender aufgetreten.";
        case AppErrorType.EXTERNAL.MAILER_SEND:
            return "Es ist ein externer Fehler mit dem Mailversender aufgetreten.";
        case AppErrorType.EXTERNAL.STRIPE:
            return "Es ist ein externer Fehler mit dem Zahlungsabwickler aufgetreten.";
        case AppErrorType.INTERNAL.NOT_IMPLEMENTED:
            return "Die Funktion ist nicht implementiert.";
        case AppErrorType.INTERNAL.MAINTENANCE_MODE:
            return "Der Server ist im Wartungsmodus.";
        case AppErrorType.INTERNAL.OTHER:
            return "Es ist ein interner Serverfehler aufgetreten.";
        case AppErrorType.INVALID.AUTHORIZATION_REFRESH_TOKEN:
            return "Abgemeldet.";
        case AppErrorType.INVALID.AUTOMATIC_CRON_INVOCATION_PROTECTION:
            return "Die Funktion darf nicht automatisch aufgerufen werden.";
        case AppErrorType.INVALID.CERT_TEMPLATE_PRESET:
            return "Das Urkundenvorlagen-Preset ist ungültig.";
        case AppErrorType.INVALID.DISCOUNT_CODE:
            return "Der Discount-Code ist ungültig.";
        case AppErrorType.INVALID.EVENT_FORMAT:
            return "Das Veranstaltungsformat ist ungültig.";
        case AppErrorType.INVALID.EVENT_IS_IN_ACTIVE:
            return "Die Veranstaltung ist zeitlich abgelaufen.";
        case AppErrorType.INVALID.EVENT_LIMIT_REACHED:
            return "Die max. Veranstaltungsanzahl ist erreicht.";
        case AppErrorType.INVALID.EVENT_NAME_ALREADY_USED:
            return "Der Name ist bereits in Verwendung.";
        case AppErrorType.INVALID.GROUP_LIMIT_REACHED:
            return "Die max. Riegenanzahl ist erreicht.";
        case AppErrorType.INVALID.GROUP_NAME_ALREADY_USED:
            return "Der Name ist bereits in Verwendung.";
        case AppErrorType.INVALID.HELPER_IS_IN_ACTIVE:
            return "Der Helfer ist inaktiv.";
        case AppErrorType.INVALID.HELPER_LIMIT_REACHED:
            return "Die max. Helferanzahl ist erreicht.";
        case AppErrorType.INVALID.HELPER_USERNAME_ALREADY_USED:
            return "Der Benutzername ist bereits in Verwendung.";
        case AppErrorType.INVALID.MAIL_ADDRESS_ALREADY_USED:
            return "Die Email-Adresse ist bereits in Verwendung.";
        case AppErrorType.INVALID.MAIL_ADDRESS_NOT_VERIFIED:
            return "Die Email-Adresse ist nicht verifiziert.";
        case AppErrorType.INVALID.VERIFIABLE_MAIL_ADDRESS_EQUALS_MAIL_ADDRESS:
            return "Die neue Email-Adresse darf nicht gleich der aktuellen Email-Adresse sein.";
        case AppErrorType.INVALID.MAIL_ADDRESS_VERIFICATION_TOKEN:
            return "Die Anfrage ist abgelaufen.";
        case AppErrorType.INVALID.NEW_PASSWORD_EQUALS_OLD_PASSWORD:
            return "Das neue Passwort darf nicht gleich dem alten Passwort sein.";
        case AppErrorType.INVALID.ORIGIN:
            return "Von diesem Gerät können keine Anfragen and den Server gesendet werden.";
        case AppErrorType.INVALID.PARTICIPANT_LIMIT_REACHED:
            return "Die max. Teilnehmeranzahl ist erreicht.";
        case AppErrorType.INVALID.PASSWORD:
            return "Das Passwort ist falsch.";
        case AppErrorType.INVALID.PASSWORD_CHANGE_TOKEN:
            return "Die Anfrage ist abgelaufen.";
        case AppErrorType.INVALID.REMOVAL_TOKEN:
            return "Die Anfrage ist abgelaufen.";
        case AppErrorType.INVALID.REQUEST_BODY:
            return "Die Eingabedaten sind ungültig.";
        case AppErrorType.INVALID.REQUEST_PARAMS:
            return "Die Eingabedaten sind ungültig.";
        case AppErrorType.INVALID.REQUEST_QUERY_PARAMS:
            return "Die Eingabedaten sind ungültig.";
        case AppErrorType.INVALID.RESULT_LIMIT_REACHED:
            return "Die max. Ergebnisanzahl ist erreicht.";
        case AppErrorType.INVALID.SCHOOL_DPA_STATE:
            return "Der Status des Auftragsverarbeitungsvertrags ist ungültig.";
        case AppErrorType.INVALID.SPECIAL_DISCOUNT_CODE:
            return "Der Discount-Code ist ungültig.";
        case AppErrorType.INVALID.VERSION_OUTDATED:
            return "Die Version ist ungültig.";
        case AppErrorType.INVALID.VERSION_HEADER_FORMAT:
            return "Die Version ist ungültig.";
        case AppErrorType.INVALID.VERSION_HEADER_MISSING:
            return "Die Version ist ungültig.";
        case AppErrorType.NETWORK:
            return "Es ist ein Netzwerkfehler aufgetreten.";
        case AppErrorType.NOT_FOUND.CERT_TEMPLATE_SET:
            return "Das Urkundenvorlagen-Set wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.CERT:
            return "Die Urkunde wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.DISCIPLINE_PRESELECTION:
            return "Die Disziplinvorauswahl wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.DISCIPLINE:
            return "Die Disziplin wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.EVENT:
            return "Die Veranstaltung wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.GENERATED_CERT:
            return "Die generierte Urkunde wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.GROUP:
            return "Die Riege wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.HELPER:
            return "Der Helfer wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.MIGRATION:
            return "Die Migration wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.PARTICIPANT:
            return "Der Teilnehmer wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.RESULT:
            return "Das Ergebnis wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.ROUTE:
            return "Die URL wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.SCHOOL:
            return "Die Schule wurde nicht gefunden.";
        case AppErrorType.NOT_FOUND.SPECIAL_DISCOUNT:
            return "Der Discount-Code wurde nicht gefunden.";
        case AppErrorType.OTHER:
            return "Es ist ein Fehler aufgetreten.";
        case AppErrorType.UNAUTHENTICATED.PERMISSION:
            return "Nicht berechtigt.";
        case AppErrorType.UNAUTHENTICATED.ROLE:
            return "Nicht berechtigt.";
        case AppErrorType.UNAUTHENTICATED.SCHOOL_IS_NOT_VERIFIED:
            return "Die Schule ist nicht verifiziert.";
        case AppErrorType.UNAUTHORIZED.AUTHORIZATION_HEADER_FORMAT:
            return "Nicht berechtigt.";
        case AppErrorType.UNAUTHORIZED.AUTHORIZATION_HEADER_MISSING:
            return "Nicht berechtigt.";
        case AppErrorType.UNAUTHORIZED.CREDENTIALS:
            return "Nicht berechtigt.";
        case AppErrorType.UNAUTHORIZED.E_SIGNATURES_CLIENT:
            return "Der E-Signatures Client ist nicht berechtigt.";
        case AppErrorType.UNAUTHORIZED.GCLOUD_OAUTH2_CLIENT:
            return "Der Gcloud OAuth2 Client ist nicht berechtigt.";
        case AppErrorType.UNAUTHORIZED.JWT_FORMAT:
            return "Nicht berechtigt.";
        case AppErrorType.UNAUTHORIZED.SCHOOL_CREATED:
            return "Das Erstelldatum der Schule ist falsch.";
        case AppErrorType.UNAUTHORIZED.SCHOOL_MAIL_ADDRESS:
            return "Die Email-Adresse der Schule ist falsch.";
        case AppErrorType.UNAUTHORIZED.STRIPE_CLIENT:
            return "Stripe Client ist nicht berechtigt.";
        default:
            return "Es ist ein unbekannter Fehler aufgetreten.";
    }
};

const formatAppError = (appError) => {
    if (
        !Object.values(FlatAppErrorType).includes(appError.type) &&
        appError.customMessage !== undefined
    ) {
        return appError.customMessage;
    }
    return formatAppErrorType(appError.type);
};

const CertGenerationErrorType = {
    // Contest event
    DISCIPLINE_CATEGORY_SPECIFIC: "DISCIPLINE_CATEGORY_SPECIFIC",
    NOT_EVALUATED_YET: "NOT_EVALUATED_YET",
    NOT_IN_A_GROUP: "NOT_IN_A_GROUP",
    NOT_DONE_COMMON_DISCIPLINE_EXCLUSIVELY: "NOT_DONE_COMMON_DISCIPLINE_EXCLUSIVELY",
    NOT_ENOUGH_RESULTS: "NOT_ENOUGH_RESULTS",
    ROOT: "ROOT",
    // Competition event
    NOT_DONE_DISCIPLINE_CATEGORY: "NOT_DONE_DISCIPLINE_CATEGORY",
    NOT_DONE_ENOUGH_DISCIPLINE_CATEGORIES: "NOT_DONE_ENOUGH_DISCIPLINE_CATEGORIES",
};

const formatCertGenerationErrorType = (certGenerationError) => {
    switch (certGenerationError.type) {
        case CertGenerationErrorType.DISCIPLINE_CATEGORY_SPECIFIC:
            return "Disziplinskategorie spezifisch";
        case CertGenerationErrorType.NOT_EVALUATED_YET:
            return "";
        case CertGenerationErrorType.NOT_IN_A_GROUP:
            return "Ist in keiner Riege";
        case CertGenerationErrorType.NOT_DONE_COMMON_DISCIPLINE_EXCLUSIVELY:
            return `Nicht die Disziplin ausgeführt, die die Riege ausgeführt hat (Erwartet: ${certGenerationError.targetDisciplineName})`;
        case CertGenerationErrorType.NOT_ENOUGH_RESULTS:
            return "Nicht genügend Ergebnisse für die Disziplin ausgeführt";
        case CertGenerationErrorType.ROOT:
            return "";
        case CertGenerationErrorType.NOT_DONE_DISCIPLINE_CATEGORY:
            return "Disziplinskategorie nicht ausgeführt";
        case CertGenerationErrorType.NOT_DONE_ENOUGH_DISCIPLINE_CATEGORIES:
            return "Nicht genügend Disziplinskategorien ausgeführt";
        default:
            throw new Error(`Invalid cert generation error: ${certGenerationError.type}`);
    }
};

const CertSortingPreset = {
    PARTICIPANT_CLASS: "PARTICIPANT_CLASS",
    PARTICIPANT_GROUP_NAME: "PARTICIPANT_GROUP_NAME",
    PARTICIPANT_NAME: "PARTICIPANT_NAME",
    POINTS: "POINTS",
};

const generateCertSorting = (certSortingPreset, order) => {
    switch (certSortingPreset) {
        case CertSortingPreset.PARTICIPANT_CLASS:
            return [
                {
                    path: "participant.class",
                    order,
                },
                {
                    path: "participant.name.last",
                    order,
                },
                {
                    path: "participant.name.first",
                    order,
                },
                {
                    path: "participant.schoolInternalId",
                    order,
                },
                {
                    path: "participant.group.name",
                    order,
                },
                {
                    path: "points",
                    order,
                },
                {
                    path: "type",
                    searchablePath: "_searchableType",
                    order,
                },
                {
                    path: "runningPoints",
                    order,
                },
                {
                    path: "jumpingPoints",
                    order,
                },
                {
                    path: "throwingPoints",
                    order,
                },
            ];
        case CertSortingPreset.PARTICIPANT_GROUP_NAME:
            return [
                {
                    path: "participant.group.name",
                    order,
                },
                {
                    path: "participant.name.last",
                    order,
                },
                {
                    path: "participant.name.first",
                    order,
                },
                {
                    path: "participant.schoolInternalId",
                    order,
                },
                {
                    path: "participant.class",
                    order,
                },
                {
                    path: "points",
                    order,
                },
                {
                    path: "type",
                    searchablePath: "_searchableType",
                    order,
                },
                {
                    path: "runningPoints",
                    order,
                },
                {
                    path: "jumpingPoints",
                    order,
                },
                {
                    path: "throwingPoints",
                    order,
                },
            ];
        case CertSortingPreset.PARTICIPANT_NAME:
            return [
                {
                    path: "participant.name.last",
                    order,
                },
                {
                    path: "participant.name.first",
                    order,
                },
                {
                    path: "participant.schoolInternalId",
                    order,
                },
                {
                    path: "participant.group.name",
                    order,
                },
                {
                    path: "points",
                    order,
                },
                {
                    path: "type",
                    searchablePath: "_searchableType",
                    order,
                },
                {
                    path: "runningPoints",
                    order,
                },
                {
                    path: "jumpingPoints",
                    order,
                },
                {
                    path: "throwingPoints",
                    order,
                },
            ];
        case CertSortingPreset.POINTS:
            return [
                {
                    path: "points",
                    order,
                },
                {
                    path: "participant.name.last",
                    order,
                },
                {
                    path: "participant.name.first",
                    order,
                },
                {
                    path: "participant.schoolInternalId",
                    order,
                },
                {
                    path: "participant.class",
                    order,
                },
                {
                    path: "participant.group.name",
                    order,
                },
                {
                    path: "type",
                    searchablePath: "_searchableType",
                    order,
                },
                {
                    path: "runningPoints",
                    order,
                },
                {
                    path: "jumpingPoints",
                    order,
                },
                {
                    path: "throwingPoints",
                    order,
                },
            ];
        default:
            throw new Error(`Invalid cert sorting preset: ${certSortingPreset}`);
    }
};

const formatCertSortingPreset = (certSortingPreset) => {
    switch (certSortingPreset) {
        case CertSortingPreset.PARTICIPANT_CLASS:
            return "Teilnehmer Klasse";
        case CertSortingPreset.PARTICIPANT_GROUP_NAME:
            return "Teilnehmer Riege";
        case CertSortingPreset.PARTICIPANT_NAME:
            return "Teilnehmer Name";
        case CertSortingPreset.POINTS:
            return "Punkte";
        default:
            throw new Error(`Invalid cert sorting preset: ${certSortingPreset}`);
    }
};

const CertTemplateElementPurpose = {
    ALIGNMENT_GUIDE: "ALIGNMENT_GUIDE",
    STANDARD: "STANDARD",
    EXTRA: "EXTRA",
};

const CertTemplateVerticalTextAlignment = {
    TOP: "TOP",
    // CENTER: "CENTER",
    BOTTOM: "BOTTOM",
};

const formatCertTemplateVerticalTextAlignment = (certTemplateVerticalTextAlignment) => {
    switch (certTemplateVerticalTextAlignment) {
        case CertTemplateVerticalTextAlignment.TOP:
            return "Oben";
        // case CertTemplateVerticalTextAlignment.CENTER:
        //     return "Mittig";
        case CertTemplateVerticalTextAlignment.BOTTOM:
            return "Unten";
        default:
            throw new Error(
                `Invalid cert template vertical text alignment: ${certTemplateVerticalTextAlignment}`,
            );
    }
};

const CertTemplateElementType = {
    LINE: "LINE",
    TEXT: "TEXT",
    RUBBER_STAMP: "RUBBER_STAMP",
    SIGNATURE: "SIGNATURE",
};

const formatCertTemplateElementType = (certTemplateElementType) => {
    switch (certTemplateElementType) {
        case CertTemplateElementType.LINE:
            return "Line";
        case CertTemplateElementType.TEXT:
            return "Text";
        case CertTemplateElementType.RUBBER_STAMP:
            return "Stempel";
        case CertTemplateElementType.SIGNATURE:
            return "Unterschrift";
        default:
            throw new Error(`Invalid cert template element type: ${certTemplateElementType}`);
    }
};

const CertTemplateHorizontalTextAlignment = {
    LEFT: "LEFT",
    CENTER: "CENTER",
    RIGHT: "RIGHT",
};

const formatCertTemplateHorizontalTextAlignment = (certTemplateHorizontalTextAlignment) => {
    switch (certTemplateHorizontalTextAlignment) {
        case CertTemplateHorizontalTextAlignment.LEFT:
            return "Links";
        case CertTemplateHorizontalTextAlignment.CENTER:
            return "Mittig";
        case CertTemplateHorizontalTextAlignment.RIGHT:
            return "Rechts";
        default:
            throw new Error(
                `Invalid cert template horizontal text alignment: ${certTemplateHorizontalTextAlignment}`,
            );
    }
};

const colors = {
    white: "#ffffff",
    grayShades: {
        0: "#f2f2f7",
        1: "#e5e5ea", // Divider
        2: "#d1d1d6",
        3: "#c7c7cc",
        4: "#aeaeb2",
        5: "#8e8e93",
    },
    grayBlack: "#8a898d",
    black: "#000000",
    amaranthShades: {
        50: "#fce8eb",
        100: "#f8c6cc",
        200: "#f4a1ab",
        300: "#f07b89",
        400: "#ec5e6f",
        500: "#e94256",
        600: "#e63c4f",
        700: "#e33345",
        800: "#df2b3c",
        900: "#d91d2b",
        A100: "#ffffff",
        A200: "#ffdcde",
        A400: "#ffa9ae",
        A700: "#ff9096",
    },
    mulberryShades: {
        50: "#f9e8ef",
        100: "#efc6d8",
        200: "#e4a0be",
        300: "#d97aa4",
        400: "#d15e91",
        500: "#c9417d",
        600: "#c33b75",
        700: "#bc326a",
        800: "#b52a60",
        900: "#a91c4d",
        A100: "#ffe0ea",
        A200: "#ffadc7",
        A400: "#ff7aa4",
        A700: "#ff6193",
    },
    redShades: {
        50: "#ffe7e6",
        100: "#ffc4c1",
        200: "#ff9d98",
        300: "#ff766e",
        400: "#ff584f",
        500: "#ff3b30",
        600: "#ff352b",
        700: "#ff2d24",
        800: "#ff261e",
        900: "#ff1913",
        A100: "#ffffff",
        A200: "#fffafa",
        A400: "#ffc8c7",
        A700: "#ffafad",
    },
    orangeShades: {
        50: "#fff2e0",
        100: "#ffdfb3",
        200: "#ffca80",
        300: "#ffb54d",
        400: "#ffa526",
        500: "#ff9500",
        600: "#ff8d00",
        700: "#ff8200",
        800: "#ff7800",
        900: "#ff6700",
        A100: "#ffffff",
        A200: "#fff7f2",
        A400: "#ffd5bf",
        A700: "#ffc4a6",
    },
    greenShades: {
        50: "#e7f8eb",
        100: "#c2eecd",
        200: "#9ae3ac",
        300: "#71d88b",
        400: "#52cf72",
        500: "#34c759",
        600: "#2fc151",
        700: "#27ba48",
        800: "#21b33e",
        900: "#15a62e",
        A100: "#d9ffde",
        A200: "#a6ffb3",
        A400: "#73ff87",
        A700: "#59ff71",
    },
    blueShades: {
        50: "#e6efff",
        100: "#c2d7ff",
        200: "#99bdff",
        300: "#70a2ff",
        400: "#518eff",
        500: "#327aff",
        600: "#2d72ff",
        700: "#2667ff",
        800: "#1f5dff",
        900: "#134aff",
        A100: "#ffffff",
        A200: "#fafbff",
        A400: "#c7d2ff",
        A700: "#adbeff",
    },
};

const CertType = {
    HONOR: "HONOR",
    WINNER: "WINNER",
    PARTICIPATION: "PARTICIPATION",
};

const formatCertType = (certType, { isShort = false, isPlural = false } = {}) => {
    let string;
    switch (certType) {
        case CertType.HONOR:
            string = isShort ? "Ehren" : "Ehrenurkunde";
            break;
        case CertType.WINNER:
            string = isShort ? "Sieger" : "Siegerurkunde";
            break;
        case CertType.PARTICIPATION:
            string = isShort ? "Teilnahme" : "Teilnahmeurkunde";
            break;
        default:
            throw new Error(`Invalid cert type: ${certType}`);
    }
    return isShort ? string : `${string}${isPlural ? "n" : ""}`;
};

const formatCertTypeToColor = (certType) => {
    switch (certType) {
        case CertType.HONOR:
            return colors.greenShades[400];
        case CertType.WINNER:
            return colors.orangeShades[400];
        case CertType.PARTICIPATION:
            return colors.redShades[400];
        default:
            throw new Error(`Invalid cert type: ${certType}`);
    }
};

const ClassLevelRange = {
    N_1_AND_2: "N_1_AND_2",
    N_3_AND_4: "N_3_AND_4",
    N_5_AND_6: "N_5_AND_6",
    N_7_AND_8: "N_7_AND_8",
    N_9_AND_10: "N_9_AND_10",
};

const formatClassLevelRange = (classLevelRange) => {
    switch (classLevelRange) {
        case ClassLevelRange.N_1_AND_2:
            return "1-2";
        case ClassLevelRange.N_3_AND_4:
            return "3-4";
        case ClassLevelRange.N_5_AND_6:
            return "5-6";
        case ClassLevelRange.N_7_AND_8:
            return "7-8";
        case ClassLevelRange.N_9_AND_10:
            return "9-10";
        default:
            throw new Error(`Invalid class level range: ${classLevelRange}`);
    }
};

const extractClassLevelRange = (string) => {
    const match = string.match(/\d+/);
    if (match !== null) {
        const classLevel = parseInt(match[0], 10);
        switch (classLevel) {
            case 1:
            case 2:
                return ClassLevelRange.N_1_AND_2;
            case 3:
            case 4:
                return ClassLevelRange.N_3_AND_4;
            case 5:
            case 6:
                return ClassLevelRange.N_5_AND_6;
            case 7:
            case 8:
                return ClassLevelRange.N_7_AND_8;
            case 9:
            case 10:
                return ClassLevelRange.N_9_AND_10;
        }
    }
    if (string.startsWith("EF")) {
        return ClassLevelRange.N_9_AND_10;
    }
    throw new Error(`Invalid class level range string: ${string}`);
};

const DisciplineEvaluationType = {
    ADD_ALL: "ADD_ALL",
    ADD_TOP_3: "ADD_TOP_3",
    BEST: "BEST",
    SINGLE: "SINGLE",
};

const formatDisciplineEvaluationType = (disciplineEvaluationType) => {
    switch (disciplineEvaluationType) {
        case DisciplineEvaluationType.ADD_ALL:
            return "Alle addieren";
        case DisciplineEvaluationType.ADD_TOP_3:
            return "Beste 3 addieren";
        case DisciplineEvaluationType.BEST:
            return "Besten Wert werten";
        case DisciplineEvaluationType.SINGLE:
            return "Einzelergebnis";
        default:
            throw new Error(
                `Invalid discipline evaluation type: ${disciplineEvaluationType}`,
            );
    }
};

const DpaState = {
    NONE: "NONE",
    SENT: "SENT",
    SIGNED: "SIGNED",
};

const Gender = {
    MALE: "MALE",
    FEMALE: "FEMALE",
};

const formatGender = (gender) => {
    switch (gender) {
        case Gender.MALE:
            return "Männlich";
        case Gender.FEMALE:
            return "Weiblich";
        default:
            throw new Error(`Invalid gender: ${gender}`);
    }
};

const parseGender = (string) => {
    if (Object.values(Gender).includes(string)) {
        return string;
    }
    if (string.toUpperCase().startsWith("M")) {
        return Gender.MALE;
    }
    if (string.toUpperCase().startsWith("W") || string.toUpperCase().startsWith("F")) {
        return Gender.FEMALE;
    }
    throw new Error(`Invalid gender string: ${string}`);
};

const formatGenderToColor = (gender) => {
    switch (gender) {
        case Gender.MALE:
            return colors.blueShades[400];
        case Gender.FEMALE:
            return colors.redShades[400];
        default:
            throw new Error(`Invalid gender: ${gender}`);
    }
};

const MixpanelEventName = {
    SCHOOL_SIGNED_UP: "SCHOOL_SIGNED_UP",
    SCHOOL_LOGGED_IN: "SCHOOL_LOGGED_IN",
    SCHOOL_UPDATED: "SCHOOL_UPDATED",
    SCHOOL_REQUESTED_DPA: "SCHOOL_REQUESTED_DPA",
    SCHOOL_DPA_SIGNED: "SCHOOL_DPA_SIGNED",
    SCHOOL_UPDATED_DATA_PRIVACY: "SCHOOL_UPDATED_DATA_PRIVACY",
    SCHOOL_UPDATED_FLOW: "SCHOOL_UPDATED_FLOW",
    SCHOOL_REQUESTED_MAIL_ADDRESS_CHANGE: "SCHOOL_REQUESTED_MAIL_ADDRESS_CHANGE",
    SCHOOL_CANCELLED_MAIL_ADDRESS_CHANGE: "SCHOOL_CANCELLED_MAIL_ADDRESS_CHANGE",
    SCHOOL_VERIFIED_MAIL_ADDRESS: "SCHOOL_VERIFIED_MAIL_ADDRESS",
    SCHOOL_FORGOT_PASSWORD: "SCHOOL_FORGOT_PASSWORD",
    SCHOOL_REQUESTED_PASSWORD_CHANGE: "SCHOOL_REQUESTED_PASSWORD_CHANGE",
    SCHOOL_CANCELLED_PASSWORD_CHANGE: "SCHOOL_CANCELLED_PASSWORD_CHANGE",
    SCHOOL_CHANGED_PASSWORD: "SCHOOL_CHANGED_PASSWORD",
    SCHOOL_REQUESTED_REMOVAL: "SCHOOL_REQUESTED_REMOVAL",
    SCHOOL_CANCELLED_REMOVAL: "SCHOOL_CANCELLED_REMOVAL",
    SCHOOL_REMOVED: "SCHOOL_REMOVED",
    EVENT_CREATED: "EVENT_CREATED",
    EVENT_REPORT_GENERATED: "EVENT_REPORT_GENERATED",
    EVENT_NAME_UPDATED: "EVENT_NAME_UPDATED",
    EVENT_REQUESTED_REMOVAL: "EVENT_REQUESTED_REMOVAL",
    EVENT_CANCELLED_REMOVAL: "EVENT_CANCELLED_REMOVAL",
    EVENT_REMOVED: "EVENT_REMOVED",
    DISCIPLINE_PRESELECTION_UPDATED: "DISCIPLINE_PRESELECTION_UPDATED",
    HELPER_CREATED: "HELPER_CREATED",
    HELPERS_IMPORTED: "HELPERS_IMPORTED",
    HELPER_LOGGED_IN: "HELPER_LOGGED_IN",
    HELPER_LOGIN_DATAS_OF_ALL_PRINTED: "HELPER_LOGIN_DATAS_OF_ALL_PRINTED",
    HELPER_UPDATED: "HELPER_UPDATED",
    HELPER_UPDATED_IS_ACTIVE_OF_ALL: "HELPER_UPDATED_IS_ACTIVE_OF_ALL",
    HELPER_REMOVED: "HELPER_REMOVED",
    HELPERS_REMOVED: "HELPERS_REMOVED",
    PARTICIPANT_CREATED: "PARTICIPANT_CREATED",
    PARTICIPANTS_IMPORTED: "PARTICIPANTS_IMPORTED",
    PARTICIPANT_ALLOWED_DISCIPLINES_OF_ALL: "PARTICIPANT_ALLOWED_DISCIPLINES_OF_ALL",
    PARTICIPANT_UPDATED: "PARTICIPANT_UPDATED",
    PARTICIPANT_REMOVED: "PARTICIPANT_REMOVED",
    PARTICIPANTS_REMOVED: "PARTICIPANTS_REMOVED",
    GROUP_CREATED: "GROUP_CREATED",
    GROUPS_UPDATED_ALL: "GROUPS_UPDATED_ALL",
    RESULT_CREATED: "RESULT_CREATED",
    RESULTS_IMPORT_IN_HELPER: "RESULTS_IMPORT_IN_HELPER",
    RESULTS_EXPORTED_ALL: "RESULTS_EXPORTED_ALL",
    RESULT_UPDATED: "RESULT_UPDATED",
    RESULT_REMOVED: "RESULT_REMOVED",
    CERTS_GENERATED_ALL: "CERTS_GENERATED_ALL",
    CERT_PRINTED: "CERT_PRINTED",
    CERTS_OVERVIEW_OF_ALL_PRINTED: "CERTS_OVERVIEW_OF_ALL_PRINTED",
    CERTS_EXPORTED_ALL: "CERTS_EXPORTED_ALL",
    CERTS_PRINTED_ALL: "CERTS_PRINTED_ALL",
    CERT_TEMPLATE_PRINTED_TEST: "CERT_TEMPLATE_PRINTED_TEST",
    CERT_TEMPLATE_UPDATED: "CERT_TEMPLATE_UPDATED",
    CERT_TEMPLATE_SET_RUBBER_STAMP_UPDATED: "CERT_TEMPLATE_SET_RUBBER_STAMP_UPDATED",
    CERT_TEMPLATE_SET_SIGN_UPDATED: "CERT_TEMPLATE_SET_SIGN_UPDATED",
};

const MeasuringType = {
    MANUALLY: "MANUALLY",
    ELECTRONICALLY: "ELECTRONICALLY",
};

const formatMeasuringType = (measuringType) => {
    switch (measuringType) {
        case MeasuringType.MANUALLY:
            return "Manuell";
        case MeasuringType.ELECTRONICALLY:
            return "Elektronisch";
        default:
            throw new Error(`Invalid measuring type: ${measuringType}`);
    }
};

const Order = {
    ASCENDING: "ASCENDING",
    DESCENDING: "DESCENDING",
};

const formatOrder = (order) => {
    switch (order) {
        case Order.ASCENDING:
            return "Aufsteigend";
        case Order.DESCENDING:
            return "Absteigend";
        default:
            throw new Error(`Invalid order: ${order}`);
    }
};

const ParticipantStarterlistDisciplineLevelOfDetail = {
    ALL_ALLOWED: "ALL_ALLOWED",
    ONLY_PRESELECTED: "ONLY_PRESELECTED",
};

const formatParticipantStarterlistDisciplineLevelOfDetail = (
    participantStarterlistDisciplineLevelOfDetail,
) => {
    switch (participantStarterlistDisciplineLevelOfDetail) {
        case ParticipantStarterlistDisciplineLevelOfDetail.ALL_ALLOWED:
            return "Alle erlaubten Disziplinen";
        case ParticipantStarterlistDisciplineLevelOfDetail.ONLY_PRESELECTED:
            return "Nur vorausgewählte Disziplinen";
        default:
            throw new Error(
                `Invalid participant starterlist discipline level of detail: ${participantStarterlistDisciplineLevelOfDetail}`,
            );
    }
};

const PaymentType = {
    ONLINE: "ONLINE",
    INVOICE: "INVOICE",
};

const formatPaymentType = (paymentType) => {
    switch (paymentType) {
        case PaymentType.ONLINE:
            return "Online";
        case PaymentType.INVOICE:
            return "Rechnung";
        default:
            throw new Error(`Invalid payment type: ${paymentType}`);
    }
};

const RankPrintType = {
    RANK: "RANK",
    RANK_WITH_GROUP_SIZE: "RANK_WITH_GROUP_SIZE",
    MIRRORED_RANK: "MIRRORED_RANK",
    MIRRORED_RANK_WITH_GROUP_SIZE: "MIRRORED_RANK_WITH_GROUP_SIZE",
    FAKE_POINTS: "FAKE_POINTS",
};

const formatRankPrintType = (rankPrintType) => {
    switch (rankPrintType) {
        case RankPrintType.RANK:
            return "Rang";
        case RankPrintType.RANK_WITH_GROUP_SIZE:
            return "Rang mit Riegen-Größe";
        case RankPrintType.MIRRORED_RANK:
            return "Gespiegelter Rang";
        case RankPrintType.MIRRORED_RANK_WITH_GROUP_SIZE:
            return "Gespiegelter Rang mit Riegen-Größe";
        case RankPrintType.FAKE_POINTS:
            return "Verborgene Punkte";
        default:
            throw new Error(`Invalid rank print type: ${rankPrintType}`);
    }
};

const SchoolType = {
    GRUNDSCHULE: "GRUNDSCHULE",
    HAUPTSCHULE: "HAUPTSCHULE",
    REALSCHULE: "REALSCHULE",
    GYMNASIUM: "GYMNASIUM",
    GESAMTSCHULE: "GESAMTSCHULE",
    FÖRDERSCHULE: "FÖRDERSCHULE",
    WEITERBILDENDE_SCHULE: "WEITERBILDENDE_SCHULE",
    BERUFSSCHULE: "BERUFSSCHULE",
    OTHER: "OTHER",
};

const formatSchoolType = (schoolType) => {
    switch (schoolType) {
        case SchoolType.GRUNDSCHULE:
            return "Grundschule";
        case SchoolType.HAUPTSCHULE:
            return "Hauptschule";
        case SchoolType.REALSCHULE:
            return "Realschule";
        case SchoolType.GYMNASIUM:
            return "Gymnasium";
        case SchoolType.GESAMTSCHULE:
            return "Gesamtschule";
        case SchoolType.FÖRDERSCHULE:
            return "Förderschule";
        case SchoolType.WEITERBILDENDE_SCHULE:
            return "Weiterbildende Schule";
        case SchoolType.BERUFSSCHULE:
            return "Berufsschule";
        case SchoolType.OTHER:
            return "Andere Schulart";
        default:
            return "Unbekannte Schulart";
    }
};

const Unit = {
    METERS: "METERS",
    MINUTES: "MINUTES",
    POINTS: "POINTS",
    SECONDS: "SECONDS",
    ZONE: "ZONE",
};

const formatUnit = (unit, isShort = undefined) => {
    const finalIsShort = isShort === undefined ? false : isShort;
    switch (unit) {
        case Unit.METERS:
            return finalIsShort ? "m" : "Meter";
        case Unit.MINUTES:
            return finalIsShort ? "min" : "Minuten";
        case Unit.POINTS:
            return finalIsShort ? "Pkt." : "Punkte";
        case Unit.SECONDS:
            return finalIsShort ? "s" : "Sekunden";
        case Unit.ZONE:
            return finalIsShort ? "Z." : "Zone";
        default:
            throw new Error(`Invalid unit: ${unit}`);
    }
};

const DisciplineCategoryId = {
    SPRINTING: "000000000000000000000000",
    RUNNING: "000000000000000000000001",
    JUMPING: "000000000000000000000002",
    THROWING: "000000000000000000000003",
};

const contestEventDisciplineCategories = [
    {
        id: DisciplineCategoryId.SPRINTING,
        name: "Sprint",
        disciplines: [
            {
                id: "000000000002000000000000",
                name: "A | Sprint (30 m)",
                shortName: "Sprint (30 m)",
                tryLimit: 2,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.ADD_ALL,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 2x und beide erzielten Zeiten werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000001",
                name: "B | Hindernissprint (30 m)",
                shortName: "Hindernissprint (30 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000002",
                name: "C | Wendesprint mit Hindernissen und Slalomlafuf (2 x 15m)",
                shortName: "Wendesprint mit Hindernissen und Slalomlafuf (2 x 15m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000003",
                name: "A | Sprint (40 m)",
                shortName: "Sprint (40 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000004",
                name: "B | Hindernissprint (30-40 m)",
                shortName: "Hindernissprint (30-40 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000005",
                name: "C | Wendesprint mit Hindernissen und Slalomlauf (2 x 20 m)",
                shortName: "Wendesprint mit Hindernissen und Slalomlauf (2 x 20 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000006",
                name: "A | Sprint (50 m)",
                shortName: "Sprint (50 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000007",
                name: "B | Hindernissprint (40-50 m)",
                shortName: "Hindernissprint (40-50 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000008",
                name: "C | Wendesprint mit Hindernissen und Slalomlauf (2 x 25 m)",
                shortName: "Wendesprint mit Hindernissen und Slalomlauf (2 x 25 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000009",
                name: "A | Sprint (60 m)",
                shortName: "Sprint (60 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "00000000000200000000000a",
                name: "B | Hindernissprint (50 m)",
                shortName: "Hindernissprint (50 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "00000000000200000000000b",
                name: "C | Wendesprint mit Hindernissen und Slalomlauf (2 x 30 m)",
                shortName: "Wendesprint mit Hindernissen und Slalomlauf (2 x 30 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "00000000000200000000000c",
                name: "A | Sprint (75 m)",
                shortName: "Sprint (75 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "00000000000200000000000d",
                name: "B | Hindernissprint (60 m)",
                shortName: "Hindernissprint (60 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "00000000000200000000000e",
                name: "C | Wendesprint mit Hindernissen und Slalomlauf (2 x 40 m)",
                shortName: "Wendesprint mit Hindernissen und Slalomlauf (2 x 40 m)",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
        ],
    },
    {
        id: DisciplineCategoryId.RUNNING,
        name: "Lauf",
        disciplines: [
            {
                id: "00000000000200000000000f",
                name: "A | Lauf über 10 Minuten",
                shortName: "Lauf über 10 Minuten",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 3,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "000000000002000000000010",
                name: "B | Biathlon",
                shortName: "Biathlon",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 9000,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000011",
                name: "C | Transportlauf",
                shortName: "Transportlauf",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 99,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "000000000002000000000012",
                name: "D | Crosslauf",
                shortName: "Crosslauf",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 9000,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000014",
                name: "A | Lauf über 15 Minuten",
                shortName: "Lauf über 15 Minuten",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 3,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "000000000002000000000015",
                name: "B | Transportlauf",
                shortName: "Transportlauf",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 99,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "000000000002000000000016",
                name: "C | Crosslauf",
                shortName: "Crosslauf",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 9000,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "000000000002000000000017",
                name: "A | Lauf über 30 Minuten",
                shortName: "Lauf über 30 Minuten",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 3,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "000000000002000000000018",
                name: "B | Transportlauf",
                shortName: "Transportlauf",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 99,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "000000000002000000000019",
                name: "C | Zeit-Schätzlauf",
                shortName: "Zeit-Schätzlauf",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 300,
                },
                note: "Der Schüler läuft 1x und die Differenz zur Zielzeit wird in Sekunden eingetragen.",
            },
            {
                id: "00000000000200000000001a",
                name: "D | Crosslauf",
                shortName: "Crosslauf",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 9000,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "00000000000200000000001b",
                name: "A | Lauf über 30 Minuten oder 45 Minuten",
                shortName: "Lauf über 30 Minuten oder 45 Minuten",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 3,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "00000000000200000000001c",
                name: "B | Transportlauf",
                shortName: "Transportlauf",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 99,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "00000000000200000000001d",
                name: "C | Crosslauf",
                shortName: "Crosslauf",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 9000,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
            {
                id: "00000000000200000000001e",
                name: "A | Lauf über 60 Minuten",
                shortName: "Lauf über 60 Minuten",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 3,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "00000000000200000000001f",
                name: "B | Transportlauf",
                shortName: "Transportlauf",
                tryLimit: 1,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 99,
                },
                note: "Der Schüler läuft 1x und die erzielte Punktzahl wird eingetragen.",
            },
            {
                id: "000000000002000000000020",
                name: "C | Crosslauf",
                shortName: "Crosslauf",
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 9000,
                },
                note: "Der Schüler läuft 1x und die erzielte Zeit wird eingetragen.",
            },
        ],
    },
    {
        id: DisciplineCategoryId.JUMPING,
        name: "Sprung",
        disciplines: [
            {
                id: "000000000002000000000021",
                name: "A | Kleine Sprünge",
                shortName: "Kleine Sprünge",
                tryLimit: 2,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: DisciplineEvaluationType.BEST,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 15,
                },
                note: "Der Schüler springt 2x und beide erzielten Punktzahlen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000022",
                name: "B | Hoch-Weitsprung",
                shortName: "Hoch-Weitsprung",
                tryLimit: 1,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0.1,
                    max: 5,
                },
                note: "Der Schhüler springt mehrmals mit steigender Höhe. Sollte er eine Höhe nicht schaffen, hat er einen zweiten Versuch für diese. Die am höchsten übersprungende Höhe wird eingetragen.",
            },
            {
                id: "000000000002000000000023",
                name: "C | Zielweitsprung",
                shortName: "Zielweitsprung",
                tryLimit: 1,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0.1,
                    max: 10,
                },
                note: "Der Schüler springt mehrmals mit steigender Zielzone. Sollte er eine Zone nicht schaffen, hat er einen zweiten Versuch für diese. Die am weitesten gesprungene Zone wird eingetragen.",
            },
            {
                id: "000000000002000000000024",
                name: "D | Weitsprung in die Zone",
                shortName: "Weitsprung in die Zone",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 40,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000025",
                name: "A | Weitsprung in die Zone",
                shortName: "Weitsprung in die Zone",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 40,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000026",
                name: "B | Hoch-Weitsprung",
                shortName: "Hoch-Weitsprung",
                tryLimit: 1,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0.1,
                    max: 5,
                },
                note: "Der Schüler springt mehrmals mit steigender Höhe. Sollte er eine Höhe nicht schaffen, hat er einen zweiten Versuch für diese. Die am höchsten übersprungende Höhe wird eingetragen.",
            },
            {
                id: "000000000002000000000027",
                name: "C | Wechselsprünge",
                shortName: "Wechselsprünge",
                tryLimit: 2,
                unit: Unit.POINTS,
                inputUnit: Unit.POINTS,
                evaluationType: "BEST",
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 12,
                },
                note: "Der Schüler springt 2x und beide erzielten Punktzahlen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000028",
                name: "A | Weitsprung in die Zone",
                shortName: "Weitsprung in die Zone",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 40,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000029",
                name: "B | (Scher-)Hochsprung",
                shortName: "(Scher-)Hochsprung",
                tryLimit: 1,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0.1,
                    max: 5,
                },
                note: "Der Schüler springt mehrmals mit steigender Höhe. Sollte er eine Höhe nicht schaffen, hat er einen zweiten Versuch für diese. Die am höchsten übersprungende Höhe wird eingetragen.",
            },
            {
                id: "00000000000200000000002a",
                name: "C | Fünfsprung",
                shortName: "Fünfsprung",
                tryLimit: 4,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0.1,
                    max: 30,
                },
                note: "Der Schüler springt 4x und alle erzielten Weiten werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000002b",
                name: "D | Stab-Weitsprung",
                shortName: "Stab-Weitsprung",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 30,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000002c",
                name: "A | Weitsprung in die Zone",
                shortName: "Weitsprung in die Zone",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 40,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000002d",
                name: "B | Risiko-Hochsprung",
                shortName: "Risiko-Hochsprung",
                tryLimit: 1,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0.1,
                    max: 5,
                },
                note: "Der Schüler springt mehrmals mit steigender Höhe, ab seiner Anfangshöhe. Insgesamt hat er 6 Versuche, seine Höhe und dessen Steigerung zu überspringen. Die am höchsten übersprungende Höhe wird eingetragen.",
            },
            {
                id: "00000000000200000000002e",
                name: "C | Fünfsprung",
                shortName: "Fünfsprung",
                tryLimit: 4,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0.1,
                    max: 30,
                },
                note: "Der Schüler springt 4x und alle erzielten Weiten werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000002f",
                name: "D | Stab-Weitsprung",
                shortName: "Stab-Weitsprung",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 30,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000030",
                name: "A | Weitsprung in die Zone",
                shortName: "Weitsprung in die Zone",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 40,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000031",
                name: "B | Risiko-Hochsprung",
                shortName: "Risiko-Hochsprung",
                tryLimit: 1,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0.1,
                    max: 5,
                },
                note: "Der Schüler springt mehrmals mit steigender Höhe, ab seiner Anfangshöhe. Insgesamt hat er 6 Versuche, seine Höhe und dessen Steigerung zu überspringen. Die am höchsten übersprungende Höhe wird eingetragen.",
            },
            {
                id: "000000000002000000000032",
                name: "C | Stab-Weitsprung",
                shortName: "Stab-Weitsprung",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 30,
                },
                note: "Der Schüler springt 4x und alle erzielten Zonen werden einzeln eingetragen.",
            },
        ],
    },
    {
        id: DisciplineCategoryId.THROWING,
        name: "Wurf/Stoß",
        disciplines: [
            {
                id: "000000000002000000000033",
                name: 'A | Wurf in ein "Fenster"',
                shortName: 'Wurf in ein "Fenster"',
                tryLimit: 1,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_ALL,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 8,
                },
                note: "Der Schüler wirft 4x hintereinander und es wird die Summe als eine Punktzahl eingetragen.",
            },
            {
                id: "000000000002000000000034",
                name: "B | Schlagwurf",
                shortName: "Schlagwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 60,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000035",
                name: "C | Beidarmiger Stoss",
                shortName: "Beidarmiger Stoss",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 25,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000036",
                name: "D | Drehwurf",
                shortName: "Drehwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_1_AND_2,
                valueBoundaries: {
                    min: 0,
                    max: 120,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000037",
                name: "A | Schlagwurf",
                shortName: "Schlagwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 60,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000038",
                name: "B | Medizinball-Stoß",
                shortName: "Medizinball-Stoß",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 25,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000039",
                name: "C | Drehwurf",
                shortName: "Drehwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_3_AND_4,
                valueBoundaries: {
                    min: 0,
                    max: 120,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000003a",
                name: "A | Schlagwurf",
                shortName: "Schlagwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 60,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000003b",
                name: "B | Medizinball-Stoß",
                shortName: "Medizinball-Stoß",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 25,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000003c",
                name: "C | Drehwurf",
                shortName: "Drehwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_5_AND_6,
                valueBoundaries: {
                    min: 0,
                    max: 120,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000003d",
                name: "A | Schlagwurf",
                shortName: "Schlagwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 60,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000003e",
                name: "B | Kugel-Stoß",
                shortName: "Kugel-Stoß",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 25,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "00000000000200000000003f",
                name: "C | Drehwurf",
                shortName: "Drehwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_7_AND_8,
                valueBoundaries: {
                    min: 0,
                    max: 120,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000040",
                name: "A | Schlagwurf",
                shortName: "Schlagwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 60,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000041",
                name: "B | Kugel-Stoß",
                shortName: "Kugel-Stoß",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 25,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
            {
                id: "000000000002000000000042",
                name: "C | Drehwurf",
                shortName: "Drehwurf",
                tryLimit: 4,
                unit: Unit.ZONE,
                inputUnit: Unit.ZONE,
                evaluationType: DisciplineEvaluationType.ADD_TOP_3,
                allowedClassLevelRange: ClassLevelRange.N_9_AND_10,
                valueBoundaries: {
                    min: 0,
                    max: 120,
                },
                note: "Der Schüler wirft 4x und alle 4 erzielten Zonen werden einzeln eingetragen.",
            },
        ],
    },
];

const competitionEventDisciplineCategories = [
    {
        id: DisciplineCategoryId.SPRINTING,
        name: "Sprint",
        disciplines: [
            {
                id: "000000000001000000000000",
                name: "Sprint 50 m",
                shortName: "50 m",
                distance: 50,
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                allowedParticipantGroups: {
                    MALE: [6, 7, 8, 9, 10, 11, 12, 13],
                    FEMALE: [6, 7, 8, 9, 10, 11, 12, 13],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 3.79,
                            c: 0.0069,
                        },
                        FEMALE: {
                            a: 3.648,
                            c: 0.0066,
                        },
                    },
                ],
            },
            {
                id: "000000000001000000000001",
                name: "Sprint 75 m",
                shortName: "75 m",
                distance: 75,
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                allowedParticipantGroups: {
                    MALE: [13, 14, 15],
                    FEMALE: [13, 14, 15],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 4.1,
                            c: 0.00664,
                        },
                        FEMALE: {
                            a: 3.998,
                            c: 0.0066,
                        },
                    },
                ],
            },
            {
                id: "000000000001000000000002",
                name: "Sprint 100 m",
                shortName: "100 m",
                distance: 100,
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.SECONDS,
                evaluationType: DisciplineEvaluationType.SINGLE,
                valueBoundaries: {
                    min: 3,
                    max: 300,
                },
                allowedParticipantGroups: {
                    MALE: [15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                    FEMALE: [15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 4.341,
                            c: 0.00676,
                        },
                        FEMALE: {
                            a: 4.0062,
                            c: 0.00656,
                        },
                    },
                ],
            },
        ],
    },
    {
        id: DisciplineCategoryId.RUNNING,
        name: "Lauf",
        disciplines: [
            {
                id: "000000000001000000000003",
                name: "Laufen 800 m",
                shortName: "800 m",
                distance: 800,
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                valueBoundaries: {
                    min: 100,
                    max: 9000,
                },
                allowedParticipantGroups: {
                    MALE: [
                        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                    ],
                    FEMALE: [
                        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                    ],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 2.325,
                            c: 0.00644,
                        },
                        FEMALE: {
                            a: 2.0232,
                            c: 0.00647,
                        },
                    },
                ],
            },
            {
                id: "000000000001000000000004",
                name: "Laufen 1000 m",
                shortName: "1000 m",
                distance: 1000,
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                valueBoundaries: {
                    min: 120,
                    max: 9000,
                },
                allowedParticipantGroups: {
                    MALE: [
                        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                    ],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 2.158,
                            c: 0.006,
                        },
                        FEMALE: {
                            a: 1.9,
                            c: 0.0054,
                        },
                    },
                ],
            },
            {
                id: "000000000001000000000005",
                name: "Laufen 2000 m",
                shortName: "2000 m",
                distance: 2000,
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                valueBoundaries: {
                    min: 275,
                    max: 9000,
                },
                allowedParticipantGroups: {
                    MALE: [10, 11, 12, 13],
                    FEMALE: [10, 11, 12, 13, 14, 15],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.784,
                            c: 0.006,
                        },
                        FEMALE: {
                            a: 1.8,
                            c: 0.0054,
                        },
                    },
                ],
            },
            {
                id: "000000000001000000000006",
                name: "Laufen 3000 m",
                shortName: "3000 m",
                distance: 3000,
                tryLimit: 1,
                unit: Unit.SECONDS,
                inputUnit: Unit.MINUTES,
                evaluationType: DisciplineEvaluationType.SINGLE,
                valueBoundaries: {
                    min: 425,
                    max: 9000,
                },
                allowedParticipantGroups: {
                    MALE: [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                    FEMALE: [16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        FEMALE: {
                            a: 1.75,
                            c: 0.005,
                        },
                        MALE: {
                            a: 1.7,
                            c: 0.0058,
                        },
                    },
                ],
            },
        ],
    },
    {
        id: DisciplineCategoryId.JUMPING,
        name: "Sprung",
        disciplines: [
            {
                id: "000000000001000000000007",
                name: "Hochsprung",
                shortName: "Hoch",
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 5,
                },
                allowedParticipantGroups: {
                    MALE: [
                        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                    ],
                    FEMALE: [
                        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                    ],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        FEMALE: {
                            a: 0.8807,
                            c: 0.00068,
                        },
                        MALE: {
                            a: 0.841,
                            c: 0.0008,
                        },
                    },
                ],
            },
            {
                id: "000000000001000000000008",
                name: "Weitsprung",
                shortName: "Weit",
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 10,
                },
                allowedParticipantGroups: {
                    MALE: [
                        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                    ],
                    FEMALE: [
                        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                    ],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        FEMALE: {
                            a: 1.0935,
                            c: 0.00208,
                        },
                        MALE: {
                            a: 1.15028,
                            c: 0.00219,
                        },
                    },
                ],
            },
        ],
    },
    {
        id: DisciplineCategoryId.THROWING,
        name: "Wurf/Stoß",
        disciplines: [
            {
                id: "000000000001000000000009",
                name: "Ballwurf 80g",
                shortName: "Ball 80g",
                mass: 80,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 120,
                },
                allowedParticipantGroups: {
                    MALE: [6, 7, 8, 9, 10, 11],
                    FEMALE: [6, 7, 8, 9, 10, 11, 12, 13],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 2.8,
                            c: 0.011,
                        },
                        FEMALE: {
                            a: 2.0232,
                            c: 0.00874,
                        },
                    },
                ],
            },
            {
                id: "00000000000100000000000a",
                name: "Ballwurf 200g",
                shortName: "Ball 200g",
                mass: 200,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 120,
                },
                allowedParticipantGroups: {
                    MALE: [11, 12, 13, 14, 15, 16, 17, 18],
                    FEMALE: [12, 13, 14, 15, 16, 17, 18],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.936,
                            c: 0.0124,
                        },
                        FEMALE: {
                            a: 1.4149,
                            c: 0.01039,
                        },
                    },
                ],
            },
            {
                id: "00000000000100000000000b",
                name: "Kugelstoß 3 kg",
                shortName: "Kugel 3kg",
                mass: 3000,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 25,
                },
                allowedParticipantGroups: {
                    MALE: [12, 13],
                    FEMALE: [12, 13, 14, 15, 16, 17],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.425,
                            c: 0.0037,
                        },
                        FEMALE: {
                            a: 1.279,
                            c: 0.00398,
                        },
                    },
                ],
            },
            {
                id: "00000000000100000000000c",
                name: "Kugelstoß 4 kg",
                shortName: "Kugel 4kg",
                mass: 4000,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 25,
                },
                allowedParticipantGroups: {
                    MALE: [14, 15],
                    FEMALE: [18, 19, 20, 21, 22, 23, 24, 25],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.425,
                            c: 0.0037,
                        },
                        FEMALE: {
                            a: 1.279,
                            c: 0.00398,
                        },
                    },
                ],
            },
            {
                id: "00000000000100000000000d",
                name: "Kugelstoß 5 kg",
                shortName: "Kugel 5kg",
                mass: 5000,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 25,
                },
                allowedParticipantGroups: { MALE: [16, 17] },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.425,
                            c: 0.0037,
                        },
                        FEMALE: {
                            a: 1.279,
                            c: 0.00398,
                        },
                    },
                ],
            },
            {
                id: "00000000000100000000000e",
                name: "Kugelstoß 6 kg",
                shortName: "Kugel 6kg",
                mass: 6000,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 25,
                },
                allowedParticipantGroups: { MALE: [18, 19] },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.425,
                            c: 0.0037,
                        },
                        FEMALE: {
                            a: 1.279,
                            c: 0.00398,
                        },
                    },
                ],
            },
            {
                id: "000000000001000000000010",
                name: "Kugelstoß 7,26 kg",
                shortName: "Kugel 7,26kg",
                mass: 7260,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 25,
                },
                allowedParticipantGroups: { MALE: [20, 21, 22, 23, 24, 25] },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.425,
                            c: 0.0037,
                        },
                        FEMALE: {
                            a: 1.279,
                            c: 0.00398,
                        },
                    },
                ],
            },
            {
                id: "00000000000100000000000f",
                name: "Schleuderball 1 kg",
                shortName: "Schleuder 1kg",
                mass: 1000,
                tryLimit: 3,
                unit: Unit.METERS,
                inputUnit: Unit.METERS,
                evaluationType: DisciplineEvaluationType.BEST,
                valueBoundaries: {
                    min: 0.1,
                    max: 100,
                },
                allowedParticipantGroups: {
                    MALE: [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                    FEMALE: [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
                },
                constants: [
                    {
                        validityPeriod: {
                            from: new Date(Date.UTC(2020, 0, 1)),
                            to: new Date(Date.UTC(100020, 0, 1)),
                        },
                        MALE: {
                            a: 1.595,
                            c: 0.009125,
                        },
                        FEMALE: {
                            a: 1.085,
                            c: 0.00921,
                        },
                    },
                ],
            },
        ],
    },
];

const eventFormatToDisciplineCategoriesMap = {
    [EventFormat.CONTEST]: contestEventDisciplineCategories,
    [EventFormat.COMPETITION]: competitionEventDisciplineCategories,
};

const eventFormatToAllDisciplinesMap = {
    [EventFormat.CONTEST]: eventFormatToDisciplineCategoriesMap[EventFormat.CONTEST].flatMap(
        (x) => x.disciplines,
    ),
    [EventFormat.COMPETITION]: eventFormatToDisciplineCategoriesMap[
        EventFormat.COMPETITION
    ].flatMap((x) => x.disciplines),
};

const certRulings = {
    MALE: {
        6: {
            winner: 300,
            honor: 375,
        },
        7: {
            winner: 375,
            honor: 475,
        },
        8: {
            winner: 450,
            honor: 575,
        },
        9: {
            winner: 525,
            honor: 675,
        },
        10: {
            winner: 600,
            honor: 775,
        },
        11: {
            winner: 675,
            honor: 875,
        },
        12: {
            winner: 750,
            honor: 975,
        },
        13: {
            winner: 825,
            honor: 1050,
        },
        14: {
            winner: 900,
            honor: 1125,
        },
        15: {
            winner: 975,
            honor: 1225,
        },
        16: {
            winner: 1050,
            honor: 1325,
        },
        17: {
            winner: 1125,
            honor: 1400,
        },
        18: {
            winner: 1200,
            honor: 1475,
        },
        19: {
            winner: 1275,
            honor: 1550,
        },
        20: {
            winner: 1275,
            honor: 1550,
        },
        21: {
            winner: 1275,
            honor: 1550,
        },
        22: {
            winner: 1275,
            honor: 1550,
        },
        23: {
            winner: 1275,
            honor: 1550,
        },
        24: {
            winner: 1275,
            honor: 1550,
        },
        25: {
            winner: 1275,
            honor: 1550,
        },
    },
    FEMALE: {
        6: {
            winner: 325,
            honor: 425,
        },
        7: {
            winner: 400,
            honor: 525,
        },
        8: {
            winner: 475,
            honor: 625,
        },
        9: {
            winner: 550,
            honor: 725,
        },
        10: {
            winner: 625,
            honor: 825,
        },
        11: {
            winner: 700,
            honor: 900,
        },
        12: {
            winner: 775,
            honor: 975,
        },
        13: {
            winner: 825,
            honor: 1025,
        },
        14: {
            winner: 850,
            honor: 1050,
        },
        15: {
            winner: 875,
            honor: 1075,
        },
        16: {
            winner: 900,
            honor: 1100,
        },
        17: {
            winner: 925,
            honor: 1125,
        },
        18: {
            winner: 950,
            honor: 1150,
        },
        19: {
            winner: 950,
            honor: 1150,
        },
        20: {
            winner: 950,
            honor: 1150,
        },
        21: {
            winner: 950,
            honor: 1150,
        },
        22: {
            winner: 950,
            honor: 1150,
        },
        23: {
            winner: 950,
            honor: 1150,
        },
        24: {
            winner: 950,
            honor: 1150,
        },
        25: {
            winner: 950,
            honor: 1150,
        },
    },
};

class AppError extends Error {
    constructor(
        appErrType,
        details = undefined,
        originalError = undefined,
        customMessage = undefined,
    ) {
        super(appErrType);
        this.type = appErrType;
        if (details !== undefined) {
            this.details = details;
        }
        if (originalError !== undefined) {
            this.originalError = originalError;
        }
        if (customMessage !== undefined) {
            this.customMessage = customMessage;
        }
    }
}

/* eslint-disable no-restricted-syntax */
const asyncForEach = async (array, doAsync) => {
    for (const element of array) {
        await doAsync(element);
    }
};

const asyncMap = async (array, mapAsync) => {
    const result = [];
    for (const element of array) {
        result.push(await mapAsync(element));
    }
    return result;
};

const asyncEvery = async (array, checkAsync) => {
    for (const element of array) {
        if (!(await checkAsync(element))) {
            return false;
        }
    }
    return true;
};

const asyncSome = async (array, checkAsync) => {
    for (const element of array) {
        if (await checkAsync(element)) {
            return true;
        }
    }
    return false;
};

const calculateAgeGroup = (referenceDateTime, birthYear) =>
    dateFns.getYear(referenceDateTime) - birthYear;

// Big base + small multiplier keep even the lowest fake points well above single digits, so the printed value on a Teilnahmeurkunde never reads like "1 Punkt".
const fakePointsBase = 36;
const fakePointsMultiplier = 3;

// Mirrors the rank sum so that bigger means better (best person -> highest fake points), then scales it into a "looks like real points" range starting at 48.
//   bestPossibleRankSum  = disciplineCategoryCount (rank 1 in every category)
//   worstPossibleRankSum = disciplineCategoryCount * groupParticipantCount
//   mirroredRankSum      = worstPossibleRankSum - rankSum + bestPossibleRankSum
//   fakePoints           = mirroredRankSum * multiplier + base
const calculateFakePoints = (rankSum, groupParticipantCount, disciplineCategoryCount) => {
    const mirroredRankSum =
        disciplineCategoryCount * groupParticipantCount - rankSum + disciplineCategoryCount;
    return mirroredRankSum * fakePointsMultiplier + fakePointsBase;
};

const Environment = {
    DEVELOPMENT: "development",
    STAGING: "staging",
    PRODUCTION: "production",
};

const getEnvironment = () => "production";

const checkIsProductionEnvironment = () => getEnvironment() === Environment.PRODUCTION;

const checkIsProductionOrStagingEnvironment = () =>
    getEnvironment() === Environment.PRODUCTION || getEnvironment() === Environment.STAGING;

const generateDevelopmentConfig = () => ({
    info: {
        companyName: "Bujus UG (haftungsbeschränkt)",
        founderName: "Julius Huck",
        mailAddress: "mail@bujus.de",
        phoneNumber: "+49 179 3669961",
    },
    urls: {
        homePage: "https://bujus.de",
        contact: "https://bujus.de/#contact-section",
        liveChat: "https://bujus.de/#hs-chat-open",
        instructions: "https://instructions.bujus.de",
        onboarding: "https://bujus.de/#contact-section",
        imprint: "https://bujus.de/imprint",
        dataPrivacy: "https://bujus.de/data-privacy",
        dataPrivacyCompliance: "https://bujus.de/data-privacy/data-privacy-compliance",
        api: "http://localhost:4000",
        apiPort: 4000,
        schoolApp: "http://localhost:4001",
    },
    timeZone: "Europe/Berlin",
    timeout: dateFns.milliseconds({ minutes: 5 }),
    artificialDelayForHeavyOperations: dateFns.milliseconds({ seconds: 0 }),
    bulkRemovalItemLimit: 100,
    mixpanel: { token: "e50caff16bdfa2bbd188f93435237f14" },
    event: {
        futureCreationLimitInYears: 3,
        schedule: {
            warmUpTime: dateFns.milliseconds({ days: 30 }),
            expireTime: dateFns.milliseconds({ days: 100 }),
        },
        testLimits: {
            helper: 2,
            participant: 5,
            group: 5,
            result: 250,
        },
        real: {
            allowedParticipantLimits: [
                50, 100, 150, 200, 250, 300, 350, 400, 450, 500, 550, 600, 650, 700, 750, 800, 850,
                900, 950, 1_000, 1_050, 1_100, 1_150, 1_200, 1_250, 1_300, 1_350, 1_400, 1_450,
                1_500, 1_550, 1_600, 1_650, 1_700, 1_750, 1_800, 1_850, 1_900, 1_950, 2_000, 2_050,
                2_100, 2_150, 2_200, 2_250, 2_300, 2_350, 2_400, 2_450, 2_500, 2_550, 2_600, 2_650,
                2_700, 2_750, 2_800, 2_850, 2_900, 2_950, 3_000,
            ],
            limitsPer50Participants: {
                helper: 10,
                group: 25,
                result: 2500,
            },
        },
        alltimeLimits: {
            helper: 2_000,
            group: 5_000,
            participant: 10_000,
            result: 500_000,
        },
    },
    result: { importChunkSize: 20 },
});

const generateProductionConfig = () => ({
    urls: {
        api: "https://api.bujus.de",
        apiPort: undefined,
        schoolApp: "https://school-app.bujus.de",
    },
    artificialDelayForHeavyOperations: dateFns.milliseconds({ seconds: 3 }),
    mixpanel: { token: "9b77e44c83293d67b8fa823546c8f3e4" },
});

const generateStagingConfig = () => ({
    urls: {
        api: "https://api.staging-hb4exk9g.bujus.de",
        apiPort: undefined,
        schoolApp: "https://school-app.staging-hb4exk9g.bujus.de",
    },
    artificialDelayForHeavyOperations: dateFns.milliseconds({ seconds: 3 }),
    mixpanel: { token: "862e432dacaeb2122884314e6f6d6e58" },
});

const mergeConfigs = (destinationConfig, sourceConfig) =>
    deepmerge(destinationConfig, sourceConfig, {
        arrayMerge: (_destinationArray, sourceArray, _options) => sourceArray,
    });

let config;
switch (getEnvironment()) {
    case Environment.DEVELOPMENT:
        config = generateDevelopmentConfig();
        break;
    case Environment.STAGING:
        config = mergeConfigs(generateDevelopmentConfig(), generateStagingConfig());
        break;
    case Environment.PRODUCTION:
        config = mergeConfigs(generateDevelopmentConfig(), generateProductionConfig());
        break;
    default:
        throw new Error(`Invalid environment: ${getEnvironment()}`);
}

const calculateGroupLimit = (participantLimit) =>
    (participantLimit / 50) * config.event.real.limitsPer50Participants.group;

const calculateHelperLimit = (participantLimit) =>
    (participantLimit / 50) * config.event.real.limitsPer50Participants.helper;

const calculateResultLimit = (participantLimit) =>
    (participantLimit / 50) * config.event.real.limitsPer50Participants.result;

const calculateTextSpacing = (fontSize, lineHeight, index) => fontSize * lineHeight * index;

// Sort needs return value to be positive when y should be in front of x. When y is fallback it should be behind x, so return value has to be negative
const checkIsCountingResult = (
    disciplineCategoryIdToDisciplineCategoryEvaluationMap,
    disciplineCategoryEvaluation,
) =>
    Object.values(disciplineCategoryIdToDisciplineCategoryEvaluationMap)
        .filter((x) => x.isValid)
        .sort((x, y) => y.points - x.points)
        .slice(0, 3)
        .includes(disciplineCategoryEvaluation);

const checkIsDisciplinePreselected = (event, disciplinePreselection, participant, disciplineId) => {
    switch (event.format) {
        case EventFormat.CONTEST:
            if (participant.group === undefined) {
                return false;
            }
            return Object.values(
                disciplinePreselection[participant.group.classLevelRange],
            ).includes(disciplineId);
        case EventFormat.COMPETITION:
            return Object.values(
                disciplinePreselection[participant.gender][
                    calculateAgeGroup(event.start, participant.birthYear)
                ],
            ).includes(disciplineId);
        default:
            throw new Error(`Invalid event format: ${event.format}`);
    }
};

const checkIsEventActive = (event) =>
    dateFns.isBefore(event.warmUpStart, new Date()) && !dateFns.isBefore(event.end, new Date());

const convertHorizontalTextAlignmentToSvgTextAnchor = (horizontalTextAlignment) => {
    switch (horizontalTextAlignment) {
        case CertTemplateHorizontalTextAlignment.LEFT:
            return "start";
        case CertTemplateHorizontalTextAlignment.CENTER:
            return "middle";
        case CertTemplateHorizontalTextAlignment.RIGHT:
            return "end";
        default:
            throw new Error(
                `Invalid cert template horizontal text alignment: ${horizontalTextAlignment}`,
            );
    }
};

const convertVerticalTextAlignmentToSvgTextAnchor = (verticalTextAlignment) => {
    switch (verticalTextAlignment) {
        case CertTemplateVerticalTextAlignment.TOP:
            return "auto";
        // case CertTemplateVerticalTextAlignment.CENTER:
        //     return "middle";
        case CertTemplateVerticalTextAlignment.BOTTOM:
            return "hanging";
        default:
            throw new Error(
                `Invalid cert template vertical text alignment: ${verticalTextAlignment}`,
            );
    }
};

const correctPageIndex = (listingModifiers, count) =>
    count - listingModifiers.pageIndex * listingModifiers.pageSize <= 0
        ? 0
        : listingModifiers.pageIndex;

const defaultContestEventDisciplinePreselection = {
    [ClassLevelRange.N_1_AND_2]: {
        [DisciplineCategoryId.SPRINTING]: "000000000002000000000000",
        [DisciplineCategoryId.RUNNING]: "00000000000200000000000f",
        [DisciplineCategoryId.JUMPING]: "000000000002000000000021",
        [DisciplineCategoryId.THROWING]: "000000000002000000000033",
    },
    [ClassLevelRange.N_3_AND_4]: {
        [DisciplineCategoryId.SPRINTING]: "000000000002000000000003",
        [DisciplineCategoryId.RUNNING]: "000000000002000000000014",
        [DisciplineCategoryId.JUMPING]: "000000000002000000000025",
        [DisciplineCategoryId.THROWING]: "000000000002000000000037",
    },
    [ClassLevelRange.N_5_AND_6]: {
        [DisciplineCategoryId.SPRINTING]: "000000000002000000000006",
        [DisciplineCategoryId.RUNNING]: "000000000002000000000017",
        [DisciplineCategoryId.JUMPING]: "000000000002000000000028",
        [DisciplineCategoryId.THROWING]: "00000000000200000000003a",
    },
    [ClassLevelRange.N_7_AND_8]: {
        [DisciplineCategoryId.SPRINTING]: "000000000002000000000009",
        [DisciplineCategoryId.RUNNING]: "00000000000200000000001b",
        [DisciplineCategoryId.JUMPING]: "00000000000200000000002c",
        [DisciplineCategoryId.THROWING]: "00000000000200000000003d",
    },
    [ClassLevelRange.N_9_AND_10]: {
        [DisciplineCategoryId.SPRINTING]: "00000000000200000000000c",
        [DisciplineCategoryId.RUNNING]: "00000000000200000000001e",
        [DisciplineCategoryId.JUMPING]: "000000000002000000000030",
        [DisciplineCategoryId.THROWING]: "000000000002000000000040",
    },
};

const defaultCompetitionEventDisciplinePreselection = {
    [Gender.MALE]: {
        6: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        7: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        8: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        9: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        10: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        11: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        12: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        13: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000001",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        14: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000001",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        15: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        16: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000d",
        },
        17: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000d",
        },
        18: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000e",
        },
        19: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000e",
        },
        20: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000010",
        },
        21: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000010",
        },
        22: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000010",
        },
        23: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000010",
        },
        24: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000010",
        },
        25: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000010",
        },
    },
    [Gender.FEMALE]: {
        6: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        7: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        8: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        9: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        10: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        11: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "000000000001000000000009",
        },
        12: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000000",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        13: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000001",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        14: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000001",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        15: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        16: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        17: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000b",
        },
        18: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        19: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        20: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        21: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        22: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        23: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        24: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
        25: {
            [DisciplineCategoryId.SPRINTING]: "000000000001000000000002",
            [DisciplineCategoryId.RUNNING]: "000000000001000000000003",
            [DisciplineCategoryId.JUMPING]: "000000000001000000000008",
            [DisciplineCategoryId.THROWING]: "00000000000100000000000c",
        },
    },
};

const eventFormatToDefaultDisciplinePreselectionMap = {
    [EventFormat.CONTEST]: defaultContestEventDisciplinePreselection,
    [EventFormat.COMPETITION]: defaultCompetitionEventDisciplinePreselection,
};

const delayAsync = (duration) =>
    new Promise((resolve) => {
        setTimeout(resolve, duration);
    });

const escapeRegex = (string) => string.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

const getEventFormatObject = (contestObject, competitionObject, eventFormat) => {
    switch (eventFormat) {
        case EventFormat.CONTEST:
            return contestObject;
        case EventFormat.COMPETITION:
            return competitionObject;
        default:
            throw new Error(`Invalid event format: ${eventFormat}`);
    }
};

const callEventFormatFunction = (contestFunction, competitionFunction, eventFormat, ...params) => {
    switch (eventFormat) {
        case EventFormat.CONTEST:
            return contestFunction(...params);
        case EventFormat.COMPETITION:
            return competitionFunction(...params);
        default:
            throw new Error(`Invalid event format: ${eventFormat}`);
    }
};

const extractYear = (string) => {
    let date;
    if (/\d{4}/.test(string) && dateFns.isMatch(string, "y")) {
        date = dateFns.parse(string, "y", new Date());
    } else if (/\d{1,2}\.\d{1,2}\.\d{4}/.test(string) && dateFns.isMatch(string, "d.M.y")) {
        date = dateFns.parse(string, "d.M.y", new Date());
    } else if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(string) && dateFns.isMatch(string, "M/d/y")) {
        date = dateFns.parse(string, "M/d/y", new Date());
    } else if (/\d{4}-\d{1,2}-\d{1,2}/.test(string) && dateFns.isMatch(string, "y-M-d")) {
        date = dateFns.parse(string, "y-M-d", new Date());
    }
    if (!dateFns.isValid(date)) {
        throw new Error(`Invalid year string: ${string}`);
    }
    return dateFns.getYear(date);
};

const floatingNumberResultValueRegexPattern = /^[0-9]{1,2},[0-9]{2}$/;
const integerNumberResultValueRegexPattern = /^[0-9]{1,2}$/;
const minutesResultValueRegexPattern = /^[0-9]{1,2}:[0-5][0-9]$/;

const unitToFilledResultValueRegexPattern = {
    [Unit.METERS]: floatingNumberResultValueRegexPattern,
    [Unit.MINUTES]: minutesResultValueRegexPattern,
    [Unit.POINTS]: integerNumberResultValueRegexPattern,
    [Unit.SECONDS]: floatingNumberResultValueRegexPattern,
    [Unit.ZONE]: integerNumberResultValueRegexPattern,
};

const formatAddress = (
    address,
    { isOnlyStreetAndHouseNumber = false, isOnlyPostalCodeAndCity = false } = {},
) => {
    if (isOnlyStreetAndHouseNumber) {
        return `${address.street} ${address.houseNumber}`;
    }
    if (isOnlyPostalCodeAndCity) {
        return `${address.postalCode} ${address.city}`;
    }
    return `${address.street} ${address.houseNumber}, ${address.postalCode} ${address.city}`;
};

const formatBool = (bool) => (!bool ? "Nein" : "Ja");

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

function buildLocalizeFn(args) {
  return function (dirtyIndex, options) {
    var context = options !== null && options !== void 0 && options.context ? String(options.context) : 'standalone';
    var valuesArray;
    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options !== null && options !== void 0 && options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;
      var _width = options !== null && options !== void 0 && options.width ? String(options.width) : args.defaultWidth;
      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }
    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex;
    // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!
    return valuesArray[index];
  };
}

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);
    if (!matchResult) {
      return null;
    }
    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}
function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }
  return undefined;
}
function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return undefined;
}

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    standalone: {
      one: 'weniger als 1 Sekunde',
      other: 'weniger als {{count}} Sekunden'
    },
    withPreposition: {
      one: 'weniger als 1 Sekunde',
      other: 'weniger als {{count}} Sekunden'
    }
  },
  xSeconds: {
    standalone: {
      one: '1 Sekunde',
      other: '{{count}} Sekunden'
    },
    withPreposition: {
      one: '1 Sekunde',
      other: '{{count}} Sekunden'
    }
  },
  halfAMinute: {
    standalone: 'halbe Minute',
    withPreposition: 'halben Minute'
  },
  lessThanXMinutes: {
    standalone: {
      one: 'weniger als 1 Minute',
      other: 'weniger als {{count}} Minuten'
    },
    withPreposition: {
      one: 'weniger als 1 Minute',
      other: 'weniger als {{count}} Minuten'
    }
  },
  xMinutes: {
    standalone: {
      one: '1 Minute',
      other: '{{count}} Minuten'
    },
    withPreposition: {
      one: '1 Minute',
      other: '{{count}} Minuten'
    }
  },
  aboutXHours: {
    standalone: {
      one: 'etwa 1 Stunde',
      other: 'etwa {{count}} Stunden'
    },
    withPreposition: {
      one: 'etwa 1 Stunde',
      other: 'etwa {{count}} Stunden'
    }
  },
  xHours: {
    standalone: {
      one: '1 Stunde',
      other: '{{count}} Stunden'
    },
    withPreposition: {
      one: '1 Stunde',
      other: '{{count}} Stunden'
    }
  },
  xDays: {
    standalone: {
      one: '1 Tag',
      other: '{{count}} Tage'
    },
    withPreposition: {
      one: '1 Tag',
      other: '{{count}} Tagen'
    }
  },
  aboutXWeeks: {
    standalone: {
      one: 'etwa 1 Woche',
      other: 'etwa {{count}} Wochen'
    },
    withPreposition: {
      one: 'etwa 1 Woche',
      other: 'etwa {{count}} Wochen'
    }
  },
  xWeeks: {
    standalone: {
      one: '1 Woche',
      other: '{{count}} Wochen'
    },
    withPreposition: {
      one: '1 Woche',
      other: '{{count}} Wochen'
    }
  },
  aboutXMonths: {
    standalone: {
      one: 'etwa 1 Monat',
      other: 'etwa {{count}} Monate'
    },
    withPreposition: {
      one: 'etwa 1 Monat',
      other: 'etwa {{count}} Monaten'
    }
  },
  xMonths: {
    standalone: {
      one: '1 Monat',
      other: '{{count}} Monate'
    },
    withPreposition: {
      one: '1 Monat',
      other: '{{count}} Monaten'
    }
  },
  aboutXYears: {
    standalone: {
      one: 'etwa 1 Jahr',
      other: 'etwa {{count}} Jahre'
    },
    withPreposition: {
      one: 'etwa 1 Jahr',
      other: 'etwa {{count}} Jahren'
    }
  },
  xYears: {
    standalone: {
      one: '1 Jahr',
      other: '{{count}} Jahre'
    },
    withPreposition: {
      one: '1 Jahr',
      other: '{{count}} Jahren'
    }
  },
  overXYears: {
    standalone: {
      one: 'mehr als 1 Jahr',
      other: 'mehr als {{count}} Jahre'
    },
    withPreposition: {
      one: 'mehr als 1 Jahr',
      other: 'mehr als {{count}} Jahren'
    }
  },
  almostXYears: {
    standalone: {
      one: 'fast 1 Jahr',
      other: 'fast {{count}} Jahre'
    },
    withPreposition: {
      one: 'fast 1 Jahr',
      other: 'fast {{count}} Jahren'
    }
  }
};
var formatDistance = function formatDistance(token, count, options) {
  var result;
  var tokenValue = options !== null && options !== void 0 && options.addSuffix ? formatDistanceLocale[token].withPreposition : formatDistanceLocale[token].standalone;
  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', String(count));
  }
  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'in ' + result;
    } else {
      return 'vor ' + result;
    }
  }
  return result;
};
const formatDistance$1 = formatDistance;

// DIN 5008: https://de.wikipedia.org/wiki/Datumsformat#DIN_5008
var dateFormats = {
  full: 'EEEE, do MMMM y',
  // Montag, 7. Januar 2018
  long: 'do MMMM y',
  // 7. Januar 2018
  medium: 'do MMM y',
  // 7. Jan. 2018
  short: 'dd.MM.y' // 07.01.2018
};

var timeFormats = {
  full: 'HH:mm:ss zzzz',
  long: 'HH:mm:ss z',
  medium: 'HH:mm:ss',
  short: 'HH:mm'
};
var dateTimeFormats = {
  full: "{{date}} 'um' {{time}}",
  long: "{{date}} 'um' {{time}}",
  medium: '{{date}} {{time}}',
  short: '{{date}} {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};
const formatLong$1 = formatLong;

var formatRelativeLocale = {
  lastWeek: "'letzten' eeee 'um' p",
  yesterday: "'gestern um' p",
  today: "'heute um' p",
  tomorrow: "'morgen um' p",
  nextWeek: "eeee 'um' p",
  other: 'P'
};
var formatRelative = function formatRelative(token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
};
const formatRelative$1 = formatRelative;

var eraValues = {
  narrow: ['v.Chr.', 'n.Chr.'],
  abbreviated: ['v.Chr.', 'n.Chr.'],
  wide: ['vor Christus', 'nach Christus']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1. Quartal', '2. Quartal', '3. Quartal', '4. Quartal']
};

// Note: in German, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
  wide: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
};

// https://st.unicode.org/cldr-apps/v#/de/Gregorian/
var formattingMonthValues = {
  narrow: monthValues.narrow,
  abbreviated: ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'],
  wide: monthValues.wide
};
var dayValues = {
  narrow: ['S', 'M', 'D', 'M', 'D', 'F', 'S'],
  short: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  abbreviated: ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'],
  wide: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
};

// https://www.unicode.org/cldr/charts/32/summary/de.html#1881
var dayPeriodValues = {
  narrow: {
    am: 'vm.',
    pm: 'nm.',
    midnight: 'Mitternacht',
    noon: 'Mittag',
    morning: 'Morgen',
    afternoon: 'Nachm.',
    evening: 'Abend',
    night: 'Nacht'
  },
  abbreviated: {
    am: 'vorm.',
    pm: 'nachm.',
    midnight: 'Mitternacht',
    noon: 'Mittag',
    morning: 'Morgen',
    afternoon: 'Nachmittag',
    evening: 'Abend',
    night: 'Nacht'
  },
  wide: {
    am: 'vormittags',
    pm: 'nachmittags',
    midnight: 'Mitternacht',
    noon: 'Mittag',
    morning: 'Morgen',
    afternoon: 'Nachmittag',
    evening: 'Abend',
    night: 'Nacht'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'vm.',
    pm: 'nm.',
    midnight: 'Mitternacht',
    noon: 'Mittag',
    morning: 'morgens',
    afternoon: 'nachm.',
    evening: 'abends',
    night: 'nachts'
  },
  abbreviated: {
    am: 'vorm.',
    pm: 'nachm.',
    midnight: 'Mitternacht',
    noon: 'Mittag',
    morning: 'morgens',
    afternoon: 'nachmittags',
    evening: 'abends',
    night: 'nachts'
  },
  wide: {
    am: 'vormittags',
    pm: 'nachmittags',
    midnight: 'Mitternacht',
    noon: 'Mittag',
    morning: 'morgens',
    afternoon: 'nachmittags',
    evening: 'abends',
    night: 'nachts'
  }
};
var ordinalNumber = function ordinalNumber(dirtyNumber) {
  var number = Number(dirtyNumber);
  return number + '.';
};
var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function argumentCallback(quarter) {
      return quarter - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    formattingValues: formattingMonthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};
const localize$1 = localize;

var matchOrdinalNumberPattern = /^(\d+)(\.)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(v\.? ?Chr\.?|n\.? ?Chr\.?)/i,
  abbreviated: /^(v\.? ?Chr\.?|n\.? ?Chr\.?)/i,
  wide: /^(vor Christus|vor unserer Zeitrechnung|nach Christus|unserer Zeitrechnung)/i
};
var parseEraPatterns = {
  any: [/^v/i, /^n/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](\.)? Quartal/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(j[aä]n|feb|mär[z]?|apr|mai|jun[i]?|jul[i]?|aug|sep|okt|nov|dez)\.?/i,
  wide: /^(januar|februar|märz|april|mai|juni|juli|august|september|oktober|november|dezember)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^j[aä]/i, /^f/i, /^mär/i, /^ap/i, /^mai/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smdmf]/i,
  short: /^(so|mo|di|mi|do|fr|sa)/i,
  abbreviated: /^(son?|mon?|die?|mit?|don?|fre?|sam?)\.?/i,
  wide: /^(sonntag|montag|dienstag|mittwoch|donnerstag|freitag|samstag)/i
};
var parseDayPatterns = {
  any: [/^so/i, /^mo/i, /^di/i, /^mi/i, /^do/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(vm\.?|nm\.?|Mitternacht|Mittag|morgens|nachm\.?|abends|nachts)/i,
  abbreviated: /^(vorm\.?|nachm\.?|Mitternacht|Mittag|morgens|nachm\.?|abends|nachts)/i,
  wide: /^(vormittags|nachmittags|Mitternacht|Mittag|morgens|nachmittags|abends|nachts)/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^v/i,
    pm: /^n/i,
    midnight: /^Mitte/i,
    noon: /^Mitta/i,
    morning: /morgens/i,
    afternoon: /nachmittags/i,
    // will never be matched. Afternoon is matched by `pm`
    evening: /abends/i,
    night: /nachts/i // will never be matched. Night is matched by `pm`
  }
};

var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function valueCallback(value) {
      return parseInt(value);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function valueCallback(index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};
const match$1 = match;

/**
 * @type {Locale}
 * @category Locales
 * @summary German locale.
 * @language German
 * @iso-639-2 deu
 * @author Thomas Eilmsteiner [@DeMuu]{@link https://github.com/DeMuu}
 * @author Asia [@asia-t]{@link https://github.com/asia-t}
 * @author Van Vuong Ngo [@vanvuongngo]{@link https://github.com/vanvuongngo}
 * @author RomanErnst [@pex]{@link https://github.com/pex}
 * @author Philipp Keck [@Philipp91]{@link https://github.com/Philipp91}
 */
var locale = {
  code: 'de',
  formatDistance: formatDistance$1,
  formatLong: formatLong$1,
  formatRelative: formatRelative$1,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 1 /* Monday */,
    firstWeekContainsDate: 4
  }
};
const deDateFnsLocale = locale;

const formatDate = (date, dateFnsFormat) =>
    dateFnsTz.formatInTimeZone(date, config.timeZone, dateFnsFormat, { locale: deDateFnsLocale });

const getDisciplineCategoryOfDiscipline = (eventFormat, disciplineId) => {
    const disciplineCategory = eventFormatToDisciplineCategoriesMap[eventFormat].find(
        (x) => x.disciplines.find((y) => y.id === disciplineId) !== undefined,
    );
    if (disciplineCategory === undefined) {
        throw new Error(`Invalid discipline id: ${disciplineId}`);
    }
    return disciplineCategory;
};

const getDiscipline = (eventFormat, id) => {
    const disciplineCategory = getDisciplineCategoryOfDiscipline(eventFormat, id);
    const discipline = disciplineCategory.disciplines.find((x) => x.id === id);
    if (discipline === undefined) {
        throw new Error(`Invalid discipline id: ${id}`);
    }
    return discipline;
};

const formatResultValue = (
    eventFormat,
    disciplineId,
    isFailedAttempt,
    value,
    { isAppendUnit = true, isShortUnit = false } = {},
) => {
    if (isFailedAttempt) {
        return "Fehlversuch";
    }
    const discipline = getDiscipline(eventFormat, disciplineId);
    let string;
    switch (discipline.inputUnit) {
        case Unit.METERS:
        case Unit.SECONDS:
            string = value.toLocaleString("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            break;
        case Unit.POINTS:
        case Unit.ZONE:
            string = value.toLocaleString("de-DE", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            });
            break;
        case Unit.MINUTES: {
            const nonTotalSeconds = value % 60;
            string = `${Math.floor(value / 60)}:${nonTotalSeconds < 10 ? "0" : ""}${nonTotalSeconds}`;
            break;
        }
        default:
            throw new Error(`Invalid unit: ${discipline.inputUnit}`);
    }
    if (isAppendUnit) {
        string += ` ${formatUnit(discipline.inputUnit, isShortUnit)}`;
    }
    return string;
};

const generateDownloadFileName = (name, extension) =>
    `Bujus - ${name} - ${dateFns.getTime(new Date())}.${extension}`;

const generateFullName = (firstName, lastName) => `${firstName} ${lastName}`;

const defaultCertSorting = [
    {
        path: "participant.name.last",
        order: Order.ASCENDING,
    },
    {
        path: "participant.name.first",
        order: Order.ASCENDING,
    },
    {
        path: "participant.schoolInternalId",
        order: Order.ASCENDING,
    },
    {
        path: "participant.class",
        order: Order.ASCENDING,
    },
    {
        path: "participant.group.name",
        order: Order.ASCENDING,
    },
    // No sorting on evaluation
    {
        path: "points",
        order: Order.ASCENDING,
    },
    {
        path: "type",
        searchablePath: "_searchableType",
        order: Order.ASCENDING,
    },
    {
        path: "id",
        order: Order.ASCENDING,
    },
];

const defaultDisciplineSorting = [
    {
        path: "name",
        order: Order.ASCENDING,
    },
    {
        path: "id",
        order: Order.ASCENDING,
    },
];

const defaultEventSorting = [
    {
        path: "name",
        order: Order.ASCENDING,
    },
    {
        path: "start",
        order: Order.ASCENDING,
    },
    {
        path: "warmUpStart",
        order: Order.ASCENDING,
    },
    {
        path: "end",
        order: Order.ASCENDING,
    },
    {
        path: "n3WordId",
        order: Order.ASCENDING,
    },
    {
        path: "limits.helper",
        order: Order.ASCENDING,
    },
    {
        path: "limits.participant",
        order: Order.ASCENDING,
    },
    {
        path: "limits.group",
        order: Order.ASCENDING,
    },
    {
        path: "limits.result",
        order: Order.ASCENDING,
    },
    {
        path: "id",
        order: Order.ASCENDING,
    },
];

const defaultGroupSorting = [
    {
        path: "name",
        order: Order.ASCENDING,
    },
    {
        path: "id",
        order: Order.ASCENDING,
    },
];

const defaultHelperSorting = [
    {
        path: "name",
        order: Order.ASCENDING,
    },
    {
        path: "username",
        order: Order.ASCENDING,
    },
    {
        path: "isActive",
        order: Order.ASCENDING,
    },
    {
        path: "id",
        order: Order.ASCENDING,
    },
];

const defaultParticipantSorting = [
    {
        path: "name.last",
        order: Order.ASCENDING,
    },
    {
        path: "name.first",
        order: Order.ASCENDING,
    },
    {
        path: "schoolInternalId",
        order: Order.ASCENDING,
    },
    {
        path: "gender",
        searchablePath: "_searchableGender",
        order: Order.ASCENDING,
    },
    {
        path: "birthYear",
        order: Order.ASCENDING,
    },
    {
        path: "class",
        order: Order.ASCENDING,
    },
    {
        path: "group.name",
        order: Order.ASCENDING,
    },
    {
        path: "id",
        order: Order.ASCENDING,
    },
];

const defaultResultSorting = [
    {
        path: "participant.name.last",
        order: Order.ASCENDING,
    },
    {
        path: "participant.name.first",
        order: Order.ASCENDING,
    },
    {
        path: "participant.schoolInternalId",
        order: Order.ASCENDING,
    },
    {
        path: "participant.class",
        order: Order.ASCENDING,
    },
    {
        path: "participant.group.name",
        order: Order.ASCENDING,
    },
    // TODO Only allow for contest events
    {
        path: "participant.group.classLevelRange",
        order: Order.ASCENDING,
    },
    {
        path: "discipline.name",
        order: Order.ASCENDING,
    },
    {
        path: "measuringType",
        searchablePath: "_searchableMeasuringType",
        order: Order.ASCENDING,
    },
    {
        path: "value",
        order: Order.ASCENDING,
    },
    {
        path: "inputted",
        order: Order.DESCENDING,
    },
    // Exceptional sorting
    {
        path: "helper.name",
        order: Order.ASCENDING,
    },
    {
        path: "helper.username",
        order: Order.ASCENDING,
    },
    {
        path: "helper.name",
        order: Order.ASCENDING,
    },
    {
        path: "helper.username",
        order: Order.ASCENDING,
    },
    {
        path: "id",
        order: Order.ASCENDING,
    },
];

// Mirrors the MongoDB collation of the models (locale "de", strength 2), so client side sorting matches server side sorting.
const collator = new Intl.Collator("de", { sensitivity: "accent" });

const compareValues = (a, b) => {
    if (typeof a === "string" && typeof b === "string") {
        return collator.compare(a, b);
    }
    if (a > b) {
        return 1;
    }
    if (a < b) {
        return -1;
    }
    return 0;
};

const generateSortingOptions = (defaultSorting, listingModifiers = undefined) => {
    let sorting = [];
    if (listingModifiers === undefined) {
        sorting = defaultSorting;
    } else {
        sorting = [
            {
                path: listingModifiers.fieldSorting.path,
                order: listingModifiers.fieldSorting.order,
            },
            ...defaultSorting.filter((x) => x.path !== listingModifiers.fieldSorting.path),
        ];
    }
    return (a, b) => {
        // eslint-disable-next-line no-restricted-syntax
        for (const fieldSorting of sorting) {
            const comparison = compareValues(get(a, fieldSorting.path), get(b, fieldSorting.path));
            if (comparison !== 0) {
                return fieldSorting.order === Order.DESCENDING ? -comparison : comparison;
            }
        }
        return 0;
    };
};

const SpecialGroupId = {
    CREATION: "CREATION",
    UNALLOCATED: "UNALLOCATED",
};

const sortGroupsByDefaultSorting = (hydratedGroups) => {
    hydratedGroups.sort(
        generateSortingOptions(defaultGroupSorting, {
            fieldSorting: {
                path: "name",
                order: Order.ASCENDING,
            },
        }),
    );
    hydratedGroups.forEach((x) => {
        x.participants.sort(
            generateSortingOptions(defaultParticipantSorting, {
                fieldSorting: {
                    path: "name.last",
                    order: Order.ASCENDING,
                },
            }),
        );
    });
    return hydratedGroups;
};

const generateHydratedGroups = (groups, participants) =>
    sortGroupsByDefaultSorting([
        {
            id: SpecialGroupId.UNALLOCATED,
            name: "Nicht zugewiesen",
            participants: participants.filter((x) => x.group === undefined),
        },
        ...groups.map((x) => {
            const { participantIds, ...group } = x;
            return {
                ...group,
                participants: participants.filter(
                    (y) => y.group !== undefined && y.group.id === group.id,
                ),
            };
        }),
    ]);

const splitHydratedGroupsByGender = (hydratedGroups) =>
    hydratedGroups.reduce(
        (xAccumulator, x) => [
            ...xAccumulator,
            ...Object.values(Gender).map((y) => ({
                ...x,
                id: `${x.id}.${y}`,
                name: `${x.name} ${y}`,
                participants: x.participants.filter((z) => z.gender === y),
            })),
        ],
        [],
    );

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

const generateNanoId = () => {
    let nanoId = "";
    for (let i = 0; i < 21; i += 1) {
        nanoId += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    }
    return nanoId;
};

const fillerCharacters = [
    "b",
    "c",
    "d",
    "f",
    "g",
    "h",
    "j",
    "k",
    "m",
    "n",
    "p",
    "q",
    "r",
    "s",
    "t",
    "v",
    "w",
    "x",
    "y",
    "z",
];
const vowelCharacters = ["a", "e", "u"];
const specialCharacters = ["!", "#", "%", "&", "(", ")", "*", "?", "@", "^"];

function generatePassword() {
    let password = "";
    for (let i = 0; i < 3; i += 1) {
        const filler = fillerCharacters[Math.floor(Math.random() * fillerCharacters.length)];
        password += Math.round(Math.random()) === 0 ? filler : filler.toUpperCase();
        const vowel = vowelCharacters[Math.floor(Math.random() * vowelCharacters.length)];
        password += Math.round(Math.random()) === 0 ? vowel : vowel.toUpperCase();
    }
    // Make sure password has at least one uppercase and one lowercase character
    if (password === password.toUpperCase()) {
        const characterToChangeIndex = Math.floor(Math.random() * password.length);
        password =
            password.substring(0, characterToChangeIndex) +
            password[characterToChangeIndex].toLowerCase() +
            password.substring(characterToChangeIndex + 1);
    }
    if (password === password.toLowerCase()) {
        const characterToChangeIndex = Math.floor(Math.random() * password.length);
        password =
            password.substring(0, characterToChangeIndex) +
            password[characterToChangeIndex].toUpperCase() +
            password.substring(characterToChangeIndex + 1);
    }
    password += Math.floor(Math.random() * 89) + 10;
    // Change zeros to a non zero number
    password = password.replace(/0/g, Math.floor(Math.random() * 9) + 1); // TODO Use replaceAll when Android supports it
    password += specialCharacters[Math.floor(Math.random() * specialCharacters.length)];
    return password;
}

const examplePasswords = [
    "FaJeZo28^",
    "YoSAqo33!",
    "CADOpO88#",
    "XaSemo92#",
    "BAyetU17%",
    "SoxEYE91^",
    "vaqoZA68!",
    "JOnANe85#",
    "xacozO68)",
    "domaCo56&",
];

const usernameLengthLimit = 8;

const generateUsername = (name) =>
    name
        .replace(/[^a-zA-Z]/g, "")
        .toLowerCase()
        .padStart(2, "ab")
        .substring(0, usernameLengthLimit);

const germanStates = [
    "Baden-Württemberg",
    "Bayern",
    "Berlin",
    "Brandenburg",
    "Bremen",
    "Hamburg",
    "Hessen",
    "Mecklenburg-Vorpommern",
    "Niedersachsen",
    "Nordrhein-Westfalen",
    "Rheinland-Pfalz",
    "Saarland",
    "Sachsen",
    "Sachsen-Anhalt",
    "Schleswig-Holstein",
    "Thüringen",
];

const getMongooseReferenceId = (mongooseReference) =>
    !/^[0-9a-fA-F]{24}$/.test(mongooseReference.toString())
        ? // && mongooseReference.id !== undefined
          mongooseReference.id.toString()
        : mongooseReference.toString();

const getPreselectedDiscipline = (
    event,
    disciplinePreselection,
    participant,
    disciplineCategoryId,
) => {
    let disciplineId;
    switch (event.format) {
        case EventFormat.CONTEST:
            if (participant.group === undefined) {
                disciplineId = getDisciplineCategory(event.format, disciplineCategoryId)
                    .disciplines[0].id;
            } else {
                disciplineId =
                    disciplinePreselection[participant.group.classLevelRange][disciplineCategoryId];
            }
            break;
        case EventFormat.COMPETITION:
            disciplineId =
                disciplinePreselection[participant.gender][
                    calculateAgeGroup(event.start, participant.birthYear)
                ][disciplineCategoryId];
            break;
        default:
            throw new Error(`Invalid event format: ${event.format}`);
    }
    return getDiscipline(event.format, disciplineId);
};

const insertChar = (string, index, char) =>
    string.substring(0, index) + char + string.substring(index);

const parseAndReviveJson = (json) => {
    try {
        return JSON.parse(json, jsonDateParser);
    } catch {
        return json;
    }
};

const prefixObjectKeys = (object, prefix = undefined) => {
    if (prefix === undefined) {
        return object;
    }
    const prefixedObject = {};
    Object.keys(object).forEach((key) => {
        prefixedObject[`${prefix} ${key}`] = object[key];
    });
    return prefixedObject;
};

const replaceChar = (string, index, char) =>
    string.substring(0, index) + char + string.substring(index + 1);

const parseResultValue = (eventFormat, disciplineId, string) => {
    const discipline = getDiscipline(eventFormat, disciplineId);
    if (discipline.inputUnit === Unit.MINUTES) {
        const [minutes, seconds] = string.split(":");
        return Number(minutes) * 60 + Number(seconds);
    }
    return Number(string.replace(/,/g, ".")); // TODO Use replaceAll when Android supports it
};

const floatingNumberResultValueMask = {
    format: (value) => {
        let digits = value.replace(/\D/g, "").replace(/^0+/, "0");
        if (digits[0] === "0") {
            digits = digits.slice(-4);
        } else {
            digits = digits.slice(0, 4);
        }
        const lastTwoDigits = digits.slice(-2);
        const leadingDigits = digits.slice(0, -2);
        switch (digits.length) {
            case 0:
                return "_,__";
            case 1:
                return `_,_${digits}`;
            case 2:
                return `_,${digits}`;
            default:
                return `${leadingDigits},${lastTwoDigits}`;
        }
    },
    defaultValue: "_,__",
    maxLength: 5,
};

const integerNumberResultValueMask = {
    format: (value) => {
        const digits = value.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 2);
        switch (digits.length) {
            case 0:
                return "_";
            case 1:
                return `${digits}`;
            default:
                return digits;
        }
    },
    defaultValue: "_",
    maxLength: 2,
};

const minutesResultValueMask = {
    format: (value) => {
        let digits = value.replace(/\D/g, "").replace(/^0+/, "0");
        if (digits[0] === "0") {
            digits = digits.slice(-4);
        } else {
            digits = digits.slice(0, 4);
        }
        const lastTwoDigits = digits.slice(-2);
        const leadingDigits = digits.slice(0, -2);
        switch (digits.length) {
            case 0:
                return "_:__";
            case 1:
                return `_:_${digits}`;
            case 2:
                return `_:${digits}`;
            default:
                return `${leadingDigits}:${lastTwoDigits}`;
        }
    },
    defaultValue: "_:__",
    maxLength: 5,
};

const unitToResultValueMaskMap = {
    [Unit.METERS]: floatingNumberResultValueMask,
    [Unit.MINUTES]: minutesResultValueMask,
    [Unit.POINTS]: integerNumberResultValueMask,
    [Unit.SECONDS]: floatingNumberResultValueMask,
    [Unit.ZONE]: integerNumberResultValueMask,
};

const checkIsResultValueInputValueEmpty = (value) =>
    Object.values(unitToResultValueMaskMap)
        .map((x) => x.defaultValue)
        .includes(value);

const formatResultValueInputValue = (eventFormat, disciplineId, isFailedAttempt, value) => {
    if (isFailedAttempt) {
        return unitToResultValueMaskMap[getDiscipline(eventFormat, disciplineId).inputUnit]
            .defaultValue;
    }
    return formatResultValue(eventFormat, disciplineId, isFailedAttempt, value, {
        isAppendUnit: false,
    });
};

const transformToMinimalWhiteSpaceString = (string) => string.replace(/\s+/g, " ").trim();

const transformToNotEmptyString = (text = undefined) =>
    text === undefined || text === null || text === "" ? "-" : text;

const transformToShortedString = (string, maxLength) => {
    if (string.length > maxLength) {
        return `${string.substring(0, maxLength - 3).trimEnd()}...`;
    }
    return string;
};

const transformToValidSearchValue = (searchValue, isShortSearchValue = false) => {
    const minimalWhiteSpaceSearchValue = transformToMinimalWhiteSpaceString(searchValue);
    if (minimalWhiteSpaceSearchValue.length < (isShortSearchValue ? 1 : 3)) {
        return "";
    }
    return minimalWhiteSpaceSearchValue;
};

const version = "4.5.21";

// Measure paper dimensions in 0.25 mm steps
// Set element positions in 0.5 pt steps
// Next time use the mm to pt conversion, currently scale helper line is more our own length, not just converted 100mm to pt. Also all other values are based on this
const contestEvent2023HonorCertWidth = 845.43; // 298.25 mm
const contestEvent2023HonorCertHeight = 595.28; // 210 mm
const contestEvent2023HonorAlignmentLineDistanceX = 46;
const contestEvent2023HonorAlignmentLineDistanceY = 50;
const contestEvent2023HonorCertTemplatePreset = {
    id: "000000000003000000000003",
    year: 2023,
    certType: CertType.HONOR,
    certTemplate: {
        dimensions: {
            width: contestEvent2023HonorCertWidth,
            height: contestEvent2023HonorCertHeight,
        },
        alignmentLineDistance: {
            x: contestEvent2023HonorAlignmentLineDistanceX,
            y: contestEvent2023HonorAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 747,
                    y: 180.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 624,
                    y: 235.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 707,
                    y: 235.5,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 624,
                    y: 265,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 708,
                    y: 265,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 637,
                    y: 311.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.points",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 697,
                    y: 375,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023HonorAlignmentLineDistanceX,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 2),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023HonorAlignmentLineDistanceX,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 1),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023HonorAlignmentLineDistanceX,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 0),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023HonorCertWidth / 2 - 292 / 5, // 10 cm / 5 = 2 cm
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 0),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.BOTTOM,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 697 - 96,
                    y: 475,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 697,
                    y: 475,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        contestEvent2023HonorCertWidth -
                        contestEvent2023HonorAlignmentLineDistanceX / 3,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const contestEvent2023WinnerCertWidth = 411.73; // 145.25 mm
const contestEvent2023WinnerCertHeight = 595.28; // 210 mm
const contestEvent2023WinnerAlignmentLineDistanceX = 53;
const contestEvent2023WinnerAlignmentLineDistanceY = 49;
const contestEvent2023WinnerCertTemplatePreset = {
    id: "000000000003000000000004",
    year: 2023,
    certType: CertType.WINNER,
    certTemplate: {
        dimensions: {
            width: contestEvent2023WinnerCertWidth,
            height: contestEvent2023WinnerCertHeight,
        },
        alignmentLineDistance: {
            x: contestEvent2023WinnerAlignmentLineDistanceX,
            y: contestEvent2023WinnerAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 326,
                    y: 175 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 229.5 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 275,
                    y: 229.5 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 259 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 276,
                    y: 259 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 220.5,
                    y: 305.5 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.points",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 279.5,
                    y: 369 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        contestEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 1),
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        contestEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 2),
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY -
                        120,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5 - 96,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        contestEvent2023WinnerCertWidth -
                        contestEvent2023WinnerAlignmentLineDistanceX / 3,
                    y:
                        contestEvent2023WinnerCertHeight -
                        contestEvent2023WinnerAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const contestEvent2023ParticipationCertWidth = 411.73; // 145.25 mm
const contestEvent2023ParticipationCertHeight = 595.28; // 210 mm
const contestEvent2023ParticipationAlignmentLineDistanceX = 53;
const contestEvent2023ParticipationAlignmentLineDistanceY = 49;
const contestEvent2023ParticipationCertTemplatePreset = {
    id: "000000000003000000000005",
    year: 2023,
    certType: CertType.PARTICIPATION,
    certTemplate: {
        dimensions: {
            width: contestEvent2023ParticipationCertWidth,
            height: contestEvent2023ParticipationCertHeight,
        },
        alignmentLineDistance: {
            x: contestEvent2023ParticipationAlignmentLineDistanceX,
            y: contestEvent2023ParticipationAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 326,
                    y: 175 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 229.5 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 275,
                    y: 229.5 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 259 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 276,
                    y: 259 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 220.5,
                    y: 305.5 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.points",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 279.5,
                    y: 369 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        contestEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 1),
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        contestEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 2),
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: contestEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        contestEvent2023HonorCertHeight -
                        contestEvent2023HonorAlignmentLineDistanceY -
                        120,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5 - 96,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        contestEvent2023ParticipationCertWidth -
                        contestEvent2023ParticipationAlignmentLineDistanceX / 3,
                    y:
                        contestEvent2023ParticipationCertHeight -
                        contestEvent2023ParticipationAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const weakContestEvent2025HonorCertWidth = 845.43; // 298.25 mm
const weakContestEvent2025HonorCertHeight = 595.28; // 210 mm
const weakContestEvent2025HonorAlignmentLineDistanceX = 46;
const weakContestEvent2025HonorAlignmentLineDistanceY = 50;
const weakContestEvent2025HonorCertTemplatePreset = {
    id: "000000000003000000000006",
    year: 2025,
    certType: CertType.HONOR,
    certTemplate: {
        dimensions: {
            width: weakContestEvent2025HonorCertWidth,
            height: weakContestEvent2025HonorCertHeight,
        },
        alignmentLineDistance: {
            x: weakContestEvent2025HonorAlignmentLineDistanceX,
            y: weakContestEvent2025HonorAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 747,
                    y: 180.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 624,
                    y: 235.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 707,
                    y: 235.5,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 624,
                    y: 265,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 708,
                    y: 265,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 637,
                    y: 311.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.flippedRankPoints",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 697,
                    y: 375,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025HonorAlignmentLineDistanceX,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 2),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025HonorAlignmentLineDistanceX,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 1),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025HonorAlignmentLineDistanceX,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 0),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025HonorCertWidth / 2 - 292 / 5, // 10 cm / 5 = 2 cm
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 0),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.BOTTOM,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 697 - 96,
                    y: 475,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 697,
                    y: 475,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        weakContestEvent2025HonorCertWidth -
                        weakContestEvent2025HonorAlignmentLineDistanceX / 3,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const weakContestEvent2025WinnerCertWidth = 411.73; // 145.25 mm
const weakContestEvent2025WinnerCertHeight = 595.28; // 210 mm
const weakContestEvent2025WinnerAlignmentLineDistanceX = 53;
const weakContestEvent2025WinnerAlignmentLineDistanceY = 49;
const weakContestEvent2025WinnerCertTemplatePreset = {
    id: "000000000003000000000007",
    year: 2025,
    certType: CertType.WINNER,
    certTemplate: {
        dimensions: {
            width: weakContestEvent2025WinnerCertWidth,
            height: weakContestEvent2025WinnerCertHeight,
        },
        alignmentLineDistance: {
            x: weakContestEvent2025WinnerAlignmentLineDistanceX,
            y: weakContestEvent2025WinnerAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 326,
                    y: 175 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 229.5 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 275,
                    y: 229.5 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 259 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 276,
                    y: 259 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 220.5,
                    y: 305.5 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.flippedRankPoints",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 279.5,
                    y: 369 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025WinnerAlignmentLineDistanceX + 40,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        weakContestEvent2025WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 1),
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        weakContestEvent2025WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 2),
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025WinnerAlignmentLineDistanceX + 40,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY -
                        120,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5 - 96,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        weakContestEvent2025WinnerCertWidth -
                        weakContestEvent2025WinnerAlignmentLineDistanceX / 3,
                    y:
                        weakContestEvent2025WinnerCertHeight -
                        weakContestEvent2025WinnerAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const weakContestEvent2025ParticipationCertWidth = 411.73; // 145.25 mm
const weakContestEvent2025ParticipationCertHeight = 595.28; // 210 mm
const weakContestEvent2025ParticipationAlignmentLineDistanceX = 53;
const weakContestEvent2025ParticipationAlignmentLineDistanceY = 49;
const weakContestEvent2025ParticipationCertTemplatePreset = {
    id: "000000000003000000000008",
    year: 2025,
    certType: CertType.PARTICIPATION,
    certTemplate: {
        dimensions: {
            width: weakContestEvent2025ParticipationCertWidth,
            height: weakContestEvent2025ParticipationCertHeight,
        },
        alignmentLineDistance: {
            x: weakContestEvent2025ParticipationAlignmentLineDistanceX,
            y: weakContestEvent2025ParticipationAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 326,
                    y: 175 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 229.5 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 275,
                    y: 229.5 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 207,
                    y: 259 + 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 276,
                    y: 259 + 3,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 220.5,
                    y: 305.5 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.flippedRankPoints",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 279.5,
                    y: 369 + 2,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025WinnerAlignmentLineDistanceX + 40,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        weakContestEvent2025WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 1),
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        weakContestEvent2025WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 2),
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: weakContestEvent2025WinnerAlignmentLineDistanceX + 40,
                    y:
                        weakContestEvent2025HonorCertHeight -
                        weakContestEvent2025HonorAlignmentLineDistanceY -
                        120,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5 - 96,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 279.5,
                    y: 491,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        weakContestEvent2025ParticipationCertWidth -
                        weakContestEvent2025ParticipationAlignmentLineDistanceX / 3,
                    y:
                        weakContestEvent2025ParticipationCertHeight -
                        weakContestEvent2025ParticipationAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const competitionEvent2023HonorCertWidth = 845.43; // 298.25 mm
const competitionEvent2023HonorCertHeight = 595.28; // 210 mm
const competitionEvent2023HonorAlignmentLineDistanceX = 46;
const competitionEvent2023HonorAlignmentLineDistanceY = 50;
const competitionEvent2023HonorCertTemplatePreset = {
    id: "000000000003000000000000",
    year: 2023,
    certType: CertType.HONOR,
    certTemplate: {
        dimensions: {
            width: competitionEvent2023HonorCertWidth,
            height: competitionEvent2023HonorCertHeight,
        },
        alignmentLineDistance: {
            x: competitionEvent2023HonorAlignmentLineDistanceX,
            y: competitionEvent2023HonorAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 757.5,
                    y: 180.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 634.5,
                    y: 235.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 707,
                    y: 235.5,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 634.5,
                    y: 265,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 708,
                    y: 265,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 647.5,
                    y: 311.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.points",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 707.5,
                    y: 374,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023HonorAlignmentLineDistanceX,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 2),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023HonorAlignmentLineDistanceX,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 1),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023HonorAlignmentLineDistanceX,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 0),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023HonorCertWidth / 2 - 292 / 5, // 10 cm / 5 = 2 cm
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY -
                        calculateTextSpacing(12, 1.4, 0),
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.BOTTOM,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 707.5 - 96,
                    y: 474,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 707.5,
                    y: 474,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        competitionEvent2023HonorCertWidth -
                        competitionEvent2023HonorAlignmentLineDistanceX / 3,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const competitionEvent2023WinnerCertWidth = 411.73; // 145.25 mm
const competitionEvent2023WinnerCertHeight = 595.28; // 210 mm
const competitionEvent2023WinnerAlignmentLineDistanceX = 53;
const competitionEvent2023WinnerAlignmentLineDistanceY = 49;
const competitionEvent2023WinnerCertTemplatePreset = {
    id: "000000000003000000000001",
    year: 2023,
    certType: CertType.WINNER,
    certTemplate: {
        dimensions: {
            width: competitionEvent2023WinnerCertWidth,
            height: competitionEvent2023WinnerCertHeight,
        },
        alignmentLineDistance: {
            x: competitionEvent2023WinnerAlignmentLineDistanceX,
            y: competitionEvent2023WinnerAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 320,
                    y: 177.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 201,
                    y: 232,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 275,
                    y: 232,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 201,
                    y: 261.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 276,
                    y: 261.5,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 214.5,
                    y: 308,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.points",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 273.5,
                    y: 371.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        competitionEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 1),
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        competitionEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 2),
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY -
                        120,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 273.5 - 96,
                    y: 491.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 273.5,
                    y: 491.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        competitionEvent2023WinnerCertWidth -
                        competitionEvent2023WinnerAlignmentLineDistanceX / 3,
                    y:
                        competitionEvent2023WinnerCertHeight -
                        competitionEvent2023WinnerAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const competitionEvent2023ParticipationCertWidth = 411.73; // 145.25 mm
const competitionEvent2023ParticipationCertHeight = 595.28; // 210 mm
const competitionEvent2023ParticipationAlignmentLineDistanceX = 53;
const competitionEvent2023ParticipationAlignmentLineDistanceY = 49;
const competitionEvent2023ParticipationCertTemplatePreset = {
    id: "000000000003000000000002",
    year: 2023,
    certType: CertType.PARTICIPATION,
    certTemplate: {
        dimensions: {
            width: competitionEvent2023ParticipationCertWidth,
            height: competitionEvent2023ParticipationCertHeight,
        },
        alignmentLineDistance: {
            x: competitionEvent2023ParticipationAlignmentLineDistanceX,
            y: competitionEvent2023ParticipationAlignmentLineDistanceY,
        },
        elements: [
            {
                id: "eventStartYear",
                name: "Veranstaltungs-Start Jahreszahl",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 320,
                    y: 177.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.last2StartYearDigits",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "line1",
                name: "Linie 1",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 201,
                    y: 232,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 275,
                    y: 232,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "line2",
                name: "Linie 2",
                type: CertTemplateElementType.LINE,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 201,
                    y: 261.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                endPosition: {
                    x: 276,
                    y: 261.5,
                },
                width: 1,
                color: colors.black,
            },
            {
                id: "points",
                name: "Punkte",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 214.5,
                    y: 308,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.points",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 20,
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "name",
                name: "Name",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.STANDARD,
                isVisible: true,
                position: {
                    x: 273.5,
                    y: 371.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.name.full",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.CENTER,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                dynamicSize: {
                    maxWidth: 225,
                    max: 20,
                    min: 12,
                },
                lineHeight: 1.4,
                color: colors.black,
            },
            {
                id: "date",
                name: "Veranstaltung Datum",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "event.start",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantSchoolInternalId",
                name: "Teilnehmer Schulinterne-ID",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        competitionEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 1),
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.schoolInternalId",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "participantClass",
                name: "Teilnehmer Klasse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        competitionEvent2023WinnerAlignmentLineDistanceX +
                        40 +
                        calculateTextSpacing(12, 1.4, 2),
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "cert.bestResultsSummary",
                name: "Teilnehmer Beste Ergebnisse",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: competitionEvent2023WinnerAlignmentLineDistanceX + 40,
                    y:
                        competitionEvent2023HonorCertHeight -
                        competitionEvent2023HonorAlignmentLineDistanceY -
                        120,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.bestResultsSummary",
                rotation: -90,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.LEFT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.grayBlack,
            },
            {
                id: "rubberStamp",
                name: "Stempel",
                type: CertTemplateElementType.RUBBER_STAMP,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 273.5 - 96,
                    y: 491.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "signature",
                name: "Unterschrift",
                type: CertTemplateElementType.SIGNATURE,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x: 273.5,
                    y: 491.5,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
            },
            {
                id: "participantClass2",
                name: "Teilnehmer Klasse (Zur Sortierung)",
                type: CertTemplateElementType.TEXT,
                purpose: CertTemplateElementPurpose.EXTRA,
                isVisible: false,
                position: {
                    x:
                        competitionEvent2023ParticipationCertWidth -
                        competitionEvent2023ParticipationAlignmentLineDistanceX / 3,
                    y:
                        competitionEvent2023ParticipationCertHeight -
                        competitionEvent2023ParticipationAlignmentLineDistanceY / 3,
                },
                positionOffset: {
                    max: 50,
                    step: 0.5,
                    x: 0,
                    y: 0,
                },
                fieldPath: "cert.participant.class",
                rotation: 0,
                alignment: {
                    horizontal: CertTemplateHorizontalTextAlignment.RIGHT,
                    vertical: CertTemplateVerticalTextAlignment.TOP,
                },
                family: "Helvetica",
                size: 12,
                lineHeight: 1.4,
                color: colors.black,
            },
        ],
        scale: {
            max: 100,
            step: 0.5,
            value: 0,
        },
        positionOffset: {
            max: 100,
            step: 0.5,
            x: 0,
            y: 0,
        },
    },
};

const eventFormatToCertTypeToCertTemplatePresets = {
    [EventFormat.CONTEST]: {
        [CertType.HONOR]: [
            contestEvent2023HonorCertTemplatePreset,
            weakContestEvent2025HonorCertTemplatePreset,
        ],
        [CertType.WINNER]: [
            contestEvent2023WinnerCertTemplatePreset,
            weakContestEvent2025WinnerCertTemplatePreset,
        ],
        [CertType.PARTICIPATION]: [
            contestEvent2023ParticipationCertTemplatePreset,
            weakContestEvent2025ParticipationCertTemplatePreset,
        ],
    },
    [EventFormat.COMPETITION]: {
        [CertType.HONOR]: [competitionEvent2023HonorCertTemplatePreset],
        [CertType.WINNER]: [competitionEvent2023WinnerCertTemplatePreset],
        [CertType.PARTICIPATION]: [competitionEvent2023ParticipationCertTemplatePreset],
    },
};

const checkIsMovingDisciplineCategory = (id) =>
    id === DisciplineCategoryId.SPRINTING || id === DisciplineCategoryId.RUNNING;

const generateExampleEvent = (id, eventFormat) => ({
    id,
    school: "000000000009000000000000",
    format: eventFormat,
    n3WordId: "frucht.apfel.zitrone",
    name: `BJS ${dateFns.getYear(new Date())}`,
    start: dateFns.startOfDay(new Date()),
    warmUpStart: dateFns.sub(dateFns.startOfDay(new Date()), {
        milliseconds: config.event.schedule.warmUpTime,
    }),
    end: dateFns.add(dateFns.startOfDay(new Date()), {
        milliseconds: config.event.schedule.expireTime,
    }),
    limits: {
        helper: 2,
        participant: 5,
        group: 5,
        result: 250,
    },
});

const contestEventExampleEvent = generateExampleEvent(
    "000000000009000000000001",
    EventFormat.CONTEST,
);

const competitionEventExampleEvent = generateExampleEvent(
    "000000000009000000000002",
    EventFormat.COMPETITION,
);

const eventFormatToExampleEventMap = {
    [EventFormat.CONTEST]: contestEventExampleEvent,
    [EventFormat.COMPETITION]: competitionEventExampleEvent,
};

const contestEvent9aGroup = {
    id: "000000000009000000000003",
    event: contestEventExampleEvent,
    name: "9a",
    classLevelRange: ClassLevelRange.N_9_AND_10,
};

const contestEvent8cGroup = {
    id: "000000000009000000000004",
    event: contestEventExampleEvent,
    name: "8c",
    classLevelRange: ClassLevelRange.N_7_AND_8,
};

const contestEventExampleParticipant = {
    id: "000000000009000000000005",
    event: contestEventExampleEvent,
    schoolInternalId: "38044",
    name: {
        first: "Harry",
        last: "Potter",
    },
    gender: Gender.MALE,
    birthYear: dateFns.sub(contestEventExampleEvent.start, { years: 15 }).getFullYear(),
    class: "9a",
    group: contestEvent9aGroup,
};

const contestEventExampleParticipant2 = {
    id: "000000000009000000000006",
    event: contestEventExampleEvent,
    schoolInternalId: "63161",
    name: {
        first: "Hermine",
        last: "Granger",
    },
    gender: Gender.MALE,
    birthYear: dateFns.sub(contestEventExampleEvent.start, { years: 15 }).getFullYear(),
    class: "9a",
    group: contestEvent9aGroup,
};

const contestEventExampleParticipant3 = {
    id: "000000000009000000000007",
    event: contestEventExampleEvent,
    schoolInternalId: "92769",
    name: {
        first: "Ron",
        last: "Weasley",
    },
    gender: Gender.MALE,
    birthYear: dateFns.sub(contestEventExampleEvent.start, { years: 14 }).getFullYear(),
    class: "8c",
    group: contestEvent8cGroup,
};

const competitionEventExampleParticipant = {
    id: "000000000009000000000008",
    event: competitionEventExampleEvent.id,
    schoolInternalId: "38044",
    name: {
        first: "Harry",
        last: "Potter",
    },
    gender: Gender.MALE,
    birthYear: dateFns.sub(competitionEventExampleEvent.start, { years: 15 }).getFullYear(),
    class: "9a",
};

const competitionEventExampleParticipant2 = {
    id: "000000000009000000000009",
    event: competitionEventExampleEvent.id,
    schoolInternalId: "63161",
    name: {
        first: "Hermine",
        last: "Granger",
    },
    gender: Gender.MALE,
    birthYear: dateFns.sub(competitionEventExampleEvent.start, { years: 15 }).getFullYear(),
    class: "9a",
};

const competitionEventExampleParticipant3 = {
    id: "00000000000900000000000a",
    event: competitionEventExampleEvent.id,
    schoolInternalId: "92769",
    name: {
        first: "Ron",
        last: "Weasley",
    },
    gender: Gender.MALE,
    birthYear: dateFns.sub(competitionEventExampleEvent.start, { years: 14 }).getFullYear(),
    class: "8c",
};

const generateExampleResult = (
    id,
    participant,
    discipline,
    measuringType,
    isFailedAttempt,
    value,
    inputted,
) => ({
    id,
    participant,
    discipline,
    ...(measuringType !== undefined && { measuringType }),
    isFailedAttempt,
    value,
    inputted,
});

const contest_event_example_a_sprinting75m_result = generateExampleResult(
    "000000000009000000000017",
    contestEventExampleParticipant,
    getDiscipline(EventFormat.CONTEST, "00000000000200000000000c"),
    undefined,
    false,
    11.41,
    new Date(),
);

const contest_event_example_a_running60min_result = generateExampleResult(
    "000000000002000000000018",
    contestEventExampleParticipant,
    getDiscipline(EventFormat.CONTEST, "00000000000200000000001e"),
    undefined,
    false,
    3,
    new Date(),
);

const contest_event_example_a_longjumping_result = generateExampleResult(
    "000000000009000000000019",
    contestEventExampleParticipant,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000030"),
    undefined,
    false,
    25,
    new Date(),
);

const contest_event_example_a_longjumping_result2 = generateExampleResult(
    "00000000000900000000001a",
    contestEventExampleParticipant2,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000030"),
    undefined,
    false,
    24,
    new Date(),
);

const contest_event_example_a_longjumping_result3 = generateExampleResult(
    "00000000000900000000001b",
    contestEventExampleParticipant3,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000030"),
    undefined,
    false,
    23,
    new Date(),
);

const contest_event_example_a_longjumping_result4 = generateExampleResult(
    "00000000000900000000001d",
    contestEventExampleParticipant2,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000030"),
    undefined,
    false,
    22,
    new Date(),
);

const contest_event_example_a_throwing_result = generateExampleResult(
    "00000000000900000000001c",
    contestEventExampleParticipant,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000040"),
    undefined,
    false,
    23,
    new Date(),
);

const contest_event_example_a_throwing_result2 = generateExampleResult(
    "00000000000900000000001e",
    contestEventExampleParticipant,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000040"),
    undefined,
    false,
    22,
    new Date(),
);

const contest_event_example_a_throwing_result3 = generateExampleResult(
    "00000000000900000000001f",
    contestEventExampleParticipant,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000040"),
    undefined,
    false,
    21,
    new Date(),
);

const contest_event_example_a_throwing_result4 = generateExampleResult(
    "000000000009000000000020",
    contestEventExampleParticipant,
    getDiscipline(EventFormat.CONTEST, "000000000002000000000040"),
    undefined,
    false,
    20,
    new Date(),
);

const contestEventExampleResults = [
    contest_event_example_a_sprinting75m_result,
    contest_event_example_a_running60min_result,
    contest_event_example_a_longjumping_result,
    contest_event_example_a_longjumping_result2,
    contest_event_example_a_longjumping_result3,
    contest_event_example_a_longjumping_result4,
    contest_event_example_a_throwing_result,
    contest_event_example_a_throwing_result2,
    contest_event_example_a_throwing_result3,
    contest_event_example_a_throwing_result4,
];

const competition_event_example_sprinting50m_participation_result = generateExampleResult(
    "00000000000900000000000b",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000000"),
    MeasuringType.MANUALLY,
    false,
    11.41,
    new Date(),
);

const competition_event_example_sprinting50m_winner_result = generateExampleResult(
    "00000000000900000000000c",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000000"),
    MeasuringType.MANUALLY,
    false,
    9.45,
    new Date(),
);

const competition_event_example_sprinting50m_honor_result = generateExampleResult(
    "00000000000900000000000d",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000000"),
    MeasuringType.MANUALLY,
    false,
    7.21,
    new Date(),
);

const competition_event_example_running1000m_participation_result = generateExampleResult(
    "00000000000900000000000e",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000004"),
    MeasuringType.MANUALLY,
    false,
    348,
    new Date(),
);

const competition_event_example_running1000m_winner_result = generateExampleResult(
    "00000000000900000000000f",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000004"),
    MeasuringType.MANUALLY,
    false,
    224,
    new Date(),
);

const competition_event_example_running1000m_honor_result = generateExampleResult(
    "000000000009000000000010",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000004"),
    MeasuringType.MANUALLY,
    false,
    169,
    new Date(),
);

const competition_event_example_longjumping_participation_result = generateExampleResult(
    "000000000009000000000011",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000008"),
    undefined,
    false,
    2.53,
    new Date(),
);

const competition_event_example_longjumping_winner_result = generateExampleResult(
    "000000000009000000000012",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000008"),
    undefined,
    false,
    3.56,
    new Date(),
);

const competition_event_example_longjumping_honor_result = generateExampleResult(
    "000000000009000000000013",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "000000000001000000000008"),
    undefined,
    false,
    6.13,
    new Date(),
);

const competition_event_example_throwing200g_participation_result = generateExampleResult(
    "000000000009000000000014",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "00000000000100000000000a"),
    undefined,
    false,
    20.39,
    new Date(),
);

const competition_event_example_throwing200g_winner_result = generateExampleResult(
    "000000000009000000000015",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "00000000000100000000000a"),
    undefined,
    false,
    35.46,
    new Date(),
);

const competition_event_example_throwing200g_honor_result = generateExampleResult(
    "000000000009000000000016",
    competitionEventExampleParticipant,
    getDiscipline(EventFormat.COMPETITION, "00000000000100000000000a"),
    undefined,
    false,
    45.23,
    new Date(),
);

const competitionEventExampleHonorResults = [
    competition_event_example_sprinting50m_honor_result,
    competition_event_example_running1000m_honor_result,
    competition_event_example_longjumping_honor_result,
    competition_event_example_throwing200g_honor_result,
];

const competitionEventExampleWinnerResults = [
    competition_event_example_sprinting50m_winner_result,
    competition_event_example_running1000m_winner_result,
    competition_event_example_longjumping_winner_result,
    competition_event_example_throwing200g_winner_result,
];

const competitionEventExampleParticipationResults = [
    competition_event_example_sprinting50m_participation_result,
    competition_event_example_running1000m_participation_result,
    competition_event_example_longjumping_participation_result,
    competition_event_example_throwing200g_participation_result,
];

const contestEventExampleHonorCert = {
    id: "000000000009000000000021",
    participant: contestEventExampleParticipant,
    isGenerated: true,
    disciplineCategoryIdToDisciplineCategoryEvaluationMap: {
        [DisciplineCategoryId.SPRINTING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "00000000000200000000000c" },
                    value: 11.41,
                },
            ],
            value: 11.41,
            rank: 1,
        },
        [DisciplineCategoryId.RUNNING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000018" },
                    value: 3,
                },
            ],
            value: 3,
            rank: 1,
        },
        [DisciplineCategoryId.JUMPING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 25,
                },
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 24,
                },
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 23,
                },
            ],
            value: 72,
            rank: 1,
        },
        [DisciplineCategoryId.THROWING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 23,
                },
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 22,
                },
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 21,
                },
            ],
            value: 66,
            rank: 1,
        },
    },
    rankSum: 4,
    groupParticipantCount: 10,
    points: 1,
    type: CertType.HONOR,
};

const contestEventExampleWinnerCert = {
    id: "000000000009000000000022",
    participant: contestEventExampleParticipant,
    isGenerated: true,
    disciplineCategoryIdToDisciplineCategoryEvaluationMap: {
        [DisciplineCategoryId.SPRINTING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "00000000000200000000000c" },
                    value: 12.41,
                },
            ],
            value: 12.41,
            rank: 3,
        },
        [DisciplineCategoryId.RUNNING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000018" },
                    value: 2,
                },
            ],
            value: 2,
            rank: 3,
        },
        [DisciplineCategoryId.JUMPING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 24,
                },
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 23,
                },
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 22,
                },
            ],
            value: 69,
            rank: 3,
        },
        [DisciplineCategoryId.THROWING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 22,
                },
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 21,
                },
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 20,
                },
            ],
            value: 63,
            rank: 3,
        },
    },
    rankSum: 8,
    groupParticipantCount: 10,
    points: 3,
    type: CertType.WINNER,
};

const contestEventExampleParticipationCert = {
    id: "000000000009000000000023",
    participant: contestEventExampleParticipant,
    isGenerated: true,
    disciplineCategoryIdToDisciplineCategoryEvaluationMap: {
        [DisciplineCategoryId.SPRINTING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "00000000000200000000000c" },
                    value: 13.41,
                },
            ],
            value: 13.41,
            rank: 8,
        },
        [DisciplineCategoryId.RUNNING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000018" },
                    value: 1,
                },
            ],
            value: 1,
            rank: 8,
        },
        [DisciplineCategoryId.JUMPING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 23,
                },
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 22,
                },
                {
                    discipline: { id: "000000000002000000000030" },
                    value: 21,
                },
            ],
            value: 66,
            rank: 8,
        },
        [DisciplineCategoryId.THROWING]: {
            isValid: true,
            bestResults: [
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 21,
                },
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 20,
                },
                {
                    discipline: { id: "000000000002000000000040" },
                    value: 19,
                },
            ],
            value: 60,
            rank: 8,
        },
    },
    rankSum: 8,
    groupParticipantCount: 10,
    points: 8,
    type: CertType.PARTICIPATION,
};

const competitionEventExampleHonorCert = {
    id: "000000000009000000000017",
    participant: competitionEventExampleParticipant,
    isGenerated: true,
    disciplineCategoryIdToDisciplineCategoryEvaluationMap: {
        [DisciplineCategoryId.SPRINTING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000002"),
                value: 224,
            },
            points: 13.49,
        },
        [DisciplineCategoryId.RUNNING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000006"),
                value: 804,
            },
            points: 350,
        },
        [DisciplineCategoryId.JUMPING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000007"),
                value: 1.38,
            },
            points: 417,
        },
        [DisciplineCategoryId.THROWING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "00000000000100000000000a"),
                value: 50.12,
            },
            points: 414,
        },
    },
    points: 1266,
    type: CertType.HONOR,
};

const competitionEventExampleWinnerCert = {
    id: "000000000009000000000018",
    participant: competitionEventExampleParticipant2,
    isGenerated: true,
    disciplineCategoryIdToDisciplineCategoryEvaluationMap: {
        [DisciplineCategoryId.SPRINTING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000001"),
                value: 12.49,
            },
            points: 286,
        },
        [DisciplineCategoryId.RUNNING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000003"),
                value: 184,
            },
            points: 359,
        },
        [DisciplineCategoryId.JUMPING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000008"),
                value: 2.96,
            },
            points: 301,
        },
        [DisciplineCategoryId.THROWING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "00000000000100000000000e"),
                value: 21.64,
            },
            points: 311,
        },
    },
    points: 971,
    type: CertType.WINNER,
};

const competitionEventExampleParticipationCert = {
    id: "000000000009000000000019",
    participant: competitionEventExampleParticipant3,
    isGenerated: true,
    disciplineCategoryIdToDisciplineCategoryEvaluationMap: {
        [DisciplineCategoryId.SPRINTING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000001"),
                value: 12.14,
            },
            points: 294,
        },
        [DisciplineCategoryId.RUNNING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000003"),
                value: 193,
            },
            points: 282,
        },
        [DisciplineCategoryId.JUMPING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "000000000001000000000008"),
                value: 3.17,
            },
            points: 287,
        },
        [DisciplineCategoryId.THROWING]: {
            isValid: true,
            bestResult: {
                discipline: getDiscipline(EventFormat.COMPETITION, "00000000000100000000000a"),
                value: 31.18,
            },
            points: 294,
        },
    },
    points: 875,
    type: CertType.PARTICIPATION,
};

const eventFormatToCertTypeToExampleCertMap = {
    [EventFormat.CONTEST]: {
        [CertType.HONOR]: contestEventExampleHonorCert,
        [CertType.PARTICIPATION]: contestEventExampleParticipationCert,
        [CertType.WINNER]: contestEventExampleWinnerCert,
    },
    [EventFormat.COMPETITION]: {
        [CertType.HONOR]: competitionEventExampleHonorCert,
        [CertType.WINNER]: competitionEventExampleWinnerCert,
        [CertType.PARTICIPATION]: competitionEventExampleParticipationCert,
    },
};

const getAllowedDisciplines = (event, participant) =>
    callEventFormatFunction(
        () =>
            participant.group === undefined
                ? eventFormatToAllDisciplinesMap[event.format]
                : eventFormatToAllDisciplinesMap[event.format].filter(
                      (x) => x.allowedClassLevelRange === participant.group.classLevelRange,
                  ),
        () =>
            eventFormatToAllDisciplinesMap[event.format].filter(
                (x) =>
                    x.allowedParticipantGroups[participant.gender] !== undefined &&
                    x.allowedParticipantGroups[participant.gender].find(
                        (y) => y === calculateAgeGroup(event.start, participant.birthYear),
                    ) !== undefined,
            ),
        event.format,
    );

const allCertTemplatePresets = Object.values(eventFormatToCertTypeToCertTemplatePresets)
    .reduce((xAccumulator, x) => xAccumulator.concat(Object.values(x)), [])
    .flat();

const getCertTemplatePreset = (id) => allCertTemplatePresets.find((x) => x.id === id);

const getDisciplineCategory = (eventFormat, id) => {
    const disciplineCategory = eventFormatToDisciplineCategoriesMap[eventFormat].find(
        (x) => x.id === id,
    );
    if (disciplineCategory === undefined) {
        throw new Error(`Invalid discipline category id: ${id}`);
    }
    return disciplineCategory;
};

const movingDisciplineCategoryIds = [DisciplineCategoryId.SPRINTING, DisciplineCategoryId.RUNNING];

const groupConvert = {
    toDto: (eventFormat, group) => ({
        id: group.id,
        eventId: getMongooseReferenceId(group.event),
        name: group.name,
        ...(eventFormat === EventFormat.CONTEST && { classLevelRange: group.classLevelRange }),
    }),
    toFormattedDto: (eventFormat, group) => ({
        id: group.id,
        eventId: getMongooseReferenceId(group.event),
        name: group.name,
        ...(eventFormat === EventFormat.CONTEST && {
            classLevelRange: formatClassLevelRange(group.classLevelRange),
        }),
    }),
};

const participantConvert = {
    toDto: (eventFormat, participant) => ({
        id: participant.id,
        eventId: getMongooseReferenceId(participant.event),
        ...(participant.schoolInternalId !== undefined && {
            schoolInternalId: participant.schoolInternalId,
        }),
        name: {
            first: participant.name.first,
            last: participant.name.last,
        },
        gender: participant.gender,
        birthYear: participant.birthYear,
        ...(participant.class !== undefined && { class: participant.class }),
        ...(participant.group !== undefined && {
            group: groupConvert.toDto(eventFormat, participant.group),
        }),
    }),
    toFormattedDto: (eventFormat, participant) => ({
        id: participant.id,
        eventId: getMongooseReferenceId(participant.event),
        ...(participant.schoolInternalId !== undefined && {
            schoolInternalId: participant.schoolInternalId,
        }),
        name: {
            first: participant.name.first,
            last: participant.name.last,
            full: generateFullName(participant.name.first, participant.name.last),
        },
        gender: formatGender(participant.gender),
        birthYear: participant.birthYear.toString(),
        ...(participant.class !== undefined && { class: participant.class }),
        ...(participant.group !== undefined && {
            group: groupConvert.toFormattedDto(eventFormat, participant.group),
        }),
    }),
    toMockupCsvRow: (eventFormat, { isGenderAndBirthYearIncluded = true, prefix = undefined }) =>
        prefixObjectKeys(
            {
                "Schulinterne-ID": undefined,
                Vorname: undefined,
                Nachname: undefined,
                ...(isGenderAndBirthYearIncluded && {
                    Geschlecht: undefined,
                    Geburtsjahr: undefined,
                }),
                Klasse: undefined,
                Riege: undefined,
                ...(eventFormat === EventFormat.CONTEST && { "Riege Klassenstufe": undefined }),
            },
            prefix,
        ),
    toCsvRow: (
        eventFormat,
        participant,
        {
            isGenderAndBirthYearIncluded = true,
            isGroupNameInsteadOfGroup = false,
            prefix = undefined,
        } = {},
    ) =>
        prefixObjectKeys(
            {
                "Schulinterne-ID": participant.schoolInternalId,
                Vorname: participant.name.first,
                Nachname: participant.name.last,
                ...(isGenderAndBirthYearIncluded && {
                    Geschlecht: formatGender(participant.gender),
                    Geburtsjahr: participant.birthYear,
                }),
                Klasse: participant.class === undefined ? undefined : participant.class,
                Riege: isGroupNameInsteadOfGroup
                    ? participant.groupName === undefined
                        ? undefined
                        : participant.groupName
                    : participant.group === undefined
                      ? undefined
                      : participant.group.name,
                ...(eventFormat === EventFormat.CONTEST && {
                    "Riege Klassenstufe":
                        participant.group === undefined
                            ? undefined
                            : formatClassLevelRange(participant.group.classLevelRange),
                }),
            },
            prefix,
        ),
};

const helperConvert = {
    toDto: (helper) => ({
        id: helper.id,
        eventId: getMongooseReferenceId(helper.event),
        ...(helper.name !== undefined && { name: helper.name }),
        username: helper.username,
        password: helper.password,
        isActive: helper.isActive,
    }),
    toSelfDto: (helper) => ({
        id: helper.id,
        eventId: getMongooseReferenceId(helper.event),
        ...(helper.name !== undefined && { name: helper.name }),
        username: helper.username,
        isActive: helper.isActive,
    }),
    toMockupCsvRow: ({
        isPasswordIncluded = false,
        isIsActiveIncluded = true,
        prefix = undefined,
    }) =>
        prefixObjectKeys(
            {
                Name: undefined,
                Benutzername: undefined,
                ...(isPasswordIncluded && { Passwort: undefined }),
                ...(isIsActiveIncluded && { Aktiv: undefined }),
            },
            prefix,
        ),
    toCsvRow: (
        helper,
        { isPasswordIncluded = false, isIsActiveIncluded = true, prefix = undefined } = {},
    ) =>
        prefixObjectKeys(
            {
                Name: helper.name,
                Benutzername: helper.username,
                ...(isPasswordIncluded && { Passwort: helper.password }),
                ...(isIsActiveIncluded && { Aktiv: helper.isActive }),
            },
            prefix,
        ),
};

const getIdField = (object, fieldName) => {
    if (object[fieldName] !== undefined && object[fieldName].id !== undefined) {
        return object[fieldName].id;
    }
    if (object[`${fieldName}Id`] !== undefined) {
        return object[`${fieldName}Id`];
    }
    throw new Error("Field does not exist on object");
};

const resultConvert = {
    toDto: (eventFormat, result) => ({
        id: result.id,
        ...(result.helper !== undefined && { helper: helperConvert.toDto(result.helper) }),
        participant: participantConvert.toDto(eventFormat, result.participant),
        disciplineId: result.discipline.id,
        ...(eventFormat === EventFormat.COMPETITION &&
            checkIsMovingDisciplineCategory(
                getDisciplineCategoryOfDiscipline(eventFormat, result.discipline.id).id,
            ) && { measuringType: result.measuringType }),
        isFailedAttempt: result.isFailedAttempt,
        ...(!result.isFailedAttempt && { value: result.value }),
        inputted: result.inputted,
    }),
    toMockupCsvRow: (eventFormat, { isParticipantIncluded = true, prefix = undefined }) =>
        prefixObjectKeys(
            {
                ...helperConvert.toMockupCsvRow({ prefix: "Helfer" }),
                ...(isParticipantIncluded &&
                    participantConvert.toMockupCsvRow(eventFormat, { prefix: "Teilnehmer" })),
                Disziplinkategorie: undefined,
                Disziplin: undefined,
                ...(eventFormat === EventFormat.COMPETITION && { Messart: undefined }),
                "Ist Fehlversuch": undefined,
                "Basis Wert": undefined,
                Einheit: undefined,
                Wert: undefined,
                Eingetragen: undefined,
            },
            prefix,
        ),
    toCsvRow: (eventFormat, result, { isParticipantIncluded = true, prefix = undefined } = {}) => {
        const discipline = getDiscipline(eventFormat, result.discipline.id);
        const disciplineCategory = getDisciplineCategoryOfDiscipline(eventFormat, discipline.id);
        return prefixObjectKeys(
            {
                ...(result.helper !== undefined &&
                    helperConvert.toCsvRow(result.helper, { prefix: "Helfer" })),
                ...(isParticipantIncluded &&
                    participantConvert.toCsvRow(eventFormat, result.participant, {
                        prefix: "Teilnehmer",
                    })),
                Disziplinkategorie: disciplineCategory.name,
                Disziplin: discipline.name,
                ...(eventFormat === EventFormat.COMPETITION && {
                    Messart: !checkIsMovingDisciplineCategory(disciplineCategory.id)
                        ? undefined
                        : formatMeasuringType(result.measuringType),
                }),
                "Ist Fehlversuch": formatBool(result.isFailedAttempt),
                ...(!result.isFailedAttempt && {
                    "Basis Wert": result.value.toLocaleString("de-DE"),
                    Einheit: formatUnit(discipline.unit),
                    Wert: formatResultValue(
                        eventFormat,
                        discipline.id,
                        result.isFailedAttempt,
                        result.value,
                    ),
                }),
                Eingetragen: formatDate(result.inputted, "Pp"),
            },
            prefix,
        );
    },
    toBestResultDto: (result) => ({
        isFake: result.isFake,
        disciplineId: result.discipline.id,
        value: result.value,
    }),
    toBestResultFormattedDto: (result) => ({
        isFake: formatBool(result.isFake),
        disciplineId: result.discipline.id,
        value: result.value.toString(),
    }),
    toBestResultMockupCsvRow: ({ prefix = undefined }) =>
        prefixObjectKeys(
            {
                "Ist Gefälscht": undefined,
                Disziplin: undefined,
                "Basis Wert": undefined,
                Einheit: undefined,
                Wert: undefined,
            },
            prefix,
        ),
    toBestResultCsvRow: (eventFormat, result, { prefix = undefined } = {}) => {
        const discipline = getDiscipline(eventFormat, getIdField(result, "discipline"));
        return prefixObjectKeys(
            {
                "Ist Gefälscht": formatBool(result.isFake),
                Disziplin: discipline.name,
                "Basis Wert": result.value.toLocaleString("de-DE"),
                Einheit: formatUnit(discipline.unit),
                Wert: formatResultValue(eventFormat, discipline.id, false, result.value),
            },
            prefix,
        );
    },
};

const certConvert = {
    toGenerationResultDto: (generationResult) => ({
        counts: {
            notGenerated: generationResult.counts.notGenerated,
            generated: generationResult.counts.generated,
        },
    }),
    toDto: (eventFormat, cert) => ({
        id: cert.id,
        participant: participantConvert.toDto(eventFormat, cert.participant),
        disciplineCategoryIdToDisciplineCategoryEvaluationMap: Object.values(
            DisciplineCategoryId,
        ).reduce(
            (xAccumulator, x) => ({
                ...xAccumulator,
                [x]: {
                    isValid: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x].isValid,
                    ...(!cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x].isValid
                        ? {
                              error:
                                  cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x]
                                      .error,
                          }
                        : callEventFormatFunction(
                              () => ({
                                  value: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                      x
                                  ].value,
                                  rank: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                      x
                                  ].rank,
                              }),
                              () => ({
                                  bestResult: resultConvert.toBestResultDto(
                                      cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x]
                                          .bestResult,
                                  ),
                                  points: cert
                                      .disciplineCategoryIdToDisciplineCategoryEvaluationMap[x]
                                      .points,
                              }),
                              eventFormat,
                          )),
                    ...(eventFormat === EventFormat.CONTEST && {
                        bestResults: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                            x
                        ].bestResults.map(resultConvert.toBestResultDto),
                    }),
                },
            }),
            {},
        ),
        isGenerated: cert.isGenerated,
        ...(!cert.isGenerated
            ? { error: cert.error }
            : {
                  ...(eventFormat === EventFormat.CONTEST && {
                      rankSum: cert.rankSum,
                      groupParticipantCount: cert.groupParticipantCount,
                  }),
                  points: cert.points,
                  type: cert.type,
              }),
    }),
    toFormattedDto: (eventFormat, cert) => ({
        id: cert.id,
        participant: participantConvert.toFormattedDto(eventFormat, cert.participant),
        disciplineCategoryIdToDisciplineCategoryEvaluationMap: Object.values(
            DisciplineCategoryId,
        ).reduce(
            (xAccumulator, x) => ({
                ...xAccumulator,
                [x]: {
                    isValid: formatBool(
                        cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x].isValid,
                    ),
                    ...(!cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x].isValid
                        ? {
                              error: formatCertGenerationErrorType(
                                  cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x]
                                      .error,
                              ),
                          }
                        : callEventFormatFunction(
                              () => ({
                                  value: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                      x
                                  ].value.toString(),
                                  rank: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                      x
                                  ].rank.toString(),
                              }),
                              () => ({
                                  bestResult: resultConvert.toBestResultFormattedDto(
                                      cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x]
                                          .bestResult,
                                  ),
                                  points: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                      x
                                  ].points.toString(),
                              }),
                              eventFormat,
                          )),
                    ...(eventFormat === EventFormat.CONTEST && {
                        bestResults: cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                            x
                        ].bestResults.map(resultConvert.toBestResultFormattedDto),
                    }),
                },
            }),
            {},
        ),
        isGenerated: formatBool(cert.isGenerated),
        ...(!cert.isGenerated
            ? { error: formatCertGenerationErrorType(cert.error) }
            : {
                  bestResultsSummary: Object.values(DisciplineCategoryId)
                      .reduce((xAccumulator, x) => {
                          if (
                              !cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x].isValid
                          ) {
                              return xAccumulator;
                          }
                          const bestResult = callEventFormatFunction(
                              () =>
                                  cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                      x
                                  ].bestResults.find((y) => !y.isFake),
                              () =>
                                  cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x]
                                      .bestResult,
                              eventFormat,
                          );
                          if (bestResult === undefined || bestResult.isFake) {
                              return xAccumulator;
                          }
                          return [
                              ...xAccumulator,
                              `${
                                  getDiscipline(eventFormat, bestResult.discipline.id).name
                              }: ${formatResultValue(
                                  eventFormat,
                                  bestResult.discipline.id,
                                  false,
                                  bestResult.value,
                              )}`,
                          ];
                      }, [])
                      .join("\n"),
                  ...(eventFormat === EventFormat.CONTEST && {
                      rankSum: cert.rankSum.toString(),
                      groupParticipantCount: cert.groupParticipantCount.toString(),
                      pointsAndOfGroupParticipantCount: `${cert.points} von ${cert.groupParticipantCount}`,
                      flippedRankPoints: (cert.groupParticipantCount - cert.points + 1).toString(),
                      flippedRankPointsAndOfGroupParticipantCount: `${cert.groupParticipantCount - cert.points + 1} von ${cert.groupParticipantCount}`,
                      fakePoints: calculateFakePoints(
                          cert.rankSum,
                          cert.groupParticipantCount,
                          Object.values(DisciplineCategoryId).length,
                      ).toString(),
                  }),
                  points: cert.points.toString(),
                  type: formatCertType(cert.type),
                  shortType: formatCertType(cert.type, { isShort: true }),
              }),
    }),
    toMockupCsvRow: (eventFormat, { isParticipantIncluded = true, prefix = undefined } = {}) =>
        prefixObjectKeys(
            {
                ...(isParticipantIncluded &&
                    participantConvert.toMockupCsvRow(eventFormat, { prefix: "Teilnehmer" })),
                ...Object.values(DisciplineCategoryId).reduce((xAccumulator, x) => {
                    const disciplineCategoryName = getDisciplineCategory(eventFormat, x).name;
                    return {
                        ...xAccumulator,
                        [`${disciplineCategoryName} Ist Gültig`]: undefined,
                        [`${disciplineCategoryName} Fehler`]: undefined,
                        ...callEventFormatFunction(
                            () => ({
                                [`${disciplineCategoryName} Wert`]: undefined,
                                [`${disciplineCategoryName} Rang`]: undefined,
                            }),
                            () => ({
                                ...resultConvert.toBestResultMockupCsvRow({
                                    prefix: `${disciplineCategoryName} Ergebnis`,
                                }),
                                [`${disciplineCategoryName} Punkte`]: undefined,
                            }),
                            eventFormat,
                        ),
                        ...(eventFormat === EventFormat.CONTEST &&
                            Array.from(Array(3).keys()).reduce(
                                (yAccumualator, y, yIndex) => ({
                                    ...yAccumualator,
                                    ...resultConvert.toBestResultMockupCsvRow({
                                        prefix: `${disciplineCategoryName} Ergebnis ${yIndex + 1}`,
                                    }),
                                }),
                                {},
                            )),
                    };
                }, {}),
                "Ist Generiert": undefined,
                Fehler: undefined,
                ...(eventFormat === EventFormat.CONTEST
                    ? {
                          "Rang Summe": undefined,
                          "Riege Teilnehmeranzahl": undefined,
                          "Punkte (Rang)": undefined,
                          "Punkte (Rang von Anzahl)": undefined,
                          "Punkte (Umgedreht)": undefined,
                          "Punkte (Umgedreht von Anzahl)": undefined,
                          "Punkte (Wettbewerb)": undefined,
                      }
                    : { Punkte: undefined }),
                Typ: undefined,
            },
            prefix,
        ),
    toCsvRow: (eventFormat, cert, { isParticipantIncluded = true, prefix = undefined } = {}) =>
        prefixObjectKeys(
            {
                ...(isParticipantIncluded &&
                    participantConvert.toCsvRow(eventFormat, cert.participant, {
                        prefix: "Teilnehmer",
                    })),
                ...Object.values(DisciplineCategoryId).reduce((xAccumulator, x) => {
                    const disciplineCategoryName = getDisciplineCategory(eventFormat, x).name;
                    return {
                        ...xAccumulator,
                        [`${disciplineCategoryName} Ist Gültig`]: formatBool(
                            cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x].isValid,
                        ),
                        ...(!cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x].isValid
                            ? {
                                  [`${disciplineCategoryName} Fehler`]:
                                      formatCertGenerationErrorType(
                                          cert
                                              .disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                              x
                                          ].error,
                                      ),
                              }
                            : callEventFormatFunction(
                                  () => ({
                                      [`${disciplineCategoryName} Wert`]:
                                          cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                              x
                                          ].value.toString(),
                                      [`${disciplineCategoryName} Rang`]:
                                          cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                              x
                                          ].rank.toString(),
                                  }),
                                  () => ({
                                      ...resultConvert.toBestResultCsvRow(
                                          eventFormat,
                                          cert
                                              .disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                              x
                                          ].bestResult,
                                          { prefix: `${disciplineCategoryName} Ergebnis` },
                                      ),
                                      [`${disciplineCategoryName} Punkte`]:
                                          cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                              x
                                          ].points.toString(),
                                  }),
                                  eventFormat,
                              )),
                        ...(eventFormat === EventFormat.CONTEST &&
                            cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                                x
                            ].bestResults.reduce(
                                (yAccumulator, y, yIndex) => ({
                                    ...yAccumulator,
                                    ...resultConvert.toBestResultCsvRow(eventFormat, y, {
                                        prefix: `${disciplineCategoryName} Ergebnis ${yIndex + 1}`,
                                    }),
                                }),
                                {},
                            )),
                    };
                }, {}),
                "Ist Generiert": formatBool(cert.isGenerated),
                ...(cert.error !== undefined && {
                    Fehler: formatCertGenerationErrorType(cert.error),
                }),
                ...(cert.isGenerated && {
                    ...(eventFormat === EventFormat.CONTEST
                        ? {
                              "Rang Summe": cert.rankSum.toString(),
                              "Riege Teilnehmeranzahl": cert.groupParticipantCount.toString(),
                              "Punkte (Rang)": cert.points.toString(),
                              "Punkte (Rang von Anzahl)": `${cert.points} von ${cert.groupParticipantCount}`,
                              "Punkte (Umgedreht)": (
                                  cert.groupParticipantCount -
                                  cert.points +
                                  1
                              ).toString(),
                              "Punkte (Umgedreht von Anzahl)": `${cert.groupParticipantCount - cert.points + 1} von ${cert.groupParticipantCount}`,
                              "Punkte (Wettbewerb)": calculateFakePoints(
                                  cert.rankSum,
                                  cert.groupParticipantCount,
                                  Object.values(DisciplineCategoryId).length,
                              ).toString(),
                          }
                        : { Punkte: cert.points.toString() }),
                    Typ: formatCertType(cert.type),
                }),
            },
            prefix,
        ),
};

const certTemplateConvert = {
    toDto: (certTemplate) => ({
        dimensions: {
            width: certTemplate.dimensions.width,
            height: certTemplate.dimensions.height,
        },
        alignmentLineDistance: {
            x: certTemplate.alignmentLineDistance.x,
            y: certTemplate.alignmentLineDistance.y,
        },
        elements: certTemplate.elements.map((x) => {
            const commonElementDto = {
                id: x.id,
                name: x.name,
                type: x.type,
                purpose: x.purpose,
                isVisible: x.isVisible,
                position: {
                    x: x.position.x,
                    y: x.position.y,
                },
                positionOffset: {
                    max: x.positionOffset.max,
                    step: x.positionOffset.step,
                    x: x.positionOffset.x,
                    y: x.positionOffset.y,
                },
            };
            switch (x.type) {
                case CertTemplateElementType.TEXT:
                    return {
                        ...commonElementDto,
                        fieldPath: x.fieldPath,
                        rotation: x.rotation,
                        alignment: {
                            horizontal: x.alignment.horizontal,
                            vertical: x.alignment.vertical,
                        },
                        family: x.family,
                        ...(x.size !== undefined && { size: x.size }),
                        ...(x.dynamicSize !== undefined && {
                            dynamicSize: {
                                maxWidth: x.dynamicSize.maxWidth,
                                max: x.dynamicSize.max,
                                min: x.dynamicSize.min,
                            },
                        }),
                        lineHeight: x.lineHeight,
                        color: x.color,
                    };
                case CertTemplateElementType.LINE:
                    return {
                        ...commonElementDto,
                        endPosition: {
                            x: x.endPosition.x,
                            y: x.endPosition.y,
                        },
                        width: x.width,
                        color: x.color,
                    };
                case CertTemplateElementType.RUBBER_STAMP:
                    return commonElementDto;
                case CertTemplateElementType.SIGNATURE:
                    return commonElementDto;
                default:
                    throw new Error(`Invalid cert template element type: ${x.type}`);
            }
        }),
        scale: {
            max: certTemplate.scale.max,
            step: certTemplate.scale.step,
            value: certTemplate.scale.value,
        },
        positionOffset: {
            max: certTemplate.positionOffset.max,
            step: certTemplate.positionOffset.step,
            x: certTemplate.positionOffset.x,
            y: certTemplate.positionOffset.y,
        },
    }),
};

const certTemplateSetConvert = {
    toDto: (certTemplateSet) => ({
        id: certTemplateSet.id,
        event: certTemplateSet.event.toString(),
        certTypeToCertTemplateInfoMap: Object.fromEntries(
            Object.values(CertType).map((x) => [
                x,
                {
                    presetId: certTemplateSet.certTypeToCertTemplateInfoMap[x].presetId,
                    certTemplate: certTemplateConvert.toDto(
                        certTemplateSet.certTypeToCertTemplateInfoMap[x].certTemplate,
                    ),
                },
            ]),
        ),
        rubberStampPngDataUrl: certTemplateSet.rubberStampPngDataUrl,
        signaturePngDataUrl: certTemplateSet.signaturePngDataUrl,
    }),
};

/**
 * @typedef ImportSuccess
 * @property tempId
 * @property item
 *
 * @typedef ImportFail
 * @property tempId
 * @property error
 *
 * @typedef ImportResult
 * @property {ImportFail[]} fails
 * @property {ImportSuccess[]} successes
 *
 * @param {ImportResult} importResult
 */
const convertImportResultToDto = (importResult, convertItemToDto) => ({
    fails: importResult.fails.map((x) => ({
        tempId: x.tempId,
        error: {
            type: x.error.type,
            ...(x.error.details === undefined ? {} : { details: x.error.details }),
        },
    })),
    successes: importResult.successes.map((x) => ({
        tempId: x.tempId,
        item: convertItemToDto(x.item),
    })),
});

/**
 * @typedef ListResult
 * @property searchValue
 * @property pageSize
 * @property pageIndex
 * @property count
 * @property items
 *
 * @param {ListResult} listResult
 */
const convertListResultToDto = (listResult, convertItemToDto) => {
    if (listResult.searchValue !== undefined) {
        return {
            searchValue: listResult.searchValue,
            pageSize: listResult.pageSize,
            items: listResult.items.map(convertItemToDto),
        };
    }
    return {
        pageSize: listResult.pageSize,
        pageIndex: listResult.pageIndex,
        count: listResult.count,
        items: listResult.items.map(convertItemToDto),
    };
};

const disciplinePreselectionConvert = {
    toDto: (eventFormat, disciplinePreselection) => ({
        id: disciplinePreselection.id,
        eventId: getMongooseReferenceId(disciplinePreselection.event),
        value: callEventFormatFunction(
            () =>
                Object.values(ClassLevelRange).reduce(
                    (xAccumulator, x) => ({
                        ...xAccumulator,
                        [x]: eventFormatToDisciplineCategoriesMap[eventFormat].reduce(
                            (yAccumulator, y) => ({
                                ...yAccumulator,
                                [y.id]: disciplinePreselection.value[x][y.id],
                            }),
                            {},
                        ),
                    }),
                    {},
                ),
            () =>
                Object.values(Gender).reduce(
                    (xAccumulator, x) => ({
                        ...xAccumulator,
                        [x]: eventFormatToAgeGroupsMap[eventFormat].reduce(
                            (yAccumulator, y) => ({
                                ...yAccumulator,
                                [y]: eventFormatToDisciplineCategoriesMap[eventFormat].reduce(
                                    (zAccumulator, z) => ({
                                        ...zAccumulator,
                                        [z.id]: disciplinePreselection.value[x][y][z.id],
                                    }),
                                    {},
                                ),
                            }),
                            {},
                        ),
                    }),
                    {},
                ),
            eventFormat,
        ),
    }),
};

const generateRoute =
    (segments = [""], subRoutes = {}) =>
    // The second function gets called by the system.
    // buildPrevious gives the sub routes recursive access to their parent routes.
    (buildPrevious = () => "") =>
    // The third function gets called by the developer to generate the routes build functions and sub routes.
    // isRouteRootsParent defines, if the system should further add segments to the final route. When a route has isRootsParent equals true, it and it's parent routes will not get included in the final route.
    // args defines values for the parameter placeholders of the route. These are only used in the build function.
    (isRootsParent = false, args = {}) => {
        // The build function generates the final route recursively and replaces the parameter placeholders with their values. When the final route does not get generated to the highest level route, a slash gets added in front of it.
        // queryParams can define the base for a query string to be generated and appended to the final route.
        const build = (queryParams = undefined) => {
            const segmentsToJoin = [];
            if (isRootsParent === false) {
                segmentsToJoin.push(buildPrevious());
                segmentsToJoin.push(
                    ...Object.keys(args).reduce(
                        (segmentsWithArgsAccumulator, arg) =>
                            segmentsWithArgsAccumulator.map((segment) =>
                                segment !== `:${arg}` ? segment : String(args[arg]),
                            ),
                        segments,
                    ),
                );
            }
            if (queryParams !== undefined) {
                segmentsToJoin.push(`?${queryString.stringify(queryParams)}`);
            }
            return (isRootsParent === true ? "/" : "") + urlJoin(segmentsToJoin);
        };
        // The result of the third function is an object with a build function and all sub routes represented by their third functions. So the subroutes can get called with isRootsParent and args to get their object with their build function and their sub routes.
        return {
            build,
            ...Object.fromEntries(
                Object.keys(subRoutes).map((x) => [
                    x,
                    subRoutes[x](build), // The system calls the second function, to give the sub routes recursive access to their parent routes.
                ]),
            ),
        };
    };

const generateApiRoutes = (apiUrl) =>
    generateRoute([apiUrl], {
        migrations: generateRoute(["migrations"], {
            migrate: generateRoute([":name"]), // POST
        }),
        eSignatures: generateRoute(["e-signatures"]),
        stripe: generateRoute(["stripe"]),
        cron: generateRoute(["cron"], {
            removeAllOldAuthorizationRefreshIntents: generateRoute([
                "remove-all-old-authorization-refresh-intents",
            ]),
            removeAllNotVerifyingSchools: generateRoute(["remove-all-not-verifying-schools"]),
            removeAllOldSpecialDiscounts: generateRoute(["remove-all-old-special-discounts"]),
        }),
        specialDiscounts: generateRoute(["special-discounts"], {
            createMultiple: generateRoute(["multiple"]), // POST
        }),
        schools: generateRoute(["schools"], {
            signUp: generateRoute(["sign-up"]), // POST
            login: generateRoute(["login"]), // POST
            refreshAuthorization: generateRoute(["refresh-authorization"]), // POST
            logout: generateRoute(["logout"]), // POST
            get: generateRoute([":id"]), // GET
            getAllPayments: generateRoute([":id", "all-payments"]), // GET
            update: generateRoute([":id"]), // PUT
            requestDpa: generateRoute([":id", "request-dpa"]), // POST
            updateDataPrivacy: generateRoute([":id", "data-privacy"]), // PUT
            updateFlow: generateRoute([":id", "flow"]), // PUT
            requestMailAddressChange: generateRoute([":id", "request-mail-address-change"]), // POST
            validateMailAddressVerificationToken: generateRoute([
                "validate-mail-address-change-token",
                ":token",
            ]), // GET
            cancelMailAddressChange: generateRoute([":id", "cancel-mail-address-change"]), // POST
            verifyMailAddress: generateRoute(["verify-mail-address", ":token"]), // POST
            printDpa: generateRoute([":id", "print-data-privacy-dpa"]), // POST
            forgotPassword: generateRoute(["forgot-password"]), // POST
            requestPasswordChange: generateRoute([":id", "request-password-change"]), // POST
            validatePasswordChangeToken: generateRoute([
                "validate-password-change-token",
                ":token",
            ]), // GET
            cancelPasswordChange: generateRoute([":id", "cancel-password-change"]), // POST
            changePassword: generateRoute(["change-password", ":token"]), // POST
            requestRemoval: generateRoute([":id", "request-removal"]), // POST
            validateRemovalToken: generateRoute(["validate-removal-token", ":token"]), // GET
            cancelRemoval: generateRoute([":id", "cancel-removal"]), // POST
            remove: generateRoute([":token"]), // DELETE
        }),
        events: generateRoute(["events"], {
            getPricing: generateRoute(["pricing"]), // GET
            // getStripePromotionCodeId: generateRoute(["stripe-promotion-code-id", ":discountCode"]), // GET
            create: generateRoute(["create"]), // POST
            generateReport: generateRoute([":id", "generate-report"]), // POST
            get: generateRoute([":id"]), // GET
            getCheckoutCompletedResult: generateRoute([
                "checkout-completed-result",
                ":stripeSessionId",
            ]), // GET
            list: generateRoute(), // GET
            updateName: generateRoute([":id", "name"]), // PUT
            requestRemoval: generateRoute([":id", "request-removal"]), // POST
            validateRemovalToken: generateRoute(["validate-removal-token", ":token"]), // GET
            cancelRemoval: generateRoute([":id", "cancel-removal"]), // POST
            remove: generateRoute([":token"]), // DELETE
            removeByAdmin: generateRoute([":n3WordId", "by-admin"]), // DELETE
        }),
        disciplinePreselection: generateRoute(["discipline-preselection"], {
            get: generateRoute(), // GET
            update: generateRoute(), // PUT
        }),
        helpers: generateRoute(["helpers"], {
            create: generateRoute(), // POST
            import: generateRoute(["import"]), // POST
            login: generateRoute(["login"]), // POST
            relogin: generateRoute(["relogin"]), // POST
            refreshAuthorization: generateRoute(["refresh-authorization"]), // POST
            logout: generateRoute(["logout"]), // POST
            get: generateRoute([":id"]), // GET
            list: generateRoute(), // GET
            printLoginDataOfAll: generateRoute(["print-login-data-of-all"]), // POST
            update: generateRoute([":id"]), // PUT
            updateIsActiveOfAll: generateRoute(["update-is-active-of-all"]), // PUT
            remove: generateRoute([":id"]), // DELETE
            removeMany: generateRoute(["remove-many"]), // DELETE
        }),
        groups: generateRoute(["groups"], {
            create: generateRoute(), // POST
            // list: generateRoute(), // GET
            getAll: generateRoute(), // GET
            updateAll: generateRoute(), // PUT
            remove: generateRoute([":id"]), // DELETE
        }),
        participants: generateRoute(["participants"], {
            create: generateRoute(), // POST
            import: generateRoute(["import"]), // POST
            list: generateRoute(), // GET
            printStarterlistOfAll: generateRoute(["print-starterlist-of-all"]), // POST
            update: generateRoute([":id"]), // PUT
            remove: generateRoute([":id"]), // DELETE
            removeMany: generateRoute(["remove-many"]), // DELETE
        }),
        results: generateRoute(["results"], {
            create: generateRoute(), // POST
            import: generateRoute(["import"]), // POST
            list: generateRoute(), // GET
            getAllOfParticipantAndDiscipline: generateRoute([
                "get-all-of-participant-and-discipline",
            ]), // GET
            exportAll: generateRoute(["export-all"]), // POST
            update: generateRoute([":id"]), // PUT
            remove: generateRoute([":id"]), // DELETE
            removeMany: generateRoute(["remove-many"]), // DELETE
        }),
        certTemplateSets: generateRoute(["cert-template-sets"], {
            get: generateRoute(), // GET
            printTest: generateRoute(["print-test"]), // POST
            update: generateRoute(), // PUT
            updateRubberStamp: generateRoute(["rubber-stamp"]), // PUT
            updateSignature: generateRoute(["signature"]), // PUT
        }),
        certs: generateRoute(["certs"], {
            generateAll: generateRoute(["generate-all"]), // POST
            print: generateRoute([":id", "print"]), // POST
            exportAll: generateRoute(["export-all"]), // POST
            printAll: generateRoute(["print-all"]), // POST
            printOverviewOfAll: generateRoute(["print-overview-of-all"]), // POST
            list: generateRoute(), // GET
        }),
    })();

const apiRoutes = generateApiRoutes(config.urls.api);

const helperAppRoutes = generateRoute(["helper-app"], {
    onboarding: generateRoute(["onboarding"], {
        welcome: generateRoute(["welcome"]),
        // school: generateRoute(["school"]),
        instructions: generateRoute(["instructions"]),
    }),
    login: generateRoute(["login"]),
    relogin: generateRoute(["relogin"]),
    logout: generateRoute(["logout"]),
    dashboard: generateRoute(["dashboard"], {
        localResultCreation: generateRoute(["local-result-creation"], {
            main: generateRoute(["main"]),
        }),
        bigLocalResultCreation: generateRoute(["big-local-result-creation"], {
            main: generateRoute(["main"]),
        }),
        localResults: generateRoute(["local-results"], {
            master: generateRoute(["master"]),
            update: generateRoute(["update"]),
            upload: generateRoute(["upload"], {
                main: generateRoute(["main"]),
                problems: generateRoute(["problems"]),
            }),
        }),
        onlineResults: generateRoute(["online-results"], {
            master: generateRoute(["master"]),
            update: generateRoute(["update"]),
        }),
        information: generateRoute(["information"], { main: generateRoute(["main"]) }),
    }),
})();

const schoolAppRoutes = generateRoute([config.urls.schoolApp], {
    pageNotFound: generateRoute(["page-not-found"]),
    logout: generateRoute(["logout"]),
    account: generateRoute(["account"], {
        signUp: generateRoute(["sign-up"]),
        login: generateRoute(["login"]),
        mailAddressVerification: generateRoute(["mail-address-verification", ":token"]),
        passwordForgotten: generateRoute(["password-forgotten"]),
        passwordChange: generateRoute(["password-change", ":token"]),
        eventRemoval: generateRoute(["event-removal", ":token"]),
        removal: generateRoute(["removal", ":token"]),
    }),
    dashboard: generateRoute(["dashboard"], {
        event: generateRoute(["event"]),
        disciplinePreSelection: generateRoute(["discipline-preselection"]),
        helpers: generateRoute(["helpers"]),
        participants: generateRoute(["participants"]),
        groups: generateRoute(["groups"]),
        results: generateRoute(["results"]),
        certTemplateSet: generateRoute(["cert-template-set"]),
        certs: generateRoute(["certs"], {
            generated: generateRoute(["generated"]),
            notGenerated: generateRoute(["not-generated"]),
        }),
        statistics: generateRoute(["statistics"]),
        events: generateRoute(["events"]),
        account: generateRoute(["account"]),
        payments: generateRoute(["payments"]),
    }),
})();

const eventConvert = {
    toNameAnd3WordIdDto: (event) => ({
        n3WordId: event.n3WordId,
        name: event.name,
    }),
    toDto: (event) => ({
        id: event.id,
        schoolId: getMongooseReferenceId(event.school),
        format: event.format,
        n3WordId: event.n3WordId,
        name: event.name,
        start: event.start,
        warmUpStart: event.warmUpStart,
        end: event.end,
        limits: {
            helper: event.limits.helper,
            participant: event.limits.participant,
            group: event.limits.group,
            result: event.limits.result,
        },
        isTest: event.isTest,
    }),
    toOwnDto: (event) => ({
        id: event.id,
        schoolId: getMongooseReferenceId(event.school),
        format: event.format,
        n3WordId: event.n3WordId,
        name: event.name,
        start: event.start,
        warmUpStart: event.warmUpStart,
        end: event.end,
        limits: {
            helper: event.limits.helper,
            participant: event.limits.participant,
            group: event.limits.group,
            result: event.limits.result,
        },
        ...(event.lastAllCertsGeneration !== undefined && {
            lastAllCertsGeneration: event.lastAllCertsGeneration,
        }),
        ...(event.removalIntent !== undefined && {
            removalIntent: { expires: event.removalIntent.expires },
        }),
        isTest: event.isTest,
    }),
    toFormattedDto: (event) => ({
        id: event.id,
        schoolId: getMongooseReferenceId(event.school),
        format: formatEventFormat(event.format),
        n3WordId: event.n3WordId,
        name: event.name,
        start: formatDate(event.start, "P"),
        last2StartYearDigits: dateFns
            .getYear(dateFnsTz.utcToZonedTime(event.start))
            .toString()
            .slice(-2),
        warmUpStart: formatDate(event.warmUpStart, "P"),
        end: formatDate(event.end, "P"),
        limits: {
            helper: event.limits.helper,
            participant: event.limits.participant,
            group: event.limits.group,
            result: event.limits.result,
        },
        ...(event.lastAllCertsGeneration !== undefined && {
            lastAllCertsGeneration: formatDate(event.lastAllCertsGeneration, "Pp"),
        }),
        ...(event.removalIntent !== undefined && {
            removalIntent: {
                expires: event.removalIntent.expires,
                expiresString: formatDate(event.removalIntent.expires, "Pp"),
                token: event.removalIntent.token,
                url: schoolAppRoutes()
                    .account()
                    .eventRemoval(false, { token: event.removalIntent.token })
                    .build(),
            },
        }),
        isTest: formatBool(event.isTest),
    }),
    toCheckoutCompletedResult: (event, stripeInvoiceUrl) => ({
        event: eventConvert.toOwnDto(event),
        stripeInvoiceUrl,
    }),
};

const schoolConvert = {
    toMailAddressDto: (school) => ({ mailAddress: school.mailAddress }),
    toNameAndVerifiableMailAddressDto: (school) => ({
        name: school.name,
        verifiableMailAddress: school.verifiableMailAddress,
    }),
    toNameAndMailAddressDto: (school) => ({
        name: school.name,
        mailAddress: school.mailAddress,
    }),
    toDto: (school) => ({
        id: school.id,
        name: school.name,
        mailAddress: school.mailAddress,
    }),
    toSelfDto: (school) => ({
        id: school.id,
        name: school.name,
        mailAddress: school.mailAddress,
        ...(school.type !== undefined && { type: school.type }),
        ...(school.address !== undefined && {
            address: {
                street: school.address.street || "",
                houseNumber: school.address.houseNumber || "",
                postalCode: school.address.postalCode || "",
                city: school.address.city || "",
                state: school.address.state || "",
            },
        }),
        dataPrivacy: {
            dpa: {
                state: school.dataPrivacy.dpa.state,
                ...(school.dataPrivacy.dpa.eSignaturesContract !== undefined && {
                    eSignaturesContract: {
                        id: school.dataPrivacy.dpa.eSignaturesContract.id,
                        signaturePageUrl:
                            school.dataPrivacy.dpa.eSignaturesContract.signaturePageUrl,
                    },
                }),
                ...(school.dataPrivacy.dpa.pdf !== undefined && {
                    pdf: { filePath: school.dataPrivacy.dpa.pdf.filePath },
                }),
                ...(school.dataPrivacy.dpa.sent !== undefined && {
                    sent: school.dataPrivacy.dpa.sent,
                }),
                ...(school.dataPrivacy.dpa.signed !== undefined && {
                    signed: school.dataPrivacy.dpa.signed,
                }),
            },
            isConsentFormCollected: school.dataPrivacy.isConsentFormCollected,
            isDataPrivacyPolicyUpdated: school.dataPrivacy.isDataPrivacyPolicyUpdated,
            isGuideRead: school.dataPrivacy.isGuideRead,
        },
        flow: { isOnboarded: school.flow.isOnboarded },
        verifiableMailAddress: school.verifiableMailAddress,
        ...(school.mailAddressVerificationIntent !== undefined && {
            mailAddressVerificationIntent: {
                expires: school.mailAddressVerificationIntent.expires,
            },
        }),
        firstMailAddressVerification: school.firstMailAddressVerification,
        lastMailAddressVerification: school.lastMailAddressVerification,
        ...(school.passwordChangeIntent !== undefined && {
            passwordChangeIntent: { expires: school.passwordChangeIntent.expires },
        }),
        lastPasswordChange: school.lastPasswordChange,
        ...(school.removalIntent !== undefined && {
            removalIntent: { expires: school.removalIntent.expires },
        }),
        created: school.created,
        isVerified: school.isVerified,
    }),
    toFormattedDto: (school) => ({
        id: school.id,
        name: school.name,
        mailAddress: school.mailAddress,
        ...(school.type !== undefined && { type: formatSchoolType(school.type) }),
        ...(school.address !== undefined && {
            address: {
                street: school.address.street || "",
                houseNumber: school.address.houseNumber || "",
                postalCode: school.address.postalCode || "",
                city: school.address.city || "",
                state: school.address.state || "",
            },
        }),
        dataPrivacy: {
            dpa: {
                state: school.dataPrivacy.dpa.state,
                ...(school.dataPrivacy.dpa.eSignaturesContract !== undefined && {
                    eSignaturesContract: {
                        id: school.dataPrivacy.dpa.eSignaturesContract.id,
                        signaturePageUrl:
                            school.dataPrivacy.dpa.eSignaturesContract.signaturePageUrl,
                    },
                }),
                ...(school.dataPrivacy.dpa.pdf !== undefined && {
                    pdf: { filePath: school.dataPrivacy.dpa.pdf.filePath },
                }),
                ...(school.dataPrivacy.dpa.sent !== undefined && {
                    sent: formatDate(school.dataPrivacy.dpa.sent, "Pp"),
                }),
                ...(school.dataPrivacy.dpa.signed !== undefined && {
                    signed: formatDate(school.dataPrivacy.dpa.signed, "Pp"),
                }),
            },
            isConsentFormCollected: formatBool(school.dataPrivacy.isConsentFormCollected),
            isDataPrivacyPolicyUpdated: formatBool(school.dataPrivacy.isDataPrivacyPolicyUpdated),
            isGuideRead: formatBool(school.dataPrivacy.isGuideRead),
        },
        flow: { isOnboarded: school.flow.isOnboarded },
        verifiableMailAddress: school.verifiableMailAddress,
        ...(school.mailAddressVerificationIntent !== undefined && {
            mailAddressVerificationIntent: {
                expires: school.mailAddressVerificationIntent.expires,
                expiresString: formatDate(school.mailAddressVerificationIntent.expires, "Pp"),
                token: school.mailAddressVerificationIntent.token,
                url: schoolAppRoutes()
                    .account()
                    .mailAddressVerification(false, {
                        token: school.mailAddressVerificationIntent.token,
                    })
                    .build(),
            },
        }),
        firstMailAddressVerification: school.firstMailAddressVerification,
        lastMailAddressVerification: school.lastMailAddressVerification,
        ...(school.passwordChangeIntent !== undefined && {
            passwordChangeIntent: {
                expires: school.passwordChangeIntent.expires,
                expiresString: formatDate(school.passwordChangeIntent.expires, "Pp"),
                token: school.passwordChangeIntent.token,
                url: schoolAppRoutes()
                    .account()
                    .passwordChange(false, { token: school.passwordChangeIntent.token })
                    .build(),
            },
        }),
        lastPasswordChange: school.lastPasswordChange,
        ...(school.removalIntent !== undefined && {
            removalIntent: {
                expires: school.removalIntent.expires,
                expiresString: formatDate(school.removalIntent.expires, "Pp"),
                token: school.removalIntent.token,
                url: schoolAppRoutes()
                    .account()
                    .removal(false, { token: school.removalIntent.token })
                    .build(),
            },
        }),
        created: school.created,
        isVerified: school.isVerified,
    }),
};

const toDto = (stripeInvoice) => ({
    id: stripeInvoice.id,
    amount: stripeInvoice.amount_due,
    receivedAmount: stripeInvoice.amount_paid,
    currency: stripeInvoice.currency,
    status: stripeInvoice.status,
    created: dateFns.fromUnixTime(stripeInvoice.created),
    url: stripeInvoice.hosted_invoice_url,
    ...(stripeInvoice.payment_intent !== null && {
        paymentIntent: {
            id: stripeInvoice.payment_intent.id,
            status: stripeInvoice.payment_intent.status,
        },
    }),
});

const stripeInvoiceConvert = { toDto };

const specialDiscountConvert = {
    toDto: (specialDiscount) => ({
        id: specialDiscount.id,
        code: specialDiscount.code,
        expires: specialDiscount.expires,
        ...(specialDiscount.redeemed !== undefined && {
            redeemed: specialDiscount.redeemed,
            redeemedBySchool: specialDiscount.redeemedBySchool,
        }),
    }),
};

const alignmentLineLength = 292 / 5;
const scaleLineLength = 292;
const getTextSize = (text, size) => calulateTextWidth(text, {
  font: "Helvetica",
  size
});
const calculateTextFormatting = (text, min, max, maxWidth) => {
  const maxSizeWidth = getTextSize(text, max);
  if (maxSizeWidth <= maxWidth) {
    return {
      size: max,
      isScaled: false
    };
  }
  const minSizeWidth = getTextSize(text, min);
  if (minSizeWidth <= maxWidth) {
    return {
      size: min,
      width: minSizeWidth,
      isScaled: true
    };
  }
  let newLength = text.length;
  let minSizeShortedTextWidth;
  do {
    newLength -= 1;
    minSizeShortedTextWidth = getTextSize(transformToShortedString(text, newLength), min);
  } while (minSizeShortedTextWidth > maxWidth);
  return {
    maxLength: newLength,
    size: min,
    width: minSizeShortedTextWidth,
    isScaled: true
  };
};
const CertSvg = ({ cert, certTemplateSet, certType, event, isShowAlignmentGuide = false }) => {
  const { certTemplate } = certTemplateSet.certTypeToCertTemplateInfoMap[certType];
  const aspectRatio = certTemplate.dimensions.width / certTemplate.dimensions.height;
  const deltaX = certTemplate.scale.value;
  const deltaY = certTemplate.scale.value / aspectRatio;
  const newWidth = certTemplate.dimensions.width - 2 * deltaX;
  const newHeight = certTemplate.dimensions.height - 2 * deltaY;
  const formattedCert = certConvert.toFormattedDto(event.format, cert);
  const formattedEvent = eventConvert.toFormattedDto(event);
  return /* @__PURE__ */ React.createElement(
    "svg",
    {
      display: "block",
      overflow: "hidden",
      viewBox: `${deltaX} ${deltaY} ${newWidth} ${newHeight}`
    },
    isShowAlignmentGuide && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.alignmentLineDistance.x + alignmentLineLength + certTemplate.positionOffset.x,
        y1: certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        y1: certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.alignmentLineDistance.y + alignmentLineLength + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x - alignmentLineLength + certTemplate.positionOffset.x,
        y1: certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        y1: certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.alignmentLineDistance.y + alignmentLineLength + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x - alignmentLineLength + certTemplate.positionOffset.x,
        y1: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        y1: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y - alignmentLineLength + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.alignmentLineDistance.x + alignmentLineLength + certTemplate.positionOffset.x,
        y1: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        y1: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y + certTemplate.positionOffset.y,
        y2: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y - alignmentLineLength + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "text",
      {
        fill: colors.amaranthShades[500],
        fontFamily: "Helvetica",
        fontSize: 12,
        textAnchor: "end",
        x: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x - 10 + certTemplate.positionOffset.x,
        y: certTemplate.dimensions.height - certTemplate.alignmentLineDistance.y - 10 - 17 + certTemplate.positionOffset.y
      },
      /* @__PURE__ */ React.createElement(
        "tspan",
        {
          x: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x - 10 + certTemplate.positionOffset.x
        },
        "Diese Linien sollten 2 cm von"
      ),
      /* @__PURE__ */ React.createElement(
        "tspan",
        {
          dy: "1.4em",
          x: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x - 10 + certTemplate.positionOffset.x
        },
        "den Seitenrändern entfernt sein"
      )
    ), /* @__PURE__ */ React.createElement(
      "line",
      {
        stroke: colors.amaranthShades[500],
        strokeWidth: 1,
        x1: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        x2: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x + certTemplate.positionOffset.x,
        y1: certTemplate.dimensions.height / 2 - scaleLineLength / 2 + certTemplate.positionOffset.y,
        y2: certTemplate.dimensions.height / 2 + scaleLineLength / 2 + certTemplate.positionOffset.y
      }
    ), /* @__PURE__ */ React.createElement(
      "svg",
      {
        overflow: "visible",
        x: certTemplate.dimensions.width - certTemplate.alignmentLineDistance.x - 10 + certTemplate.positionOffset.x,
        y: certTemplate.dimensions.height / 2 + certTemplate.positionOffset.y
      },
      /* @__PURE__ */ React.createElement(
        "text",
        {
          fill: colors.amaranthShades[500],
          fontFamily: "Helvetica",
          fontSize: 12,
          textAnchor: "middle",
          transform: "rotate(-90)"
        },
        "Diese Linie sollte 10 cm lang sein"
      )
    )),
    certTemplate.elements.map((x) => {
      switch (x.type) {
        case CertTemplateElementType.LINE:
          return /* @__PURE__ */ React.createElement(
            "line",
            {
              key: x.id,
              stroke: x.color,
              strokeWidth: x.width,
              visibility: !x.isVisible ? "hidden" : "visible",
              x1: x.position.x + certTemplate.positionOffset.x + x.positionOffset.x,
              x2: x.endPosition.x + certTemplate.positionOffset.x + x.positionOffset.x,
              y1: x.position.y + certTemplate.positionOffset.y + x.positionOffset.y,
              y2: x.endPosition.y + certTemplate.positionOffset.y + x.positionOffset.y
            }
          );
        case CertTemplateElementType.TEXT: {
          const text = get(
            {
              event: formattedEvent,
              cert: formattedCert
            },
            x.fieldPath
          );
          if (text === void 0) {
            return void 0;
          }
          const textLines = text.toString().split("\n");
          const longestTextLine = textLines.reduce(
            (a, b) => a.length > b.length ? a : b
          );
          let textFormatting;
          if (x.dynamicSize !== void 0) {
            textFormatting = calculateTextFormatting(
              longestTextLine,
              x.dynamicSize.min,
              x.dynamicSize.max,
              x.dynamicSize.maxWidth
            );
          }
          const size = textFormatting !== void 0 ? textFormatting.size : x.size;
          const shortedTextLines = textLines.map((y) => {
            if (textFormatting !== void 0 && textFormatting.maxLength !== void 0) {
              return transformToShortedString(y, textFormatting.maxLength);
            }
            return y;
          });
          return /* @__PURE__ */ React.createElement(
            "svg",
            {
              key: x.id,
              overflow: "visible",
              x: x.position.x + certTemplate.positionOffset.x + x.positionOffset.x,
              y: x.position.y + certTemplate.positionOffset.y + x.positionOffset.y,
              ...textFormatting !== void 0 && textFormatting.isScaled && {
                width: x.dynamicSize.maxWidth,
                height: "100%"
              }
            },
            /* @__PURE__ */ React.createElement(
              "svg",
              {
                overflow: "visible",
                x: 0,
                y: 0,
                ...textFormatting !== void 0 && textFormatting.isScaled && {
                  viewBox: `0 0 ${textFormatting.width} 1`,
                  width: "100%",
                  height: "100%",
                  preserveAspectRatio: "xMinYMin"
                }
              },
              /* @__PURE__ */ React.createElement(
                "text",
                {
                  fill: x.color,
                  fontFamily: x.family,
                  fontSize: size,
                  textAnchor: convertHorizontalTextAlignmentToSvgTextAnchor(
                    x.alignment.horizontal
                  ),
                  transform: `rotate(${x.rotation})`,
                  visibility: !x.isVisible ? "hidden" : "visible",
                  x: 0,
                  y: 0
                },
                shortedTextLines.map((y, yIndex) => /* @__PURE__ */ React.createElement(
                  "tspan",
                  {
                    key: y,
                    x: 0,
                    ...yIndex > 0 && {
                      dy: `${x.alignment.vertical !== CertTemplateVerticalTextAlignment.BOTTOM ? "" : "-"}${x.lineHeight}em`
                    }
                  },
                  y
                ))
              )
            )
          );
        }
        case CertTemplateElementType.RUBBER_STAMP: {
          return /* @__PURE__ */ React.createElement(
            "image",
            {
              key: x.id,
              height: 96,
              href: certTemplateSet.rubberStampPngDataUrl,
              visibility: !x.isVisible ? "hidden" : "visible",
              width: 96,
              x: x.position.x + certTemplate.positionOffset.x + x.positionOffset.x,
              y: x.position.y + certTemplate.positionOffset.y + x.positionOffset.y
            }
          );
        }
        case CertTemplateElementType.SIGNATURE: {
          return /* @__PURE__ */ React.createElement(
            "image",
            {
              key: x.id,
              height: 96,
              href: certTemplateSet.signaturePngDataUrl,
              visibility: !x.isVisible ? "hidden" : "visible",
              width: 96,
              x: x.position.x + certTemplate.positionOffset.x + x.positionOffset.x,
              y: x.position.y + certTemplate.positionOffset.y + x.positionOffset.y
            }
          );
        }
        default:
          throw new Error(`Invalid cert template element type: ${x.type}`);
      }
    })
  );
};

// https://www.bundesjugendspiele.de/handbuch/wettkampf-leichtathletik/auswertung-formeln-und-beispiele-zur-punkteberechnung
const calculatePointsOfResult = (event, result) => {
    const discipline = getDiscipline(EventFormat.COMPETITION, result.disciplineId);
    const constant = discipline.constants.find(
        (x) =>
            !dateFns.isAfter(x.validityPeriod.from, event.start) &&
            dateFns.isAfter(x.validityPeriod.to, event.start),
    )[result.participant.gender];
    let points = 0;
    if (
        checkIsMovingDisciplineCategory(
            getDisciplineCategoryOfDiscipline(EventFormat.COMPETITION, result.disciplineId).id,
        )
    ) {
        let extra = 0;
        if (result.measuringType === MeasuringType.MANUALLY) {
            if (discipline.distance <= 300) {
                extra = 0.24;
            } else if (discipline.distance <= 400) {
                extra = 0.14;
            }
        }
        points = (discipline.distance / (result.value + extra) - constant.a) / constant.c;
    } else {
        points = (Math.sqrt(result.value) - constant.a) / constant.c;
    }
    // When the result value is very high, the calculation result could be a negative result
    return Math.floor(Math.max(points, 0));
};

const evaluateDisciplineCategory$1 = (event, results) => {
    let disciplineCategoryEvaluation = {
        isValid: false,
        error: { type: CertGenerationErrorType.NOT_DONE_DISCIPLINE_CATEGORY },
    };
    results.forEach((x) => {
        const points = calculatePointsOfResult(event, x);
        if (!disciplineCategoryEvaluation.isValid || points > disciplineCategoryEvaluation.points) {
            disciplineCategoryEvaluation = {
                isValid: true,
                bestResult: {
                    isFake: x.isFake,
                    disciplineId: x.disciplineId,
                    value: x.value,
                },
                points,
            };
        }
    });
    return disciplineCategoryEvaluation;
};

/**
 * @typedef {Object} Options
 * @property isOnlyUsingAllowedDisciplines
 * @property isRescuingNotGeneratingCerts
 *
 * @param {Options} options
 */
const evaluateParticipant = (event, hydratedParticipant, options) => {
    const allowedDisciplines = getAllowedDisciplines(event, hydratedParticipant);
    const workingHydratedParticipant = {
        ...hydratedParticipant,
        cert: {
            disciplineCategoryIdToDisciplineCategoryEvaluationMap: Object.values(
                DisciplineCategoryId,
            ).reduce(
                (yAccumulator, y) => ({
                    ...yAccumulator,
                    [y]: {},
                }),
                {},
            ),
        },
    };
    Object.values(DisciplineCategoryId).forEach((x) => {
        let disciplineCategoryResults = hydratedParticipant.results.filter(
            (y) =>
                !y.isFailedAttempt &&
                getDisciplineCategoryOfDiscipline(EventFormat.COMPETITION, y.disciplineId).id === x,
        );
        if (options.isOnlyUsingAllowedDisciplines) {
            disciplineCategoryResults = disciplineCategoryResults.filter(
                (y) => allowedDisciplines.find((z) => z.id === y.disciplineId) !== undefined,
            );
        }
        workingHydratedParticipant.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[x] =
            evaluateDisciplineCategory$1(event, disciplineCategoryResults);
    }, {});
    return workingHydratedParticipant;
};

const getCertType$1 = (event, participant, points) => {
    const certRuling =
        certRulings[participant.gender][calculateAgeGroup(event.start, participant.birthYear)];
    if (points < certRuling.winner) {
        return CertType.PARTICIPATION;
    }
    if (points < certRuling.honor) {
        return CertType.WINNER;
    }
    return CertType.HONOR;
};

// Checks that at least 3 out of 4 discipline category evaluations are valid
const checkIsGenerated = (disciplineCategoryIdToDisciplineCategoryEvaluationMap) =>
    Object.values(disciplineCategoryIdToDisciplineCategoryEvaluationMap).filter((x) => x.isValid)
        .length >= 3;

/**
 * @typedef {Object} Options
 * @property isOnlyUsingAllowedDisciplines
 * @property isRescuingNotGeneratingCerts
 *
 * @param {Options} options
 */
const generateForParticipant = (event, hydratedParticipant, options) => {
    // Generate the discipline category evaluations
    const workingHydratedParticipant = evaluateParticipant(event, hydratedParticipant, options);
    // Check if the participant has done enough discipline categories
    workingHydratedParticipant.cert.isGenerated = checkIsGenerated(
        workingHydratedParticipant.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap,
    );
    if (!workingHydratedParticipant.cert.isGenerated) {
        // If the participant has not done enough discipline categories, set the error
        workingHydratedParticipant.cert.error = {
            type: CertGenerationErrorType.NOT_DONE_ENOUGH_DISCIPLINE_CATEGORIES,
        };
    } else {
        // If the participant has done enough discipline categories, calculate the points and type
        workingHydratedParticipant.cert.points = Math.floor(
            Object.values(
                workingHydratedParticipant.cert
                    .disciplineCategoryIdToDisciplineCategoryEvaluationMap,
            )
                .filter((x) =>
                    checkIsCountingResult(
                        workingHydratedParticipant.cert
                            .disciplineCategoryIdToDisciplineCategoryEvaluationMap,
                        x,
                    ),
                )
                .reduce((xAccumulator, x) => xAccumulator + x.points, 0),
        );
        workingHydratedParticipant.cert.type = getCertType$1(
            event,
            hydratedParticipant,
            workingHydratedParticipant.cert.points,
        );
    }
    return {
        participant: { id: workingHydratedParticipant.id },
        ...workingHydratedParticipant.cert,
    };
};

const generateFakeResult$1 = (eventFormat, discipline, participant) => {
    let value;
    switch (discipline.unit) {
        case Unit.METERS:
        case Unit.POINTS:
        case Unit.ZONE:
            value = discipline.valueBoundaries.min;
            break;
        case Unit.SECONDS:
            value = discipline.valueBoundaries.max;
            break;
        default:
            throw new Error(`Invalid unit: ${discipline.unit}`);
    }
    return {
        id: generateNanoId(),
        isFake: true,
        participant,
        disciplineId: discipline.id,
        ...(getDisciplineCategoryOfDiscipline(eventFormat, discipline.id) !==
        DisciplineCategoryId.RUNNING
            ? {}
            : { measuringType: MeasuringType.MANUALLY }),
        isFailedAttempt: false,
        value,
    };
};

/**
 * @typedef {Object} Options
 * @property isOnlyUsingAllowedDisciplines
 * @property isRescuingNotGeneratingCerts
 *
 * @param {Options} options
 */
const generateAll$2 = (event, disciplinePreselection, participants, results, options) => {
    const hydratedParticipants = participants.map((x) => ({
        ...x,
        results: results.filter((y) => y.participant.id === x.id),
    }));
    hydratedParticipants.forEach((x) => {
        // Mark existing results as not fake
        x.results.forEach((y) => {
            y.isFake = false;
        });
        if (options.isRescuingNotGeneratingCerts) {
            // Add fake results to each participant to make sure, that they have enough results
            x.results.push(
                ...Object.values(DisciplineCategoryId).map((y) => {
                    const discipline = getPreselectedDiscipline(
                        event,
                        disciplinePreselection,
                        x,
                        y,
                    );
                    return generateFakeResult$1(event.format, discipline, x);
                }),
            );
        }
    });
    const certs = [];
    hydratedParticipants.forEach((x) => {
        certs.push(generateForParticipant(event, x, options));
    });
    return certs;
};

const competitionEventCertsGenerationService = {
    calculatePointsOfResult,
    findBestResult: evaluateDisciplineCategory$1,
    evaluateParticipant,
    getCertType: getCertType$1,
    generate: generateForParticipant,
    generateAll: generateAll$2,
};

// Normally we would compare disciplines of cause by their id with result.disciplineId. But because in the contest event format, there are multiple disciplines with the same short name, users are getting confused and are entering just one of them whereever. That results in a lot of not generated certs, because these participants then have the wrong discipline on their result. But only the discipline id is wrong, if you compare the short names of the disciplines they are the same. This for example happens for the "A | Weitsprung in die Zone" discipline.

// Populates bestResults with all results of a participant. Used for participants with errors so they can still see their results.
const populateAllResultsIntoBestResults = (event, participant) => {
    Object.values(DisciplineCategoryId).forEach((disciplineCategoryId) => {
        const disciplineCategory = getDisciplineCategory(
            event.format,
            disciplineCategoryId,
        );
        const disciplineCategoryResults = participant.results.filter(
            (x) =>
                getDisciplineCategoryOfDiscipline(event.format, x.disciplineId).id ===
                disciplineCategoryId,
        );
        const sortedResults = sortResults(
            disciplineCategory.disciplines[0],
            disciplineCategoryResults,
        );
        const bestResults = sortedResults.map((x) => ({
            disciplineId: x.disciplineId,
            value: x.value,
            isFake: x.isFake || false,
        }));
	participant.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
			disciplineCategoryId
		] = {
			isValid: false,
			error: participant.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
				disciplineCategoryId
			].error,
			bestResults,
		};
    });
};

// -

const findCommonDisciplinesOfGroup = (event, participants) => {
    const disciplineCategoryIdToCommonDisciplineMap = {};
    Object.values(DisciplineCategoryId).forEach((disciplineCategoryId) => {
        const disciplineIds = participants
            .flatMap((x) => x.results)
            .filter(
                (x) =>
                    getDisciplineCategoryOfDiscipline(event.format, x.disciplineId).id ===
                    disciplineCategoryId,
            )
            .map((x) => x.disciplineId);
        const commonDisciplineId = disciplineIds.reduce(
            (yAccumulator, y) => {
                const count = disciplineIds.filter((x) => x === y).length;
                return count > yAccumulator.count
                    ? {
                            disciplineId: y,
                            count,
                        }
                    : yAccumulator;
            },
            {
                disciplineId: getDisciplineCategory(event.format, disciplineCategoryId)
                    .disciplines[0].id,
                count: 0,
            },
        ).disciplineId;
        disciplineCategoryIdToCommonDisciplineMap[disciplineCategoryId] =
            getDiscipline(EventFormat.CONTEST, commonDisciplineId);
    });
    return disciplineCategoryIdToCommonDisciplineMap;
};

// --

const getRequiredResultCount = (discipline) => {
    switch (discipline.evaluationType) {
        case DisciplineEvaluationType.ADD_ALL:
            return discipline.tryLimit;
        case DisciplineEvaluationType.ADD_TOP_3:
            return 3;
        case DisciplineEvaluationType.BEST:
            return 1;
        case DisciplineEvaluationType.SINGLE:
            return 1;
        default:
            throw new Error(`Invalid evaluation type: ${discipline.evaluationType}`);
    }
};

const generateFakeResult = (discipline, participant) => {
    let value;
    switch (discipline.unit) {
        case Unit.METERS:
        case Unit.POINTS:
        case Unit.ZONE:
            value = discipline.valueBoundaries.min;
            break;
        case Unit.SECONDS:
            value = discipline.valueBoundaries.max;
            break;
        default:
            throw new Error(`Invalid unit: ${discipline.unit}`);
    }
    return {
        id: generateNanoId(),
        isFake: true,
        participant,
        disciplineId: discipline.id,
        isFailedAttempt: false,
        value,
    };
};

const addFakeResultsToParticipants = (
    participants,
    disciplineCategoryIdToCommonDisciplineMap,
) => {
    Object.values(disciplineCategoryIdToCommonDisciplineMap).forEach(
        (discipline) => {
            participants.forEach((x) => {
                x.results.push(
                    ...Array.from({ length: getRequiredResultCount(discipline) }, () =>
                        generateFakeResult(discipline, x),
                    ),
                );
            });
        },
    );
};

// --

const sortOutParticipantsWithNonCommonDisciplines = (
    event,
    participants,
    disciplineCategoryIdToCommonDisciplineMap,
) => {
    Object.entries(disciplineCategoryIdToCommonDisciplineMap).forEach(
        ([disciplineCategoryId, commonDiscipline]) => {
            participants.forEach((x) => {
				if (
					x.results
						.filter(
							(y) =>
								getDisciplineCategoryOfDiscipline(event.format, y.disciplineId)
									.id === disciplineCategoryId,
						)
						// Compare by shortName instead of id.
						.some(
							(y) =>
								getDiscipline(event.format, y.disciplineId).shortName !==
								commonDiscipline.shortName,
						)
				) {
					x.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
						disciplineCategoryId
					].error = {
						type: CertGenerationErrorType.NOT_DONE_COMMON_DISCIPLINE_EXCLUSIVELY,
						targetDisciplineName: commonDiscipline.shortName,
					};
					x.cert.isGenerated = false;
					x.cert.error = {
						type: CertGenerationErrorType.DISCIPLINE_CATEGORY_SPECIFIC,
					};
					populateAllResultsIntoBestResults(event, x); // Still populate bestResults so the user can see what results the participant has
				}
            });
        },
    );
};

// --

const sortResults = (discipline, results) =>
    [...results].sort((a, b) => {
        // Failed attempts always go to the end
        if (a.isFailedAttempt !== b.isFailedAttempt) {
            return a.isFailedAttempt ? 1 : -1;
        }

        // If both are failed attempts, maintain their original order
        if (a.isFailedAttempt) {
            return 0;
        }

        // For non-failed attempts, sort based on the discipline unit
        switch (discipline.unit) {
            case Unit.METERS:
            case Unit.POINTS:
            case Unit.ZONE:
                return b.value - a.value;
            case Unit.SECONDS:
                return a.value - b.value;
            default:
                throw new Error(`Invalid unit: ${discipline.unit}`);
        }
    });

// TODO Change so, that fake results still count but are 0 or so
const evaluateDisciplineCategory = (event, participant, discipline) => {
    /// Compare by shortName instead of id.
    const disciplineResults = participant.results.filter(
        (x) =>
            getDiscipline(event.format, x.disciplineId).shortName ===
            discipline.shortName,
    );
    const requiredResultCount = getRequiredResultCount(discipline);
    const bestResults = sortResults(
        discipline,
        disciplineResults.filter((x) => !x.isFailedAttempt),
    ).slice(0, requiredResultCount);
	if (
		disciplineResults.length < requiredResultCount ||
		bestResults.length === 0
	) {
		return {
			isValid: false,
			error: { type: CertGenerationErrorType.NOT_ENOUGH_RESULTS },
			bestResults,
		};
	}
    let value;
    switch (discipline.evaluationType) {
        case DisciplineEvaluationType.ADD_ALL:
            value = bestResults.reduce(
                (xAccumulator, x) => xAccumulator + x.value,
                0,
            );
            break;
        case DisciplineEvaluationType.ADD_TOP_3:
            value = bestResults.reduce(
                (xAccumulator, x) => xAccumulator + x.value,
                0,
            );
            break;
        case DisciplineEvaluationType.BEST:
            value = bestResults[0].value;
            break;
        case DisciplineEvaluationType.SINGLE:
            value = bestResults[0].value;
            break;
        default:
            throw new Error(`Invalid evaluation type: ${discipline.evaluationType}`);
    }
    return {
        isValid: true,
        bestResults,
        value,
    };
};

const evaluateDisciplineCategories = (
    event,
    participants,
    disciplineCategoryIdToCommonDisciplineMap,
) => {
    Object.values(DisciplineCategoryId).forEach((disciplineCategoryId) => {
        participants.forEach((x) => {
            x.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                disciplineCategoryId
            ] = evaluateDisciplineCategory(
                event,
                x,
                disciplineCategoryIdToCommonDisciplineMap[disciplineCategoryId],
            );
        });
    });
};

const sortOutParticipantsWithMissingDisciplineCategories = (participants) => {
	Object.values(DisciplineCategoryId).forEach((disciplineCategoryId) => {
		participants.forEach((x) => {
			if (
				x.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
					disciplineCategoryId
				].isValid
			) {
				return;
			}
			x.cert.isGenerated = false;
			x.cert.error = { type: CertGenerationErrorType.DISCIPLINE_CATEGORY_SPECIFIC };
		});
	});
};

// --

const generateDisciplineCategoryRankGroups = (
    participants,
    disciplineCategoryId,
) =>
    participants.reduce((xAccumulator, x) => {
        let existingRankGroup = xAccumulator.find(
            (y) =>
                y.value ===
                x.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                    disciplineCategoryId
                ].value,
        );
        if (existingRankGroup === undefined) {
            existingRankGroup = {
                value:
                    x.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                        disciplineCategoryId
                    ].value,
                participants: [],
            };
            xAccumulator.push(existingRankGroup);
        }
        existingRankGroup.participants.push(x);
        return xAccumulator;
    }, []);

const sortDisciplineCategoryRankGroups = (rankGroups, discipline) => {
    switch (discipline.unit) {
        case Unit.METERS:
        case Unit.POINTS:
        case Unit.ZONE:
            rankGroups.sort((a, b) => b.value - a.value);
            break;
        case Unit.SECONDS:
            rankGroups.sort((a, b) => a.value - b.value);
            break;
        default:
            throw new Error(`Invalid unit: ${discipline.unit}`);
    }
};

const assignDisciplineCategoryRanks = (rankGroups, disciplineCategoryId) => {
    let currentRank = 1;
    rankGroups.forEach((x) => {
        x.participants.forEach((y) => {
            y.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[
                disciplineCategoryId
            ].rank = currentRank;
        });
        currentRank += x.participants.length;
    });
};

const rankParticipantsInDisciplineCategories = (
    participants,
    disciplineCategoryIdToCommonDisciplineMap,
) => {
    Object.values(DisciplineCategoryId).forEach((disciplineCategoryId) => {
        const rankGroups = generateDisciplineCategoryRankGroups(
            participants,
            disciplineCategoryId,
        );
        sortDisciplineCategoryRankGroups(
            rankGroups,
            disciplineCategoryIdToCommonDisciplineMap[disciplineCategoryId],
        );
        assignDisciplineCategoryRanks(rankGroups, disciplineCategoryId);
    });
};

// --

const calculateRankSums = (participants) => {
    participants.forEach((x) => {
        x.cert.rankSum = Object.values(DisciplineCategoryId).reduce(
            (yAccumulator, y) =>
                yAccumulator +
                x.cert.disciplineCategoryIdToDisciplineCategoryEvaluationMap[y].rank,
            0,
        );
    });
};

const generateRankGroups = (participants) =>
    participants.reduce((xAccumulator, x) => {
        let existingRankGroup = xAccumulator.find(
            (y) => y.rankSum === x.cert.rankSum,
        );
        if (existingRankGroup === undefined) {
            existingRankGroup = {
                rankSum: x.cert.rankSum,
                participants: [],
            };
            xAccumulator.push(existingRankGroup);
        }
        existingRankGroup.participants.push(x);
        return xAccumulator;
    }, []);

const assignRanks = (rankGroups) => {
    let currentRank = 1;
    rankGroups.forEach((x) => {
        x.participants.forEach((y) => {
            y.cert.points = currentRank;
        });
        currentRank += x.participants.length;
    });
};

const calculateCertTypeToParticipantLimitMap = (participantCount) => {
    const honorCertCount = Math.ceil(participantCount * 0.2);
    const winnerCertCount =
        honorCertCount === participantCount ? 0 : Math.ceil(participantCount * 0.5);
    const participationCount =
        participantCount - honorCertCount - winnerCertCount;
    return {
        [CertType.HONOR]: honorCertCount,
        [CertType.WINNER]: winnerCertCount,
        [CertType.PARTICIPATION]: participationCount,
    };
};

const getCertType = (rank, certTypeToParticipantLimitMap) => {
    if (rank <= certTypeToParticipantLimitMap[CertType.HONOR]) {
        return CertType.HONOR;
    }
    if (
        rank <=
        certTypeToParticipantLimitMap[CertType.HONOR] +
            certTypeToParticipantLimitMap[CertType.WINNER]
    ) {
        return CertType.WINNER;
    }
    return CertType.PARTICIPATION;
};

const assignCertTypes = (participants, certTypeToParticipantLimitMap) => {
    participants.forEach((x) => {
        x.cert.groupParticipantCount = participants.length;
        x.cert.isGenerated = true;
        x.cert.type = getCertType(x.cert.points, certTypeToParticipantLimitMap);
    });
};

const rankParticipants = (participants) => {
    calculateRankSums(participants);
    const rankGroups = generateRankGroups(participants);
    rankGroups.sort((a, b) => a.rankSum - b.rankSum);
    assignRanks(rankGroups);
    const certTypeToParticipantLimitMap = calculateCertTypeToParticipantLimitMap(
        participants.length,
    );
    assignCertTypes(participants, certTypeToParticipantLimitMap);
};

// --

const generateForGroup = (event, participants, options) => {
    const disciplineCategoryIdToCommonDisciplineMap =
        findCommonDisciplinesOfGroup(event, participants);
    if (options.isRescuingNotGeneratingCerts) {
        addFakeResultsToParticipants(
            participants,
            disciplineCategoryIdToCommonDisciplineMap,
        );
    }
    sortOutParticipantsWithNonCommonDisciplines(
        event,
        participants,
        disciplineCategoryIdToCommonDisciplineMap,
    );
    let validParticipants = participants.filter(
        (x) => x.cert.isGenerated !== false,
    );
    evaluateDisciplineCategories(
        event,
        validParticipants,
        disciplineCategoryIdToCommonDisciplineMap,
    );
    sortOutParticipantsWithMissingDisciplineCategories(validParticipants);
    validParticipants = participants.filter((x) => x.cert.isGenerated !== false);
    rankParticipantsInDisciplineCategories(
        validParticipants,
        disciplineCategoryIdToCommonDisciplineMap,
    );
    rankParticipants(validParticipants);
    return participants.map((x) => x.cert);
};

/**
 * @typedef {Object} Options
 * @property {boolean} isOnlyUsingAllowedDisciplines
 * @property {boolean} isRescuingNotGeneratingCerts
 *
 * @param {Options} options
 */
const generateAll$1 = (event, groups, participants, results, options) => {
    const hydratedGroups = splitHydratedGroupsByGender(
        generateHydratedGroups(groups, participants),
    );
    hydratedGroups.forEach((x) => {
        x.participants = x.participants.map((y) => ({
            ...y,
            results: results
                .filter((z) => z.participant.id === y.id)
                .map((z) => {
                    z.isFake = false;
                    return z;
                }),
			cert: {
				participant: { id: y.id },
				disciplineCategoryIdToDisciplineCategoryEvaluationMap: Object.values(
					DisciplineCategoryId,
				).reduce(
					(zAccumulator, z) => ({
						...zAccumulator,
						[z]: {
							isValid: false,
							bestResults: [],
							error: { type: CertGenerationErrorType.NOT_EVALUATED_YET },
						},
					}),
					{},
				),
			},
        }));
    });
    const unallocatedHydratedGroups = hydratedGroups.filter((x) =>
        x.id.startsWith(SpecialGroupId.UNALLOCATED),
    );
    const allocatedHydratedGroups = hydratedGroups.filter(
        (x) => !unallocatedHydratedGroups.find((y) => y.id === x.id),
    );
    const certs = [];
	unallocatedHydratedGroups.forEach((x) => {
		x.participants.forEach((y) => {
			y.cert.isGenerated = false;
			y.cert.error = { type: CertGenerationErrorType.NOT_IN_A_GROUP };
			populateAllResultsIntoBestResults(event, y); // Still populate bestResults so the user can see what results the participant has
			certs.push(y.cert);
		});
	});
    allocatedHydratedGroups.forEach((x) => {
        certs.push(...generateForGroup(event, x.participants, options));
    });
    return certs;
};

const contestEventCertsGenerationService = { generateAll: generateAll$1 };

/**
 * @typedef {Object} Options
 * @property isOnlyUsingAllowedDisciplines
 *
 * @param {Options} options
 */
const generateAll = (event, disciplinePreselection, groups, participants, results, options) =>
    callEventFormatFunction(
        () =>
            contestEventCertsGenerationService.generateAll(
                event,
                groups,
                participants,
                results,
                options,
            ),
        () =>
            competitionEventCertsGenerationService.generateAll(
                event,
                disciplinePreselection,
                participants,
                results,
                options,
            ),
        event.format,
    );

const certsGenerationService = { generateAll };

// https://github.com/sideway/joi/blob/master/API.md#list-of-errors
// https://github.com/kamronbatman/joi-password-complexity/blob/master/src/index.ts

const validationMessages = {
    de: {
        "any.invalid": "{#invalids} ist ungültig",
        "any.myBoolSelect": "Muss ausgewählt sein",
        "any.only": "Nur {#valids} ist gültig",
        "any.required": "Darf nicht leer sein",
        "any.unknown": "Darf nicht diesen Wert haben",
        "array.myGloballyUniqueArrayField":
            "Das Feld {#fieldPath} darf keine doppelten Werte enthalten",
        "date.base": "Muss ein Datum sein",
        "date.dateOnly": "Darf keine Zeit enthalten",
        "date.format": "Muss ein Datum sein",
        "date.greater": "Muss früher als {#limit} sein",
        "date.less": "Muss später als {#limit} sein",
        "date.max": "Darf max. {#limit} sein",
        "date.min": "Muss mind. {#limit} sein",
        "date.strict": "Muss ein Datum sein",
        "myString.n3WordId": "Muss 3 mit Punkten getrennte Wörter enthalten",
        "myString.noEmojis": "Darf keine Emojis enthalten",
        "myString.myRubberStampPngDataUrl": "Muss ein PNG-Bild mit 480x480 Pixeln sein",
        "number.base": "Muss eine Zahl sein",
        "number.greater": "Muss kleiner als {#limit} sein",
        "number.infinity": "Darf nicht unendlich sein",
        "number.integer": "Muss eine ganzzahlige Zahl sein",
        "number.less": "Muss größer als {#limit} sein",
        "number.max": "Darf max. {#limit} sein",
        "number.min": "Muss mind. {#limit} sein",
        "number.multiple": "Muss durch {#mutliple} teilbar sein",
        "number.negative": "Muss negativ sein",
        "number.positive": "Muss positiv sein",
        "number.precision": "Muss mind. auf {#limit} Stellen genau sein",
        "number.unsafe": "Darf nicht so hoch sein",
        "object.myEmptyObject": "Muss leer sein",
        "object.myFormObject": "Muss ein Formobjekt sein",
        "passwordComplexity.lowercase": "Muss mind. einen Kleinbuchstaben enthalten",
        "passwordComplexity.numeric": "Muss mind. eine Ziffer enthalten",
        "passwordComplexity.requirementCount": "Muss mind. Sicherheitsstufe 4 sicher sein",
        "passwordComplexity.symbol": "Muss mind. ein Symbol enthalten",
        "passwordComplexity.tooLong": "Darf max. 25 Zeichen enthalten",
        "passwordComplexity.tooShort": "Muss mind. 8 Zeichen enthalten",
        "passwordComplexity.uppercase": "Muss mind. einen Großbuchstaben enthalten",
        "string.alphanum": "Darf nur Buchstaben und Zahlen enthalten",
        "string.base": "Muss ein Text sein",
        "string.creditCard": "Muss eine Kreditkartennummer sein",
        "string.email": "Muss eine Email-Adresse sein",
        "string.empty": "Darf nicht leer sein",
        "string.length": "Muss genau {#limit} Zeichen lang sein",
        "string.lowercase": "Darf nur Kleinbuchstaben enthalten",
        "string.max": "Darf max. {#limit} Zeichen enthalten",
        "string.min": "Muss mind. {#limit} Zeichen enthalten",
        "string.my3WordId": "Muss 3 mit Punkten getrennte Wörter enthalten",
        "string.myBirthYearParser":
            "Muss eins der folgenden Formate haben: yyyy, dd.mm.yyyy, mm/dd/yyyy oder yyyy-mm-dd. Tag und Monat dürfen auch einstellig sein",
        "string.myGenderParser": "Muss mit m/M oder w/W/f/F anfangen",
        "string.myPasswordConfirmation": "Muss das gleiche wie Passwort sein",
        "string.myResultValuePattern": "",
        "string.pattern.base": "Muss korrekt ausgefüllt sein",
        "string.trim": "Darf keine leeren Zeichen drumherum enthalten",
        "string.uppercase": "Darf nur Großbuchstaben enthalten",
        // "alternatives.all": "",
        // "alternatives.any": "",
        // "alternatives.match": "",
        // "alternatives.one": "",
        // "alternatives.types": "",
        // "any.custom": "",
        // "any.default": "",
        // "any.failover": "",
        // "any.ref": "",
        // "array.base": "",
        // "array.excludes": "",
        // "array.hasKnown": "",
        // "array.hasUnknown": "",
        // "array.includes": "",
        // "array.includesRequiredBoth": "",
        // "array.includesRequiredKnowns": "",
        // "array.includesRequiredUnknowns": "",
        // "array.length": "",
        // "array.max": "",
        // "array.min": "",
        // "array.orderedLength": "",
        // "array.sort.mismatching": "",
        // "array.sort.unsupported": "",
        // "array.sort": "",
        // "array.sparse": "",
        // "array.unique": "",
        // "binary.base": "",
        // "binary.length": "",
        // "binary.max": "",
        // "binary.min": "",
        // "boolean.base": "",
        // "function.arity": "",
        // "function.class": "",
        // "function.maxArity": "",
        // "function.minArity": "",
        // "number.port": "",
        // "object.and": "",
        // "object.assert": "",
        // "object.base": "",
        // "object.instance": "",
        // "object.length": "",
        // "object.max": "",
        // "object.min": "",
        // "object.missing": "",
        // "object.nand": "",
        // "object.oxor": "",
        // "object.pattern.match": "",
        // "object.refType": "",
        // "object.regex": "",
        // "object.rename.multiple": "",
        // "object.rename.override": "",
        // "object.schema": "",
        // "object.unknown": "",
        // "object.with": "",
        // "object.without": "",
        // "object.xor": "",
        // "string.base64": "",
        // "string.dataUri": "",
        // "string.domain": "",
        // "string.guid": "",
        // "string.hex": "",
        // "string.hexAlign": "",
        // "string.hostname": "",
        // "string.ipAddress": "",
        // "string.ipVersion": "",
        // "string.isoDate": "",
        // "string.isoDuration": "",
        // "string.normalize": "",
        // "string.pattern.invert.base": "",
        // "string.pattern.invert.name": "",
        // "string.pattern.name": "",
        // "string.token": "",
        // "string.uri": "",
        // "string.uriCustomScheme": "",
        // "string.uriRelativeOnly": "",
        // "symbol.base": "",
        // "symbol.map": "",
    },
};

const validationOptions = {
    abortEarly: false,
    allowUnknown: false,
    convert: true,
    dateFormat: "date", // TODO This produces Mon Feb 01 2021 or so
    errors: {
        language: "de",
        wrap: {
            label: "",
            array: "",
        },
    },
    messages: validationMessages,
};

// Form and body labeled validation schemas will check for required on their own and do not need an appended .required() in the final schema
const MyJoi = Joi.extend((joi) => ({
    type: "any",
    base: joi.any(),
    rules: {
        myBoolSelect: {
            validate(value, helpers) {
                const { error } = joi.boolean().validate(value, validationOptions);
                if (error !== undefined) {
                    return helpers.error("any.myBoolSelect");
                }
                return value;
            },
        },
        myEventFormatSelect: {
            validate(value, helpers) {
                const { error } = joi
                    .string()
                    .valid(...Object.values(EventFormat))
                    .validate(value, validationOptions);
                if (error !== undefined) {
                    return helpers.error("any.myBoolSelect");
                }
                return value;
            },
        },
        myRankPrintTypeSelect: {
            validate(value, helpers) {
                const { error } = joi
                    .string()
                    .valid(...Object.values(RankPrintType))
                    .validate(value, validationOptions);
                if (error !== undefined) {
                    return helpers.error("any.myBoolSelect");
                }
                return value;
            },
        },
    },
}))
    .extend((joi) => ({
        type: "myEnum",
        base: joi.string(),
        rules: {
            myAddressState: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...germanStates)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myCertSortingPreset: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(CertSortingPreset))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myCertTemplateElementType: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(CertTemplateElementType))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myCertTemplateElementPurpose: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(CertTemplateElementPurpose))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myCertTemplateHorizontalTextAlignment: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(CertTemplateHorizontalTextAlignment))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myCertTemplateVerticalTextAlignment: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(CertTemplateVerticalTextAlignment))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myCertType: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(CertType))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myClassLevelRange: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(ClassLevelRange))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            // For body
            myClassLevelRangeRequiredIfNeeded: {
                method(eventFormat) {
                    return this.$_addRule({
                        name: "myClassLevelRangeRequiredIfNeeded",
                        args: { eventFormat },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    if (args.eventFormat === EventFormat.COMPETITION) {
                        const { value: value2, error } = joi
                            .forbidden()
                            .validate(value, validationOptions);
                        if (error !== undefined) {
                            return helpers.error(error.details[0].type, error.details[0].context);
                        }
                        return value2;
                    }
                    const { value: value2, error } = joi
                        .myEnum()
                        .myClassLevelRange()
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            // For form
            myClassLevelRangeNotRequired: {
                method(eventFormat) {
                    return this.$_addRule({
                        name: "myClassLevelRangeNotRequired",
                        args: { eventFormat },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    if (args.eventFormat === EventFormat.COMPETITION) {
                        return value;
                    }
                    const { value: value2, error } = joi
                        .myEnum()
                        .myClassLevelRange()
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myEventFormat: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(EventFormat))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myGender: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(Gender))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myMeasuringType: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(MeasuringType))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myOrder: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(Order))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myParticipantStarterlistDisciplineLevelOfDetail: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(ParticipantStarterlistDisciplineLevelOfDetail))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myPaymentType: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(PaymentType))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myRankPrintType: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(RankPrintType))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            mySchoolType: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...Object.values(SchoolType))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
        },
    }))
    .extend((joi) => ({
        type: "string",
        base: joi.string(),
        rules: {
            my3WordId: {
                // convert: true,
                validate(value, helpers) {
                    const lowerCaseValue = value.toLowerCase();
                    if (
                        !/^[a-zA-ZäöüßÄÖÜ]+\.[a-zA-ZäöüßÄÖÜ]+\.[a-zA-ZäöüßÄÖÜ]+$/.test(
                            lowerCaseValue,
                        )
                    ) {
                        return helpers.error("string.my3WordId");
                    }
                    return lowerCaseValue;
                },
            },
            myBirthYear: {
                method(eventFormat, eventStart) {
                    return this.$_addRule({
                        name: "myBirthYear",
                        args: {
                            eventFormat,
                            eventStart,
                        },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "eventStart",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    const { value: value2, error } = joi
                        .string()
                        .pattern(/^[0-9]{4}$/)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    const { error: error2 } = joi
                        .number()
                        .min(
                            dateFns.getYear(args.eventStart) -
                                eventFormatToAgeGroupBoundariesMap[args.eventFormat].max,
                        )
                        .max(
                            dateFns.getYear(args.eventStart) -
                                eventFormatToAgeGroupBoundariesMap[args.eventFormat].min,
                        )
                        .validate(value, validationOptions);
                    if (error2 !== undefined) {
                        return helpers.error(error2.details[0].type, error2.details[0].context);
                    }
                    return value2;
                },
            },
            myBirthYearParser: {
                method(eventFormat, eventStart) {
                    return this.$_addRule({
                        name: "myBirthYearParser",
                        args: {
                            eventFormat,
                            eventStart,
                        },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "eventStart",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    let year;
                    try {
                        year = extractYear(value);
                    } catch {
                        return helpers.error("string.myBirthYearParser");
                    }
                    const { value: value2, error } = joi
                        .number()
                        .min(
                            dateFns.getYear(args.eventStart) -
                                eventFormatToAgeGroupBoundariesMap[args.eventFormat].max,
                        )
                        .max(
                            dateFns.getYear(args.eventStart) -
                                eventFormatToAgeGroupBoundariesMap[args.eventFormat].min,
                        )
                        .messages({ "number.base": "Muss eine Zahl oder ein Datum sein" })
                        .validate(year, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myDisciplineId: {
                method(eventFormat) {
                    return this.$_addRule({
                        name: "myDisciplineId",
                        args: { eventFormat },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(...eventFormatToAllDisciplinesMap[args.eventFormat].map((x) => x.id))
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myGenderParser: {
                validate(value, helpers) {
                    let gender;
                    try {
                        gender = parseGender(value);
                    } catch {
                        return helpers.error("string.myGenderParser");
                    }
                    return gender;
                },
            },
            myMeasuringTypeFormValue: {
                method(eventFormat, disciplineId) {
                    return this.$_addRule({
                        name: "myMeasuringTypeFormValue",
                        args: {
                            eventFormat,
                            disciplineId,
                        },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "disciplineId",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    if (
                        args.eventFormat !== EventFormat.COMPETITION ||
                        args.disciplineId === undefined
                    ) {
                        return value;
                    }
                    const discipline = getDiscipline(args.eventFormat, args.disciplineId);
                    if (
                        !checkIsMovingDisciplineCategory(
                            getDisciplineCategoryOfDiscipline(args.eventFormat, discipline.id).id,
                        )
                    ) {
                        return value;
                    }
                    const { value: value2, error } = joi
                        .myEnum()
                        .myMeasuringType()
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myMeasuringTypeBodyValue: {
                method(eventFormat, disciplineId) {
                    return this.$_addRule({
                        name: "myMeasuringTypeBodyValue",
                        args: {
                            eventFormat,
                            disciplineId,
                        },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "disciplineId",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    if (args.eventFormat !== EventFormat.COMPETITION) {
                        const { value: value2, error } = joi
                            .forbidden()
                            .validate(value, validationOptions);
                        if (error !== undefined) {
                            return helpers.error(error.details[0].type, error.details[0].context);
                        }
                        return value2;
                    }
                    const discipline = getDiscipline(args.eventFormat, args.disciplineId);
                    if (
                        !checkIsMovingDisciplineCategory(
                            getDisciplineCategoryOfDiscipline(args.eventFormat, discipline.id).id,
                        )
                    ) {
                        const { value: value2, error } = joi
                            .forbidden()
                            .validate(value, validationOptions);
                        if (error !== undefined) {
                            return helpers.error(error.details[0].type, error.details[0].context);
                        }
                        return value2;
                    }
                    const { value: value2, error } = joi
                        .myEnum()
                        .myMeasuringType()
                        .required()
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myNanoId: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .length(21)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myObjectId: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .length(24)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            // myObjectIdOrNanoId: {
            //     validate(value, helpers) {
            //         const { value: value2, error } = joi
            //             .alternatives()
            //             .try(joi.string().length(24), joi.string().length(21))
            //             .validate(value, validationOptions);
            //         if (error !== undefined) {
            //             return helpers.error(error.details[0].type, error.details[0].context);
            //         }
            //         return value2;
            //     },
            // },
            myPassword: {
                validate(value, helpers) {
                    const { value: value2, error } = joiPasswordComplexity({
                        min: 8,
                        max: 25,
                        lowerCase: 1,
                        upperCase: 1,
                        numeric: 1,
                        symbol: 1,
                        requirementCount: 4,
                    }).validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myPasswordConfirmation: {
                method(password) {
                    return this.$_addRule({
                        name: "myPasswordConfirmation",
                        args: { password },
                    });
                },
                args: [
                    {
                        name: "password",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    const { value: value2, error } = joi
                        .string()
                        .valid(args.password)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        if (error.details[0].type === "any.only") {
                            return helpers.error("string.myPasswordConfirmation");
                        }
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myPasswordPlaceholder: {
                validate(value, helpers) {
                    const { value: newValue, error } = joi
                        .string()
                        .max(100)
                        .validate(value, validationOptions);

                    if (error !== undefined) {
                        return helpers.error(
                            error.details[0].type,
                            error.details[0].context,
                        );
                    }

                    return newValue;
                },
            },
            myResultValueFormValue: {
                method(eventFormat, disciplineId, isFailedAttempt) {
                    return this.$_addRule({
                        name: "myResultValueFormValue",
                        args: {
                            eventFormat,
                            disciplineId,
                            isFailedAttempt,
                        },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "disciplineId",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "isFailedAttempt",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    if (args.disciplineId === undefined || args.isFailedAttempt) {
                        return value;
                    }
                    const discipline = getDiscipline(args.eventFormat, args.disciplineId);
                    const { value: value2, error } = joi
                        .string()
                        .pattern(unitToFilledResultValueRegexPattern[discipline.inputUnit])
                        .required()
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    const { error: error2 } = joi
                        .number()
                        .min(discipline.valueBoundaries.min)
                        .max(discipline.valueBoundaries.max)
                        .validate(
                            parseResultValue(args.eventFormat, discipline.id, value),
                            validationOptions,
                        );
                    if (error2 !== undefined) {
                        if (
                            error2.details[0].type === "number.min" &&
                            discipline.inputUnit === Unit.MINUTES
                        ) {
                            return helpers.error("number.min", {
                                limit: discipline.valueBoundaries.min / 60,
                            });
                        }
                        if (
                            error2.details[0].type === "number.max" &&
                            discipline.inputUnit === Unit.MINUTES
                        ) {
                            return helpers.error("number.max", {
                                limit: discipline.valueBoundaries.max / 60,
                            });
                        }
                        return helpers.error(error2.details[0].type, error2.details[0].context);
                    }
                    return value2;
                },
            },
            myStripeId: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .length(66)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myToken: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .length(100)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
        },
    }))
    .extend((joi) => ({
        type: "number",
        base: joi.number(),
        rules: {
            myBirthYear: {
                method(eventFormat, eventStart) {
                    return this.$_addRule({
                        name: "myBirthYear",
                        args: {
                            eventFormat,
                            eventStart,
                        },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "eventStart",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    const { error } = joi
                        .number()
                        .min(
                            dateFns.getYear(args.eventStart) -
                                eventFormatToAgeGroupBoundariesMap[args.eventFormat].max,
                        )
                        .max(
                            dateFns.getYear(args.eventStart) -
                                eventFormatToAgeGroupBoundariesMap[args.eventFormat].min,
                        )
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value;
                },
            },
            myResultValueBodyValue: {
                method(eventFormat, disciplineId, isFailedAttempt) {
                    return this.$_addRule({
                        name: "myResultValueBodyValue",
                        args: {
                            eventFormat,
                            disciplineId,
                            isFailedAttempt,
                        },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "disciplineId",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                    {
                        name: "isFailedAttempt",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    if (args.isFailedAttempt) {
                        const { value: value2, error } = joi
                            .forbidden()
                            .validate(value, validationOptions);
                        if (error !== undefined) {
                            return helpers.error(error.details[0].type, error.details[0].context);
                        }
                        return value2;
                    }
                    const discipline = getDiscipline(args.eventFormat, args.disciplineId);
                    const { error } = joi
                        .number()
                        .min(discipline.valueBoundaries.min)
                        .max(discipline.valueBoundaries.max)
                        .required()
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value;
                },
            },
        },
    }))
    .extend((joi) => ({
        type: "date",
        base: joi.date(),
        rules: {
            myDynamicMax: {
                method(generateDate) {
                    return this.$_addRule({
                        name: "myDynamicMax",
                        args: { generateDate },
                    });
                },
                args: [
                    {
                        name: "generateDate",
                        assert: (generateDate) => typeof generateDate === "function",
                        message: "must be a function",
                    },
                ],
                validate(value, helpers, args) {
                    const { value: value2, error } = joi
                        .date()
                        .less(args.generateDate())
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myDynamicMin: {
                method(generateDate) {
                    return this.$_addRule({
                        name: "myDynamicMin",
                        args: { generateDate },
                    });
                },
                args: [
                    {
                        name: "generateDate",
                        assert: (generateDate) => typeof generateDate === "function",
                        message: "must be a function",
                    },
                ],
                validate(value, helpers, args) {
                    const { value: value2, error } = joi
                        .date()
                        .min(args.generateDate())
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
        },
    }))
    .extend((joi) => ({
        type: "object",
        base: joi.object(),
        rules: {
            // myAddressObject: {
            //     validate(value, helpers) {
            //         const { value: value2, error } = joi
            //             .object({ description: joi.string().min(2).max(300).required() })
            //             .unknown(true)
            //             .validate(value, validationOptions);
            //         if (error !== undefined) {
            //             return helpers.error(error.details[0].type, error.details[0].context);
            //         }
            //         return value2;
            //     },
            // },
            myEmptyObject: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .object({})
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myFormObject: {
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .object({ id: joi.string().myObjectId().required() })
                        .unknown(true)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myDisciplinePreselectionObject: {
                method(eventFormat) {
                    return this.$_addRule({
                        name: "myDisciplinePreselectionObject",
                        args: { eventFormat },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    const { value: value2, error } = joi
                        .object(
                            callEventFormatFunction(
                                () =>
                                    Object.values(ClassLevelRange).reduce(
                                        (xAccumulator, x) => ({
                                            ...xAccumulator,
                                            [x]: eventFormatToDisciplineCategoriesMap[
                                                args.eventFormat
                                            ].reduce(
                                                (yAccumulator, y) => ({
                                                    ...yAccumulator,
                                                    [y.id]: joi
                                                        .string()
                                                        .valid(
                                                            ...y.disciplines
                                                                .filter(
                                                                    (z) =>
                                                                        z.allowedClassLevelRange ===
                                                                        x,
                                                                )
                                                                .map((z) => z.id),
                                                        )
                                                        .required(),
                                                }),
                                                {},
                                            ),
                                        }),
                                        {},
                                    ),
                                () =>
                                    Object.values(Gender).reduce(
                                        (xAccumulator, x) => ({
                                            ...xAccumulator,
                                            [x]: joi
                                                .object(
                                                    eventFormatToAgeGroupsMap[
                                                        args.eventFormat
                                                    ].reduce(
                                                        (yAccumulator, y) => ({
                                                            ...yAccumulator,
                                                            [y]: joi
                                                                .object(
                                                                    eventFormatToDisciplineCategoriesMap[
                                                                        args.eventFormat
                                                                    ].reduce(
                                                                        (zAccumulator, z) => ({
                                                                            ...zAccumulator,
                                                                            [z.id]: joi
                                                                                .string()
                                                                                .valid(
                                                                                    ...z.disciplines
                                                                                        .filter(
                                                                                            (a) =>
                                                                                                a
                                                                                                    .allowedParticipantGroups[
                                                                                                    x
                                                                                                ] !==
                                                                                                    undefined &&
                                                                                                a.allowedParticipantGroups[
                                                                                                    x
                                                                                                ].includes(
                                                                                                    y,
                                                                                                ),
                                                                                        )
                                                                                        .map(
                                                                                            (a) =>
                                                                                                a.id,
                                                                                        ),
                                                                                )
                                                                                .required(),
                                                                        }),
                                                                        {},
                                                                    ),
                                                                )
                                                                .required(),
                                                        }),
                                                        {},
                                                    ),
                                                )
                                                .required(),
                                        }),
                                        {},
                                    ),
                                args.eventFormat,
                            ),
                        )
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myDisciplinePreselectionFormObject: {
                method(eventFormat) {
                    return this.$_addRule({
                        name: "myDisciplinePreselectionFormObject",
                        args: { eventFormat },
                    });
                },
                args: [
                    {
                        name: "eventFormat",
                        ref: true,
                        assert: () => true,
                        message: "must be a ref",
                    },
                ],
                validate(value, helpers, args) {
                    const { value: value2, error } = joi
                        .object(
                            callEventFormatFunction(
                                () =>
                                    Object.values(ClassLevelRange).reduce(
                                        (xAccumulator, x) => ({
                                            ...xAccumulator,
                                            [x]: eventFormatToDisciplineCategoriesMap[
                                                args.eventFormat
                                            ].reduce(
                                                (yAccumulator, y) => ({
                                                    ...yAccumulator,
                                                    [y.id]: joi
                                                        .object({
                                                            id: joi
                                                                .string()
                                                                .myObjectId()
                                                                .valid(
                                                                    ...y.disciplines
                                                                        .filter(
                                                                            (z) =>
                                                                                z.allowedClassLevelRange ===
                                                                                x,
                                                                        )
                                                                        .map((z) => z.id),
                                                                )
                                                                .required(),
                                                        })
                                                        .unknown(true)
                                                        .required(),
                                                }),
                                                {},
                                            ),
                                        }),
                                        {},
                                    ),
                                () =>
                                    Object.values(Gender).reduce(
                                        (xAccumulator, x) => ({
                                            ...xAccumulator,
                                            [x]: joi
                                                .object(
                                                    eventFormatToAgeGroupsMap[
                                                        args.eventFormat
                                                    ].reduce(
                                                        (yAccumulator, y) => ({
                                                            ...yAccumulator,
                                                            [y]: joi
                                                                .object(
                                                                    eventFormatToDisciplineCategoriesMap[
                                                                        args.eventFormat
                                                                    ].reduce(
                                                                        (zAccumulator, z) => ({
                                                                            ...zAccumulator,
                                                                            [z.id]: joi
                                                                                .string()
                                                                                .valid(
                                                                                    ...z.disciplines
                                                                                        .filter(
                                                                                            (a) =>
                                                                                                a
                                                                                                    .allowedParticipantGroups[
                                                                                                    x
                                                                                                ] !==
                                                                                                    undefined &&
                                                                                                a.allowedParticipantGroups[
                                                                                                    x
                                                                                                ].includes(
                                                                                                    y,
                                                                                                ),
                                                                                        )
                                                                                        .map(
                                                                                            (a) =>
                                                                                                a.id,
                                                                                        ),
                                                                                )
                                                                                .required(),
                                                                        }),
                                                                        {},
                                                                    ),
                                                                )
                                                                .required(),
                                                        }),
                                                        {},
                                                    ),
                                                )
                                                .required(),
                                        }),
                                        {},
                                    ),
                                args.eventFormat,
                            ),
                        )
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
        },
    }))
    .extend((joi) => ({
        type: "array",
        base: joi.array(),
        rules: {
            myGloballyUniqueArrayField: {
                method(fieldPath) {
                    return this.$_addRule({
                        name: "myGloballyUniqueArrayField",
                        args: { fieldPath },
                    });
                },
                args: [
                    {
                        name: "fieldPath",
                        assert: (fieldPath) => typeof fieldPath === "string",
                        message: "must be a string",
                    },
                ],
                validate(value, helpers, args) {
                    const { error } = joi
                        .array()
                        .items()
                        .unique()
                        .validate(value.flatMap((x) => get(x, args.fieldPath)));
                    if (error !== undefined) {
                        return helpers.error("array.myGloballyUniqueArrayField", {
                            ...error.details[0].context,
                            fieldPath: args.fieldPath,
                        });
                    }
                    return value;
                },
            },
        },
    }))
    .extend((joi) => ({
        type: "myString",
        base: joi.string(),
        validate(value, helpers) {
            if (generateEmojiRegex().test(value)) {
                return {
                    value,
                    errors: helpers.error("myString.noEmojis"),
                };
            }
            return { value: transformToMinimalWhiteSpaceString(value) };
        },
        rules: {
            myAddressCity: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .min(2)
                        .max(50)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myAddressHouseNumber: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .min(1)
                        .max(10)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myAddressPostalCode: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .min(2)
                        .max(10)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myAddressStreet: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .min(2)
                        .max(50)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myAddressState: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .min(2)
                        .max(50)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myMailAddress: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .email({ tlds: { allow: false } })
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myName: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .min(2)
                        .max(50)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myShortName: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .min(2)
                        .max(25)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            myUsername: {
                // convert: true,
                validate(value, helpers) {
                    const { value: value2, error } = joi
                        .string()
                        .alphanum()
                        .min(2)
                        .max(25)
                        .validate(value, validationOptions);
                    if (error !== undefined) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                    return value2;
                },
            },
            // myRubberStampPngDataUrl: {
            //     // convert: true,
            //     validate(value, helpers) {
            //         let dimensions;
            //         try {
            //             dimensions = sizeOf(Buffer.from(value.split(",")[1], "base64"));
            //         } catch {}
            //         if (
            //             dimensions === undefined ||
            //             dimensions.width !== 480 ||
            //             dimensions.height !== 480
            //         ) {
            //             return helpers.error("myString.myRubberStampPngDataUrl");
            //         }
            //         return value;
            //     },
            // },
        },
    }));

const certTemplateElementLimit = 100;

const certTemplateSetValidationSchemas = {
    get: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    testPrint: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({
            eventId: MyJoi.string().myObjectId().required(),
            certType: MyJoi.myEnum().myCertType().required(),
        }).required(),
    },
    update: {
        forms: {
            presetSelection: MyJoi.object({
                preset: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                    .unknown(true)
                    .required(),
            }).required(),
            settings: MyJoi.object({
                certTemplate: MyJoi.object({
                    dimensions: MyJoi.object({
                        width: MyJoi.number().required(),
                        height: MyJoi.number().required(),
                    }).required(),
                    alignmentLineDistance: MyJoi.object({
                        x: MyJoi.number().required(),
                        y: MyJoi.number().required(),
                    }).required(),
                    elements: MyJoi.array()
                        .items(
                            MyJoi.object({
                                id: MyJoi.string().required(),
                                name: MyJoi.string().required(),
                                type: MyJoi.myEnum().myCertTemplateElementType().required(),
                                purpose: MyJoi.myEnum().myCertTemplateElementPurpose().required(),
                                isVisible: MyJoi.boolean().required(),
                                position: MyJoi.object({
                                    x: MyJoi.number().required(),
                                    y: MyJoi.number().required(),
                                }).required(),
                                positionOffset: MyJoi.object({
                                    max: MyJoi.number().required(),
                                    step: MyJoi.number().required(),
                                    x: MyJoi.number().required(),
                                    y: MyJoi.number().required(),
                                }).required(),
                                // text
                                fieldPath: MyJoi.any(),
                                rotation: MyJoi.any(),
                                alignment: MyJoi.any(),
                                family: MyJoi.any(),
                                size: MyJoi.any(),
                                dynamicSize: MyJoi.any(),
                                lineHeight: MyJoi.any(),
                                // line
                                endPosition: MyJoi.any(),
                                width: MyJoi.any(),
                                // common of text and line
                                color: MyJoi.any(),
                            }).when("type", {
                                switch: [
                                    {
                                        is: CertTemplateElementType.TEXT,
                                        then: MyJoi.object({
                                            // text
                                            fieldPath: MyJoi.string().required(),
                                            rotation: MyJoi.number().required(),
                                            alignment: MyJoi.object({
                                                horizontal: MyJoi.myEnum()
                                                    .myCertTemplateHorizontalTextAlignment()
                                                    .required(),
                                                vertical: MyJoi.myEnum()
                                                    .myCertTemplateVerticalTextAlignment()
                                                    .required(),
                                            }).required(),
                                            family: MyJoi.string().valid("Helvetica").required(),
                                            size: MyJoi.number(),
                                            dynamicSize: MyJoi.object({
                                                maxWidth: MyJoi.number().required(),
                                                max: MyJoi.number().required(),
                                                min: MyJoi.number().required(),
                                            }),
                                            lineHeight: MyJoi.number().required(),
                                            // line
                                            endPosition: MyJoi.forbidden(),
                                            width: MyJoi.forbidden(),
                                            // common of text and line
                                            color: MyJoi.string().required(),
                                        })
                                            .xor("size", "dynamicSize")
                                            .unknown(true),
                                    },
                                    {
                                        is: CertTemplateElementType.LINE,
                                        then: MyJoi.object({
                                            // text
                                            fieldPath: MyJoi.forbidden(),
                                            rotation: MyJoi.forbidden(),
                                            alignment: MyJoi.forbidden(),
                                            family: MyJoi.forbidden(),
                                            size: MyJoi.forbidden(),
                                            dynamicSize: MyJoi.forbidden(),
                                            lineHeight: MyJoi.forbidden(),
                                            // line
                                            endPosition: MyJoi.object({
                                                x: MyJoi.number().required(),
                                                y: MyJoi.number().required(),
                                            }).required(),
                                            width: MyJoi.number().required(),
                                            // common of text and line
                                            color: MyJoi.string().required(),
                                        }).unknown(true),
                                    },
                                    {
                                        is: CertTemplateElementType.RUBBER_STAMP,
                                        then: MyJoi.object({
                                            // text
                                            fieldPath: MyJoi.forbidden(),
                                            rotation: MyJoi.forbidden(),
                                            alignment: MyJoi.forbidden(),
                                            family: MyJoi.forbidden(),
                                            size: MyJoi.forbidden(),
                                            dynamicSize: MyJoi.forbidden(),
                                            lineHeight: MyJoi.forbidden(),
                                            // line
                                            endPosition: MyJoi.forbidden(),
                                            width: MyJoi.forbidden(),
                                            // common of text and line
                                            color: MyJoi.forbidden(),
                                        }).unknown(true),
                                    },
                                    {
                                        is: CertTemplateElementType.SIGNATURE,
                                        then: MyJoi.object({
                                            // text
                                            fieldPath: MyJoi.forbidden(),
                                            rotation: MyJoi.forbidden(),
                                            alignment: MyJoi.forbidden(),
                                            family: MyJoi.forbidden(),
                                            size: MyJoi.forbidden(),
                                            dynamicSize: MyJoi.forbidden(),
                                            lineHeight: MyJoi.forbidden(),
                                            // line
                                            endPosition: MyJoi.forbidden(),
                                            width: MyJoi.forbidden(),
                                            // common of text and line
                                            color: MyJoi.forbidden(),
                                        }).unknown(true),
                                    },
                                ],
                            }),
                        )
                        .unique("id")
                        .min(1)
                        .max(certTemplateElementLimit)
                        .required(),
                    scale: MyJoi.object({
                        max: MyJoi.number().required(),
                        step: MyJoi.number().required(),
                        value: MyJoi.number().required(),
                    }).required(),
                    positionOffset: MyJoi.object({
                        max: MyJoi.number().required(),
                        step: MyJoi.number().required(),
                        x: MyJoi.number().required(),
                        y: MyJoi.number().required(),
                    }).required(),
                }).required(),
            }).required(),
        },
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({
            eventId: MyJoi.string().myObjectId().required(),
            certType: MyJoi.myEnum().myCertType().required(),
        }).required(),
        body: MyJoi.object({
            presetId: MyJoi.string().myObjectId().required(),
            certTemplate: MyJoi.object({
                dimensions: MyJoi.object({
                    width: MyJoi.number().required(),
                    height: MyJoi.number().required(),
                }).required(),
                alignmentLineDistance: MyJoi.object({
                    x: MyJoi.number().required(),
                    y: MyJoi.number().required(),
                }).required(),
                elements: MyJoi.array()
                    .items(
                        MyJoi.object({
                            id: MyJoi.string().required(),
                            name: MyJoi.string().required(),
                            type: MyJoi.myEnum().myCertTemplateElementType().required(),
                            purpose: MyJoi.myEnum().myCertTemplateElementPurpose().required(),
                            isVisible: MyJoi.boolean().required(),
                            position: MyJoi.object({
                                x: MyJoi.number().required(),
                                y: MyJoi.number().required(),
                            }).required(),
                            positionOffset: MyJoi.object({
                                max: MyJoi.number().required(),
                                step: MyJoi.number().required(),
                                x: MyJoi.number().required(),
                                y: MyJoi.number().required(),
                            }).required(),
                            // text
                            fieldPath: MyJoi.any(),
                            rotation: MyJoi.any(),
                            alignment: MyJoi.any(),
                            family: MyJoi.any(),
                            size: MyJoi.any(),
                            dynamicSize: MyJoi.any(),
                            lineHeight: MyJoi.any(),
                            // crossing line
                            endPosition: MyJoi.any(),
                            width: MyJoi.any(),
                            // common of text and line
                            color: MyJoi.any(),
                        }).when("type", {
                            switch: [
                                {
                                    is: CertTemplateElementType.TEXT,
                                    then: MyJoi.object({
                                        // text
                                        fieldPath: MyJoi.string().required(),
                                        rotation: MyJoi.number().required(),
                                        alignment: MyJoi.object({
                                            horizontal: MyJoi.myEnum()
                                                .myCertTemplateHorizontalTextAlignment()
                                                .required(),
                                            vertical: MyJoi.myEnum()
                                                .myCertTemplateVerticalTextAlignment()
                                                .required(),
                                        }).required(),
                                        family: MyJoi.string().valid("Helvetica").required(),
                                        size: MyJoi.number(),
                                        dynamicSize: MyJoi.object({
                                            maxWidth: MyJoi.number().required(),
                                            max: MyJoi.number().required(),
                                            min: MyJoi.number().required(),
                                        }),
                                        lineHeight: MyJoi.number().required(),
                                        // line
                                        endPosition: MyJoi.forbidden(),
                                        width: MyJoi.forbidden(),
                                    })
                                        .xor("size", "dynamicSize")
                                        .unknown(true),
                                },
                                {
                                    is: CertTemplateElementType.LINE,
                                    then: MyJoi.object({
                                        // text
                                        fieldPath: MyJoi.forbidden(),
                                        rotation: MyJoi.forbidden(),
                                        alignment: MyJoi.forbidden(),
                                        family: MyJoi.forbidden(),
                                        size: MyJoi.forbidden(),
                                        dynamicSize: MyJoi.forbidden(),
                                        lineHeight: MyJoi.forbidden(),
                                        // line
                                        endPosition: MyJoi.object({
                                            x: MyJoi.number().required(),
                                            y: MyJoi.number().required(),
                                        }).required(),
                                        width: MyJoi.number().required(),
                                    }).unknown(true),
                                },
                                {
                                    is: CertTemplateElementType.RUBBER_STAMP,
                                    then: MyJoi.object({
                                        // text
                                        fieldPath: MyJoi.forbidden(),
                                        rotation: MyJoi.forbidden(),
                                        alignment: MyJoi.forbidden(),
                                        family: MyJoi.forbidden(),
                                        size: MyJoi.forbidden(),
                                        dynamicSize: MyJoi.forbidden(),
                                        lineHeight: MyJoi.forbidden(),
                                        // line
                                        endPosition: MyJoi.forbidden(),
                                        width: MyJoi.forbidden(),
                                        // common of text and line
                                        color: MyJoi.forbidden(),
                                    }).unknown(true),
                                },
                                {
                                    is: CertTemplateElementType.SIGNATURE,
                                    then: MyJoi.object({
                                        // text
                                        fieldPath: MyJoi.forbidden(),
                                        rotation: MyJoi.forbidden(),
                                        alignment: MyJoi.forbidden(),
                                        family: MyJoi.forbidden(),
                                        size: MyJoi.forbidden(),
                                        dynamicSize: MyJoi.forbidden(),
                                        lineHeight: MyJoi.forbidden(),
                                        // line
                                        endPosition: MyJoi.forbidden(),
                                        width: MyJoi.forbidden(),
                                        // common of text and line
                                        color: MyJoi.forbidden(),
                                    }).unknown(true),
                                },
                            ],
                        }),
                    )
                    .unique("id")
                    .min(1)
                    .max(certTemplateElementLimit)
                    .required(),
                scale: MyJoi.object({
                    max: MyJoi.number().required(),
                    step: MyJoi.number().required(),
                    value: MyJoi.number().required(),
                }).required(),
                positionOffset: MyJoi.object({
                    max: MyJoi.number().required(),
                    step: MyJoi.number().required(),
                    x: MyJoi.number().required(),
                    y: MyJoi.number().required(),
                }).required(),
            }).required(),
        }).required(),
    },
    imageOverlayUpdate: {
        forms: {
            upload: MyJoi.object({ image: MyJoi.any().required() }).required(),
            cropping: MyJoi.object({
                x: MyJoi.number().required(),
                y: MyJoi.number().required(),
                zoom: MyJoi.number().required(),
                areaPixels: MyJoi.any().required(),
                image: MyJoi.any().required(),
            }).required(),
            chromaKeying: MyJoi.object({
                threshold: MyJoi.number().required(),
                image: MyJoi.any().required(),
            }).required(),
        },
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    rubberStampUpdate: {
        body: MyJoi.object({ rubberStampPngDataUrl: MyJoi.string().required() }).required(),
    },
    signatureUpdate: {
        body: MyJoi.object({ signaturePngDataUrl: MyJoi.string().required() }).required(),
    },
};

const nameMaxLength = 50;
const searchValueMaxLength = 200;
const shortNameMaxLength = 25;

const generateListingModifiers = (defaultSorting) =>
    MyJoi.object({
        searchValue: MyJoi.myString().min(3).max(searchValueMaxLength),
        fieldSortingPath: MyJoi.when("searchValue", {
            is: MyJoi.exist(),
            then: MyJoi.forbidden(),
            otherwise: MyJoi.string()
                .valid(...defaultSorting.map((x) => x.path))
                .required(),
        }),
        fieldSortingOrder: MyJoi.when("searchValue", {
            is: MyJoi.exist(),
            then: MyJoi.forbidden(),
            otherwise: MyJoi.myEnum().myOrder().required(),
        }),
        pageSize: MyJoi.number().integer().min(1).max(100).required(),
        pageIndex: MyJoi.when("searchValue", {
            is: MyJoi.exist(),
            then: MyJoi.forbidden(),
            otherwise: MyJoi.number().integer().min(0).required(),
        }),
    }).required();

const validationTemplates = {
    nameMaxLength,
    searchValueMaxLength,
    shortNameMaxLength,
    generateListingModifiers,
};

const certValidationSchemas = {
    allGeneration: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            options: MyJoi.object({
                isOnlyUsingAllowedDisciplines: MyJoi.boolean().required(),
                isRescuingNotGeneratingCerts: MyJoi.boolean().required(),
            }).required(),
        }).required(),
    },
    print: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        form: MyJoi.object({
            rankPrintType: MyJoi.any().myRankPrintTypeSelect().required(),
        }),
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            rankPrintType: MyJoi.myEnum().myRankPrintType().required(),
        }).required(),
    },
    list: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({
            eventId: MyJoi.string().myObjectId().required(),
            isOnlyNotGenerated: MyJoi.boolean(),
            isOnlyGenerated: MyJoi.boolean(),
        })
            .xor("isOnlyGenerated", "isOnlyNotGenerated")
            .concat(validationTemplates.generateListingModifiers(defaultCertSorting))
            .required(),
    },
    overviewOfAllPrinting: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    allExporting: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    allPrinting: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        form: MyJoi.object({
            sorting: MyJoi.object({
                preset: MyJoi.myEnum().myCertSortingPreset().required(),
                order: MyJoi.myEnum().myOrder().required(),
            }).required(),
            rankPrintType: MyJoi.any().myRankPrintTypeSelect().required(),
        }).required(),
        queryParams: MyJoi.object({
            eventId: MyJoi.string().myObjectId().required(),
            sortingPreset: MyJoi.myEnum().myCertSortingPreset().required(),
            sortingOrder: MyJoi.myEnum().myOrder().required(),
        }).required(),
        body: MyJoi.object({
            rankPrintType: MyJoi.myEnum().myRankPrintType().required(),
        }).required(),
    },
};

const disciplinePreselectionValidationSchemas = {
    get: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    update: {
        form: MyJoi.object({
            value: MyJoi.object()
                .myDisciplinePreselectionFormObject(MyJoi.ref("$event.format"))
                .required(),
        }).required(),
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            disciplinePreselection: MyJoi.object()
                .myDisciplinePreselectionObject(MyJoi.ref("$event.format"))
                .required(),
        }).required(),
    },
};

const eventValidationSchemas = {
    creation: {
        forms: {
            typeSelection: MyJoi.object({
                format: MyJoi.any().myEventFormatSelect().required(),
                isTest: MyJoi.any().myBoolSelect().required(),
            }).required(),
            settings: {
                test: MyJoi.object({
                    name: MyJoi.myString().myName().required(),
                    start: MyJoi.date()
                        .iso()
                        .myDynamicMin(() => dateFns.startOfDay(new Date()))
                        .myDynamicMax(() =>
                            dateFns.addYears(
                                dateFns.startOfDay(new Date()),
                                config.event.futureCreationLimitInYears,
                            ),
                        )
                        .empty(null)
                        .required(),
                }).required(),
                real: MyJoi.object({
                    name: MyJoi.myString().myName().required(),
                    start: MyJoi.date()
                        .iso()
                        .myDynamicMin(() => dateFns.startOfDay(new Date()))
                        .myDynamicMax(() =>
                            dateFns.addYears(
                                dateFns.startOfDay(new Date()),
                                config.event.futureCreationLimitInYears,
                            ),
                        )
                        .empty(null)
                        .required(),
                    limits: MyJoi.object({
                        participant: MyJoi.number()
                            .integer()
                            .valid(...config.event.real.allowedParticipantLimits)
                            .empty(null)
                            .required(),
                    }).required(),
                }).required(),
            },
            paymentSettings: MyJoi.object({
                discountCode: MyJoi.myString().myName().allow("").required(),
                type: MyJoi.myEnum().myPaymentType().required(),
            }).required(),
        },
        queryParams: MyJoi.object({ schoolId: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            event: MyJoi.object({
                format: MyJoi.myEnum().myEventFormat().required(),
                isTest: MyJoi.boolean().required(),
                name: MyJoi.myString().myName().required(),
                start: MyJoi.date()
                    .iso()
                    .myDynamicMin(() => dateFns.subDays(dateFns.startOfDay(new Date()), 1))
                    .myDynamicMax(() =>
                        dateFns.add(dateFns.startOfDay(new Date()), {
                            years: config.event.futureCreationLimitInYears,
                            days: 1,
                        }),
                    )
                    .required(),
                limits: MyJoi.when("isTest", {
                    is: true,
                    then: MyJoi.forbidden(),
                    otherwise: MyJoi.object({
                        participant: MyJoi.number()
                            .integer()
                            .valid(...config.event.real.allowedParticipantLimits)
                            .required(),
                    }).required(),
                }),
            }).required(),
            discountCode: MyJoi.when("event.isTest", {
                is: true,
                then: MyJoi.forbidden(),
                otherwise: MyJoi.myString().myName(),
            }),
            paymentType: MyJoi.when("event.isTest", {
                is: true,
                then: MyJoi.forbidden(),
                otherwise: MyJoi.myEnum().myPaymentType().required(),
            }),
        }).required(),
    },
    reportGeneration: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        // body: MyJoi.object({ name: MyJoi.myString().myName().required() }).required(),
    },
    get: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required().required() }).required(),
    },
    checkoutCompletedResultGet: {
        preAuthentication: {
            params: MyJoi.object({ stripeSessionId: MyJoi.string().myStripeId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({
            stripeSessionId: MyJoi.string().myStripeId().required(),
        }).required(),
    },
    list: {
        preAuthentication: {
            queryParams: MyJoi.object({ schoolId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ schoolId: MyJoi.string().myObjectId().required() })
            .concat(validationTemplates.generateListingModifiers(defaultEventSorting))
            .required(),
    },
    nameUpdate: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({ name: MyJoi.myString().myName().required() }).required(),
    },
    removalRequest: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({ password: MyJoi.string().required() }).required(),
    },
    removalTokenValidation: {
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
    },
    removalCancellation: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    removal: {
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
        body: MyJoi.object({ password: MyJoi.string().required() }).required(),
    },
    removalByAdmin: {
        params: MyJoi.object({ n3WordId: MyJoi.string().my3WordId().required() }).required(),
        body: MyJoi.object({
            schoolMailAddress: MyJoi.myString().myMailAddress().required(),
            schoolCreated: MyJoi.date().iso().required(),
        }).required(),
    },
};

const groupValidationSchemas = {
    creation: {
        form: MyJoi.object({
            name: MyJoi.myString().myShortName().required(),
            classLevelRange: MyJoi.myEnum().myClassLevelRangeNotRequired(
                MyJoi.ref("$event.format"),
            ),
        }).required(),
        preAuthentication: {
            body: MyJoi.object({
                group: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                    .unknown(true)
                    .required(),
            })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            group: MyJoi.object({
                eventId: MyJoi.string().myObjectId().required(),
                name: MyJoi.myString().myShortName().required(),
                classLevelRange: MyJoi.myEnum().myClassLevelRangeRequiredIfNeeded(
                    MyJoi.ref("$event.format"),
                ),
            }).required(),
        }).required(),
    },
    list: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
            .concat(validationTemplates.generateListingModifiers(defaultGroupSorting))
            .required(),
    },
    allGet: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    update: {
        form: MyJoi.object({
            name: MyJoi.myString().myShortName().required(),
            classLevelRange: MyJoi.myEnum().myClassLevelRangeNotRequired(
                MyJoi.ref("$event.format"),
            ),
        }).required(),
    },
    updateAll: {
        forms: {
            autoDistributionSettings: MyJoi.object({
                participantLimitPerGroup: MyJoi.number()
                    .integer()
                    .min(1)
                    .max(config.event.alltimeLimits.participant)
                    .required(),
            }).required(),
        },
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
            body: MyJoi.object({
                groups: MyJoi.array()
                    .items(
                        MyJoi.object({
                            id: MyJoi.string().myObjectId(),
                            tempId: MyJoi.string().myNanoId(),
                            participantIds: MyJoi.array()
                                .items(MyJoi.string().myObjectId())
                                .unique()
                                .required(),
                        }).xor("id", "tempId"),
                    )
                    .unique(
                        (a, b) =>
                            a.id === b.id ||
                            a.tempId === b.tempId ||
                            a.id === b.tempId ||
                            a.tempId === b.id,
                    )
                    .myGloballyUniqueArrayField("participantIds")
                    // .min(0)
                    .max(config.event.alltimeLimits.group)
                    .required(),
            })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            groups: MyJoi.array()
                .items(
                    MyJoi.object({
                        id: MyJoi.string().myObjectId(),
                        tempId: MyJoi.string().myNanoId(),
                        name: MyJoi.myString().myShortName().required(),
                        classLevelRange: MyJoi.myEnum().myClassLevelRangeRequiredIfNeeded(
                            MyJoi.ref("$event.format"),
                        ),
                        participantIds: MyJoi.array()
                            .items(MyJoi.string().myObjectId())
                            .unique()
                            .required(),
                    }).xor("id", "tempId"),
                )
                .unique(
                    (a, b) =>
                        (a.id === undefined ? a.tempId : a.id) ===
                        (b.id === undefined ? b.tempId : b.id),
                )
                .unique((a, b) => a.name.toLowerCase() === b.name.toLowerCase())
                .myGloballyUniqueArrayField("participantIds")
                // .min(0)
                .max(config.event.alltimeLimits.group)
                .required(),
        }).required(),
    },
};

const helperValidationSchemas = {
    creation: {
        form: MyJoi.object({
            isGeneratingUsername: MyJoi.boolean().required(),
            isGeneratingPassword: MyJoi.boolean().required(),
            helper: MyJoi.object({
                name: MyJoi.myString()
                    .myShortName()
                    .when("/isGeneratingUsername", {
                        is: MyJoi.valid(false),
                        then: MyJoi.allow(""),
                    })
                    .required(),
                username: MyJoi.when("/isGeneratingUsername", {
                    is: MyJoi.valid(false),
                    then: MyJoi.myString().myUsername(),
                }).required(),
                password: MyJoi.when("/isGeneratingPassword", {
                    is: MyJoi.valid(false),
                    then: MyJoi.string().myPassword(),
                }).required(),
                isActive: MyJoi.boolean().required(),
            }).required(),
        }).required(),
        preAuthentication: {
            body: MyJoi.object({
                helper: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                    .unknown(true)
                    .required(),
            })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            isGeneratingUsername: MyJoi.boolean().required(),
            isGeneratingPassword: MyJoi.boolean().required(),
            helper: MyJoi.object({
                eventId: MyJoi.string().myObjectId().required(),
                name: MyJoi.myString()
                    .myShortName()
                    .when("/isGeneratingUsername", {
                        is: MyJoi.valid(true),
                        then: MyJoi.required(),
                    }),
                username: MyJoi.when("/isGeneratingUsername", {
                    is: MyJoi.valid(true),
                    then: MyJoi.forbidden(),
                    otherwise: MyJoi.myString().myUsername().required(),
                }),
                password: MyJoi.when("/isGeneratingPassword", {
                    is: MyJoi.valid(true),
                    then: MyJoi.forbidden(),
                    otherwise: MyJoi.string().myPassword().required(),
                }),
                isActive: MyJoi.boolean().required(),
            }).required(),
        }).required(),
    },
    import: {
        forms: {
            fileUpload: MyJoi.object({
                fileDatas: MyJoi.any().meta({ swaggerType: "file" }).required(),
            }).required(),
            fileSettings: MyJoi.object({
                isFirstRowContainingHeadings: MyJoi.boolean().required(),
            }).required(),
            dataSelection: MyJoi.object({
                areNamesSpecified: MyJoi.boolean().required(),
                nameColumnIndex: MyJoi.when("areNamesSpecified", {
                    is: MyJoi.valid(true),
                    then: MyJoi.number().integer(),
                }).required(),
                isGeneratingUsernames: MyJoi.boolean()
                    .when("areNamesSpecified", {
                        is: MyJoi.valid(false),
                        then: MyJoi.valid(MyJoi.override, false),
                    })
                    .required(),
                usernameColumnIndex: MyJoi.when("isGeneratingUsernames", {
                    is: MyJoi.valid(false),
                    then: MyJoi.number().integer(),
                }).required(),
                isGeneratingPasswords: MyJoi.boolean().required(),
                passwordColumnIndex: MyJoi.when("isGeneratingPasswords", {
                    is: MyJoi.valid(false),
                    then: MyJoi.number().integer(),
                }).required(),
            }).required(),
            data: MyJoi.object({
                areNamesSpecified: MyJoi.boolean().required(),
                isGeneratingUsernames: MyJoi.boolean()
                    .when("areNamesSpecified", {
                        is: MyJoi.valid(false),
                        then: MyJoi.valid(MyJoi.override, false),
                    })
                    .required(),
                isGeneratingPasswords: MyJoi.boolean().required(),
                helpers: MyJoi.array()
                    .items(
                        MyJoi.object({
                            tempId: MyJoi.string().myNanoId().required(),
                            name: MyJoi.when("/areNamesSpecified", {
                                is: MyJoi.valid(false),
                                then: MyJoi.forbidden(),
                                otherwise: MyJoi.myString().myShortName().required(),
                            }),
                            username: MyJoi.when("/isGeneratingUsernames", {
                                is: MyJoi.valid(true),
                                then: MyJoi.forbidden(),
                                otherwise: MyJoi.myString().myUsername().required(),
                            }),
                            password: MyJoi.when("/isGeneratingPasswords", {
                                is: MyJoi.valid(true),
                                then: MyJoi.forbidden(),
                                otherwise: MyJoi.string().myPassword().required(),
                            }),
                        }),
                    )
                    .unique("tempId")
                    .min(1)
                    .max(config.event.alltimeLimits.helper)
                    .required(),
            }).required(),
        },
        preAuthentication: {
            body: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            eventId: MyJoi.string().myObjectId().required(),
            areNamesSpecified: MyJoi.boolean().required(),
            isGeneratingUsernames: MyJoi.boolean()
                .when("areNamesSpecified", {
                    is: MyJoi.valid(false),
                    then: MyJoi.valid(MyJoi.override, false),
                })
                .required(),
            isGeneratingPasswords: MyJoi.boolean().required(),
            helpers: MyJoi.array()
                .items(
                    MyJoi.object({
                        tempId: MyJoi.string().myNanoId().required(),
                        name: MyJoi.when("/areNamesSpecified", {
                            is: MyJoi.valid(false),
                            then: MyJoi.forbidden(),
                            otherwise: MyJoi.myString().myShortName().required(),
                        }),
                        username: MyJoi.when("/isGeneratingUsernames", {
                            is: MyJoi.valid(true),
                            then: MyJoi.forbidden(),
                            otherwise: MyJoi.myString().myUsername().required(),
                        }),
                        password: MyJoi.when("/isGeneratingPasswords", {
                            is: MyJoi.valid(true),
                            then: MyJoi.forbidden(),
                            otherwise: MyJoi.string().myPassword().required(),
                        }),
                    }),
                )
                .unique("tempId")
                .min(1)
                .max(config.event.alltimeLimits.helper)
                .required(),
        }).required(),
    },
    login: {
        form: MyJoi.object({
            event3WordId: MyJoi.string().my3WordId().required(),
            username: MyJoi.myString().myUsername().required(),
            password: MyJoi.string().myPasswordPlaceholder().required(),
        }).required(),
        queryParams: MyJoi.object({
            event3WordId: MyJoi.string().my3WordId().required(),
        }).required(),
        body: MyJoi.object({
            username: MyJoi.myString().myUsername().required(),
            password: MyJoi.string().myPasswordPlaceholder().required(),
        }).required(),
    },
    relogin: {
        queryParams: MyJoi.object({ helperId: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({ password: MyJoi.string().myPasswordPlaceholder().required() }).required(),
    },
    authorizationRefresh: {
        queryParams: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
    },
    logout: {
        queryParams: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
    },
    get: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    list: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
            .concat(validationTemplates.generateListingModifiers(defaultHelperSorting))
            .required(),
    },
    loginDataOfAllPrint: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    isActiveOfAllUpdate: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({ isActive: MyJoi.boolean().required() }).required(),
    },
    update: {
        form: MyJoi.object({
            name: MyJoi.myString().myShortName().allow("").required(),
            username: MyJoi.myString().myUsername().required(),
            password: MyJoi.string().myPassword().required(),
            isActive: MyJoi.boolean().required(),
        }).required(),
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            helper: MyJoi.object({
                name: MyJoi.myString().myShortName(),
                username: MyJoi.myString().myUsername().required(),
                password: MyJoi.string().myPassword().required(),
                isActive: MyJoi.boolean().required(),
            }).required(),
        }).required(),
    },
    removal: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({ isResultsKeepingEnabled: MyJoi.boolean().required() }).required(),
    },
    manyRemoval: {
        generateForm: (confirmation) =>
            MyJoi.object({ confirmation: MyJoi.string().valid(confirmation).required() }),
        preAuthentication: {
            body: MyJoi.object({
                ids: MyJoi.array()
                    .items(MyJoi.string().myObjectId())
                    .unique()
                    .min(1)
                    .max(config.bulkRemovalItemLimit)
                    .required(),
            })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            ids: MyJoi.array()
                .items(MyJoi.string().myObjectId())
                .unique()
                .min(1)
                .max(config.bulkRemovalItemLimit)
                .required(),
        }).required(),
    },
};

const migrationValidationSchemas = {
    migration: { params: MyJoi.object({ name: MyJoi.myString().myName().required() }).required() },
};

const participantValidationSchemas = {
    creation: {
        form: MyJoi.object({
            schoolInternalId: MyJoi.myString().myName().allow("").required(),
            name: MyJoi.object({
                first: MyJoi.myString().myName().required(),
                last: MyJoi.myString().myName().required(),
            }).required(),
            gender: MyJoi.myEnum().myGender().empty(null).required(),
            birthYear: MyJoi.string()
                .myBirthYear(MyJoi.ref("$event.format"), MyJoi.ref("$event.start"))
                .required(),
            class: MyJoi.myString().myShortName().allow("").required(),
            group: MyJoi.object().myFormObject().allow(null).required(),
        }).required(),
        preAuthentication: {
            body: MyJoi.object({
                participant: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                    .unknown(true)
                    .required(),
            })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            participant: MyJoi.object({
                eventId: MyJoi.string().myObjectId().required(),
                schoolInternalId: MyJoi.myString().myName(),
                name: MyJoi.object({
                    first: MyJoi.myString().myName().required(),
                    last: MyJoi.myString().myName().required(),
                }).required(),
                gender: MyJoi.myEnum().myGender().required(),
                birthYear: MyJoi.number()
                    .myBirthYear(MyJoi.ref("$event.format"), MyJoi.ref("$event.start"))
                    .required(),
                class: MyJoi.myString().myShortName(),
                groupId: MyJoi.string().myObjectId(),
            }).required(),
        }).required(),
    },
    import: {
        forms: {
            fileUpload: MyJoi.object({
                fileDatas: MyJoi.any().meta({ swaggerType: "file" }).required(),
            }).required(),
            fileSettings: MyJoi.object({
                isFirstRowContainingHeadings: MyJoi.boolean().required(),
            }).required(),
            dataSelection: MyJoi.object({
                areSchoolInternalIdsSpecified: MyJoi.boolean().required(),
                schoolInternalIdColumnIndex: MyJoi.when("areSchoolInternalIdsSpecified", {
                    is: MyJoi.valid(true),
                    then: MyJoi.number().integer(),
                }).required(),
                name: MyJoi.object({
                    firstColumnIndex: MyJoi.number().integer().required(),
                    lastColumnIndex: MyJoi.number().integer().required(),
                }).required(),
                genderColumnIndex: MyJoi.number().integer().required(),
                birthYearColumnIndex: MyJoi.number().integer().required(),
                areClassesSpecified: MyJoi.boolean().required(),
                classColumnIndex: MyJoi.when("areClassesSpecified", {
                    is: MyJoi.valid(true),
                    then: MyJoi.number().integer(),
                }).required(),
                areGroupsSpecified: MyJoi.boolean().required(),
                groupColumnIndex: MyJoi.when("areGroupsSpecified", {
                    is: MyJoi.valid(true),
                    then: MyJoi.number().integer(),
                }).required(),
            }).required(),
            data: MyJoi.object({
                areSchoolInternalIdsSpecified: MyJoi.boolean().required(),
                areClassesSpecified: MyJoi.boolean().required(),
                areGroupsSpecified: MyJoi.boolean().required(),
                participants: MyJoi.array()
                    .items(
                        MyJoi.object({
                            tempId: MyJoi.string().myNanoId().required(),
                            schoolInternalId: MyJoi.when("/areSchoolInternalIdsSpecified", {
                                is: MyJoi.valid(false),
                                then: MyJoi.forbidden(),
                                otherwise: MyJoi.myString().myName().allow(""),
                            }),
                            name: MyJoi.object({
                                first: MyJoi.myString().myName().required(),
                                last: MyJoi.myString().myName().required(),
                            }).required(),
                            gender: MyJoi.string().myGenderParser().required(),
                            birthYear: MyJoi.string()
                                .myBirthYearParser(
                                    MyJoi.ref("$event.format"),
                                    MyJoi.ref("$event.start"),
                                )
                                .required(),
                            class: MyJoi.when("/areClassesSpecified", {
                                is: MyJoi.valid(false),
                                then: MyJoi.forbidden(),
                                otherwise: MyJoi.myString().myShortName().allow(""),
                            }),
                            groupName: MyJoi.when("/areGroupsSpecified", {
                                is: MyJoi.valid(false),
                                then: MyJoi.forbidden(),
                                otherwise: MyJoi.myString().myShortName().allow(""),
                            }),
                        }),
                    )
                    .unique("tempId")
                    .min(1)
                    .max(config.event.alltimeLimits.participant)
                    .required(),
            }).required(),
        },
        preAuthentication: {
            body: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            eventId: MyJoi.string().myObjectId().required(),
            areSchoolInternalIdsSpecified: MyJoi.boolean().required(),
            areClassesSpecified: MyJoi.boolean().required(),
            areGroupsSpecified: MyJoi.boolean().required(),
            participants: MyJoi.array()
                .items(
                    MyJoi.object({
                        tempId: MyJoi.string().myNanoId().required(),
                        schoolInternalId: MyJoi.when("/areSchoolInternalIdsSpecified", {
                            is: MyJoi.valid(false),
                            then: MyJoi.forbidden(),
                            otherwise: MyJoi.myString().myName(),
                        }),
                        name: MyJoi.object({
                            first: MyJoi.myString().myName().required(),
                            last: MyJoi.myString().myName().required(),
                        }).required(),
                        gender: MyJoi.myEnum()
                            .valid(...Object.values(Gender))
                            .required(),
                        birthYear: MyJoi.number()
                            .myBirthYear(MyJoi.ref("$event.format"), MyJoi.ref("$event.start"))
                            .required(),
                        class: MyJoi.when("/areClassesSpecified", {
                            is: MyJoi.valid(false),
                            then: MyJoi.forbidden(),
                            otherwise: MyJoi.myString().myShortName(),
                        }),
                        groupName: MyJoi.when("/areGroupsSpecified", {
                            is: MyJoi.valid(false),
                            then: MyJoi.forbidden(),
                            otherwise: MyJoi.myString().myShortName(),
                        }),
                    }),
                )
                .unique("tempId")
                .min(1)
                .max(config.event.alltimeLimits.participant)
                .required(),
        }).required(),
    },
    list: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
            .concat(validationTemplates.generateListingModifiers(defaultParticipantSorting))
            .required(),
    },
    starterlistOfAllPrinting: {
        form: MyJoi.object({
            disciplineLevelOfDetail: MyJoi.myEnum()
                .myParticipantStarterlistDisciplineLevelOfDetail()
                .required(),
            isNotesFriendly: MyJoi.boolean().required(),
        }).required(),
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({
            eventId: MyJoi.string().myObjectId().required(),
            disciplineLevelOfDetail: MyJoi.myEnum()
                .myParticipantStarterlistDisciplineLevelOfDetail()
                .required(),
            isNotesFriendly: MyJoi.boolean().required(),
        }).required(),
    },
    update: {
        form: MyJoi.object({
            schoolInternalId: MyJoi.myString().myName().allow("").required(),
            name: MyJoi.object({
                first: MyJoi.myString().myName().required(),
                last: MyJoi.myString().myName().required(),
            }).required(),
            gender: MyJoi.myEnum().myGender().empty(null).required(),
            birthYear: MyJoi.string()
                .myBirthYear(MyJoi.ref("$event.format"), MyJoi.ref("$event.start"))
                .required(),
            class: MyJoi.myString().myShortName().allow("").required(),
            group: MyJoi.object().myFormObject().allow(null).required(),
        }).required(),
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            participant: MyJoi.object({
                schoolInternalId: MyJoi.myString().myName(),
                name: MyJoi.object({
                    first: MyJoi.myString().myName().required(),
                    last: MyJoi.myString().myName().required(),
                }).required(),
                gender: MyJoi.myEnum().myGender().required(),
                birthYear: MyJoi.number()
                    .myBirthYear(MyJoi.ref("$event.format"), MyJoi.ref("$event.start"))
                    .required(),
                class: MyJoi.myString().myShortName(),
                groupId: MyJoi.string().myObjectId(),
            }).required(),
        }).required(),
    },
    removal: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    manyRemoval: {
        generateForm: (confirmation) =>
            MyJoi.object({ confirmation: MyJoi.string().valid(confirmation).required() }),
        preAuthentication: {
            body: MyJoi.object({
                ids: MyJoi.array()
                    .items(MyJoi.string().myObjectId())
                    .unique()
                    .min(1)
                    .max(config.bulkRemovalItemLimit)
                    .required(),
            })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            ids: MyJoi.array()
                .items(MyJoi.string().myObjectId())
                .unique()
                .max(config.bulkRemovalItemLimit)
                .required(),
        }).required(),
    },
};

const resultValidationSchemas = {
    creation: {
        forms: {
            school: MyJoi.object({
                helper: MyJoi.object().myFormObject().allow(null).required(),
                participant: MyJoi.object().myFormObject().empty(null).required(),
                discipline: MyJoi.object().myFormObject().empty(null).required(),
                measuringType: MyJoi.string().myMeasuringTypeFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                ),
                isFailedAttempt: MyJoi.boolean().required(),
                value: MyJoi.string().myResultValueFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                    MyJoi.ref("isFailedAttempt"),
                ),
            }).required(),
            bigSchool: MyJoi.object({
                groups: MyJoi.array().required(),
                participant: MyJoi.object().myFormObject().empty(null).required(),
                disciplineCategory: MyJoi.object().myFormObject().empty(null).required(),
                discipline: MyJoi.object().myFormObject().empty(null).required(),
                measuringType: MyJoi.string().myMeasuringTypeFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                ),
                isFailedAttempt: MyJoi.boolean().required(),
                value: MyJoi.string().myResultValueFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                    MyJoi.ref("isFailedAttempt"),
                ),
            }).required(),
            helper: MyJoi.object({
                participant: MyJoi.object().myFormObject().empty(null).required(),
                disciplineCategory: MyJoi.object().myFormObject().empty(null).required(),
                discipline: MyJoi.object().myFormObject().empty(null).required(),
                measuringType: MyJoi.string().myMeasuringTypeFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                ),
                isFailedAttempt: MyJoi.boolean().required(),
                value: MyJoi.string().myResultValueFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                    MyJoi.ref("isFailedAttempt"),
                ),
            }).required(),
            bigHelper: MyJoi.object({
                groups: MyJoi.array().required(),
                participant: MyJoi.object().myFormObject().empty(null).required(),
                disciplineCategory: MyJoi.object().myFormObject().empty(null).required(),
                discipline: MyJoi.object().myFormObject().empty(null).required(),
                measuringType: MyJoi.string().myMeasuringTypeFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                ),
                isFailedAttempt: MyJoi.boolean().required(),
                value: MyJoi.string().myResultValueFormValue(
                    MyJoi.ref("$event.format"),
                    MyJoi.ref("discipline.id"),
                    MyJoi.ref("isFailedAttempt"),
                ),
            }).required(),
        },
        preAuthentication: {
            bodies: {
                school: MyJoi.object({
                    result: MyJoi.object({
                        helperId: MyJoi.string().myObjectId(),
                        participantId: MyJoi.string().myObjectId().required(),
                    })
                        .unknown(true)
                        .required(),
                })
                    .unknown(true)
                    .required(),
                helper: MyJoi.object({
                    result: MyJoi.object({
                        helperId: MyJoi.string().myObjectId().required(),
                        participantId: MyJoi.string().myObjectId().required(),
                    })
                        .unknown(true)
                        .required(),
                })
                    .unknown(true)
                    .required(),
            },
        },
        bodies: {
            school: MyJoi.object({
                result: MyJoi.object({
                    helperId: MyJoi.string().myObjectId(),
                    participantId: MyJoi.string().myObjectId().required(),
                    disciplineId: MyJoi.string()
                        .myDisciplineId(MyJoi.ref("$event.format"))
                        .required(),
                    measuringType: MyJoi.string().myMeasuringTypeBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                    ),
                    isFailedAttempt: MyJoi.boolean().required(),
                    value: MyJoi.number().myResultValueBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                        MyJoi.ref("isFailedAttempt"),
                    ),
                }).required(),
            }).required(),
            helper: MyJoi.object({
                result: MyJoi.object({
                    helperId: MyJoi.string().myObjectId().required(),
                    participantId: MyJoi.string().myObjectId().required(),
                    disciplineId: MyJoi.string()
                        .myDisciplineId(MyJoi.ref("$event.format"))
                        .required(),
                    measuringType: MyJoi.string().myMeasuringTypeBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                    ),
                    isFailedAttempt: MyJoi.boolean().required(),
                    value: MyJoi.number().myResultValueBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                        MyJoi.ref("isFailedAttempt"),
                    ),
                }).required(),
            }).required(),
        },
    },
    import: {
        preHandling: {
            body: MyJoi.object({ helperId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        inHelper: {
            preAuthentication: {
                body: MyJoi.object({
                    helperId: MyJoi.string().myObjectId().required(),
                    results: MyJoi.array()
                        .items(
                            MyJoi.object({
                                participantId: MyJoi.string().myObjectId().required(),
                            }).unknown(true),
                        )
                        .required(),
                })
                    .unknown(true)
                    .required(),
            },
            body: MyJoi.object({
                helperId: MyJoi.string().myObjectId().required(),
                results: MyJoi.array()
                    .items(
                        MyJoi.object({
                            tempId: MyJoi.string().myNanoId().required(),
                            participantId: MyJoi.string().myObjectId().required(),
                            disciplineId: MyJoi.string()
                                .myDisciplineId(MyJoi.ref("$event.format"))
                                .required(),
                            measuringType: MyJoi.string().myMeasuringTypeBodyValue(
                                MyJoi.ref("$event.format"),
                                MyJoi.ref("disciplineId"),
                            ),
                            isFailedAttempt: MyJoi.boolean().required(),
                            value: MyJoi.number().myResultValueBodyValue(
                                MyJoi.ref("$event.format"),
                                MyJoi.ref("disciplineId"),
                                MyJoi.ref("isFailedAttempt"),
                            ),
                        }),
                    )
                    .unique("tempId")
                    .min(1)
                    .max(config.result.importChunkSize)
                    .required(),
            }).required(),
        },
    },
    list: {
        preHandling: {
            queryParams: MyJoi.object({
                eventId: MyJoi.string().myObjectId(),
                helperId: MyJoi.string().myObjectId(),
            })
                .xor("eventId", "helperId")
                .unknown(true)
                .required(),
        },
        ofEvent: {
            preAuthentication: {
                queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                    .unknown(true)
                    .required(),
            },
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .concat(validationTemplates.generateListingModifiers(defaultResultSorting))
                .required(),
        },
        ofHelper: {
            preAuthentication: {
                queryParams: MyJoi.object({ helperId: MyJoi.string().myObjectId().required() })
                    .unknown(true)
                    .required(),
            },
            queryParams: MyJoi.object({ helperId: MyJoi.string().myObjectId().required() })
                .concat(validationTemplates.generateListingModifiers(defaultResultSorting))
                .required(),
        },
    },
    allOfParticipantAndDisciplineGet: {
        preAuthentication: {
            queryParams: MyJoi.object({
                participantId: MyJoi.string().myObjectId().required(),
                disciplineId: MyJoi.string().myObjectId().required(),
            })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({
            participantId: MyJoi.string().myObjectId().required(),
            disciplineId: MyJoi.string().myObjectId().required(),
        }).required(),
    },
    allExporting: {
        preAuthentication: {
            queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        queryParams: MyJoi.object({ eventId: MyJoi.string().myObjectId().required() }).required(),
    },
    update: {
        schoolForm: MyJoi.object({
            helper: MyJoi.object().myFormObject().allow(null).required(),
            participant: MyJoi.object().myFormObject().empty(null).required(),
            discipline: MyJoi.object().myFormObject().empty(null).required(),
            measuringType: MyJoi.string().myMeasuringTypeFormValue(
                MyJoi.ref("$event.format"),
                MyJoi.ref("discipline.id"),
            ),
            isFailedAttempt: MyJoi.boolean().required(),
            value: MyJoi.string().myResultValueFormValue(
                MyJoi.ref("$event.format"),
                MyJoi.ref("discipline.id"),
                MyJoi.ref("isFailedAttempt"),
            ),
        }),
        preAuthentication: {
            bodies: {
                school: MyJoi.object({
                    result: MyJoi.object({
                        helperId: MyJoi.string().myObjectId(),
                        participantId: MyJoi.string().myObjectId().required(),
                    })
                        .unknown(true)
                        .required(),
                })
                    .unknown(true)
                    .required(),
                helper: MyJoi.object({
                    result: MyJoi.object({
                        helperId: MyJoi.string().myObjectId().required(),
                        participantId: MyJoi.string().myObjectId().required(),
                    })
                        .unknown(true)
                        .required(),
                })
                    .unknown(true)
                    .required(),
            },
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        bodies: {
            school: MyJoi.object({
                result: MyJoi.object({
                    helperId: MyJoi.string().myObjectId(),
                    participantId: MyJoi.string().myObjectId().required(),
                    disciplineId: MyJoi.string()
                        .myDisciplineId(MyJoi.ref("$event.format"))
                        .required(),
                    measuringType: MyJoi.string().myMeasuringTypeBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                    ),
                    isFailedAttempt: MyJoi.boolean().required(),
                    value: MyJoi.number().myResultValueBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                        MyJoi.ref("isFailedAttempt"),
                    ),
                }).required(),
            }).required(),
            helper: MyJoi.object({
                result: MyJoi.object({
                    helperId: MyJoi.string().myObjectId().required(),
                    participantId: MyJoi.string().myObjectId().required(),
                    disciplineId: MyJoi.string()
                        .myDisciplineId(MyJoi.ref("$event.format"))
                        .required(),
                    measuringType: MyJoi.string().myMeasuringTypeBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                    ),
                    isFailedAttempt: MyJoi.boolean().required(),
                    value: MyJoi.number().myResultValueBodyValue(
                        MyJoi.ref("$event.format"),
                        MyJoi.ref("disciplineId"),
                        MyJoi.ref("isFailedAttempt"),
                    ),
                }).required(),
            }).required(),
        },
    },
    removal: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    manyRemoval: {
        generateForm: (confirmation) =>
            MyJoi.object({ confirmation: MyJoi.string().valid(confirmation).required() }),
        preAuthentication: {
            body: MyJoi.object({
                ids: MyJoi.array()
                    .items(MyJoi.string().myObjectId())
                    .unique()
                    .min(1)
                    .max(config.bulkRemovalItemLimit)
                    .required(),
            })
                .unknown(true)
                .required(),
        },
        body: MyJoi.object({
            ids: MyJoi.array()
                .items(MyJoi.string().myObjectId())
                .unique()
                .min(1)
                .max(config.bulkRemovalItemLimit)
                .required(),
        }).required(),
    },
};

const schoolValidationSchemas = {
    signUp: {
        forms: {
            accountSettings: MyJoi.object({
                verifiableMailAddress: MyJoi.myString().myMailAddress().required(),
                password: MyJoi.string().myPassword().required(),
                passwordConfirmation: MyJoi.string()
                    .myPasswordConfirmation(MyJoi.ref("password"))
                    .required(),
            }),
            schoolSettings: MyJoi.object({
                name: MyJoi.myString().myName().required(),
                type: MyJoi.myEnum().mySchoolType().required(),
            }),
            addressSettings: MyJoi.object({
                street: MyJoi.myString().myAddressState().required(),
                houseNumber: MyJoi.myString().myAddressHouseNumber().required(),
                postalCode: MyJoi.myString().myAddressPostalCode().required(),
                city: MyJoi.myString().myAddressCity().required(),
                state: MyJoi.myEnum().myAddressState().required(),
            }),
        },
        body: MyJoi.object({
            school: MyJoi.object({
                name: MyJoi.myString().myName().required(),
                verifiableMailAddress: MyJoi.myString().myMailAddress().required(),
                password: MyJoi.string().myPassword().required(),
                type: MyJoi.myEnum().mySchoolType().required(),
                address: MyJoi.object({
                    street: MyJoi.myString().myAddressState().required(),
                    houseNumber: MyJoi.myString().myAddressHouseNumber().required(),
                    postalCode: MyJoi.myString().myAddressPostalCode().required(),
                    city: MyJoi.myString().myAddressCity().required(),
                    state: MyJoi.myEnum().myAddressState().required(),
                }),
            }).required(),
        }).required(),
    },
    login: {
        body: MyJoi.object({
            mailAddress: MyJoi.myString().myMailAddress().required(),
            password: MyJoi.string().myPassword().required(),
        }).required(),
    },
    get: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    allPaymentsGet: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    update: {
        form: MyJoi.object({
            name: MyJoi.myString().myName().required(),
            type: MyJoi.myEnum().mySchoolType().required(),
            address: MyJoi.object({
                street: MyJoi.myString().myAddressState().required(),
                houseNumber: MyJoi.myString().myAddressHouseNumber().required(),
                postalCode: MyJoi.myString().myAddressPostalCode().required(),
                city: MyJoi.myString().myAddressCity().required(),
                state: MyJoi.myEnum().myAddressState().required(),
            }).required(),
        }).required(),
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            school: MyJoi.object({
                name: MyJoi.myString().myName().required(),
                type: MyJoi.myEnum().mySchoolType().required(),
                address: MyJoi.object({
                    street: MyJoi.myString().myAddressState().required(),
                    houseNumber: MyJoi.myString().myAddressHouseNumber().required(),
                    postalCode: MyJoi.myString().myAddressPostalCode().required(),
                    city: MyJoi.myString().myAddressCity().required(),
                    state: MyJoi.myEnum().myAddressState().required(),
                }).required(),
            }).required(),
        }).required(),
    },
    dpaRequest: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            signer: MyJoi.object({
                name: MyJoi.myString().myName().required(),
                mailAddress: MyJoi.myString().myMailAddress().required(),
            }).required(),
        }).required(),
    },
    dataPrivacyUpdate: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            dataPrivacy: MyJoi.object({
                isConsentFormCollected: MyJoi.boolean(),
                isDataPrivacyPolicyUpdated: MyJoi.boolean(),
                isGuideRead: MyJoi.boolean(),
            }).required(),
        }).required(),
    },
    flowUpdate: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            flow: MyJoi.object({ isOnboarded: MyJoi.boolean() }).required(),
        }).required(),
    },
    mailAddressChangeRequest: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({
            verifiableMailAddress: MyJoi.myString().myMailAddress().required(),
        }).required(),
    },
    mailAddressVerificationTokenValidation: {
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
    },
    mailAddressChangeCancellation: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    mailAddressVerification: {
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
    },
    passwordForgotten: {
        body: MyJoi.object({ mailAddress: MyJoi.myString().myMailAddress().required() }).required(),
    },
    passwordChangeRequest: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    passwordChangeTokenValidation: {
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
    },
    passwordChangeCancellation: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    passwordChange: {
        form: MyJoi.object({
            password: MyJoi.string().myPassword().required(),
            passwordConfirmation: MyJoi.string()
                .myPasswordConfirmation(MyJoi.ref("password"))
                .required(),
        }).required(),
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
        body: MyJoi.object({ password: MyJoi.string().myPassword().required() }).required(),
    },
    removalRequest: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
        body: MyJoi.object({ password: MyJoi.string().myPasswordPlaceholder().required() }).required(),
    },
    removalTokenValidation: {
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
    },
    removalCancellation: {
        preAuthentication: {
            params: MyJoi.object({ id: MyJoi.string().myObjectId().required() })
                .unknown(true)
                .required(),
        },
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
    removal: {
        params: MyJoi.object({ token: MyJoi.string().myToken().required() }).required(),
        body: MyJoi.object({ password: MyJoi.string().myPasswordPlaceholder().required() }).required(),
    },
    dpaPrinting: {
        params: MyJoi.object({ id: MyJoi.string().myObjectId().required() }).required(),
    },
};

const specialDiscountValidationSchemas = {
    creation: {
        body: MyJoi.object({
            prefix: MyJoi.myString().myName().required(),
            expires: MyJoi.date()
                .iso()
                .myDynamicMin(() => dateFns.subDays(dateFns.startOfDay(new Date()), 1))
                .myDynamicMax(() =>
                    dateFns.add(dateFns.startOfDay(new Date()), {
                        years: 3,
                        days: 1,
                    }),
                )
                .required(),
            count: MyJoi.number().integer().min(1).max(1_000).required(),
        }).required(),
    },
};

export { AppError, AppErrorType, AppErrorTypeGroupNames, CertGenerationErrorType, CertSortingPreset, CertSvg, CertTemplateElementPurpose, CertTemplateElementType, CertTemplateHorizontalTextAlignment, CertTemplateVerticalTextAlignment, CertType, ClassLevelRange, DisciplineCategoryId, DisciplineEvaluationType, DpaState, Environment, EventFormat, FlatAppErrorType, Gender, MeasuringType, MixpanelEventName, MyJoi, Order, ParticipantStarterlistDisciplineLevelOfDetail, PaymentType, RankPrintType, SchoolType, SpecialGroupId, Unit, apiRoutes, asyncEvery, asyncForEach, asyncMap, asyncSome, calculateAgeGroup, calculateFakePoints, calculateGroupLimit, calculateHelperLimit, calculateResultLimit, calculateTextSpacing, callEventFormatFunction, certConvert, certRulings, certTemplateSetConvert, certTemplateSetValidationSchemas, certValidationSchemas, certsGenerationService, checkIsCountingResult, checkIsDisciplinePreselected, checkIsEventActive, checkIsMovingDisciplineCategory, checkIsProductionEnvironment, checkIsProductionOrStagingEnvironment, checkIsResultValueInputValueEmpty, colors, competitionEventDisciplineCategories, competitionEventExampleHonorResults, competitionEventExampleParticipant, competitionEventExampleParticipant2, competitionEventExampleParticipant3, competitionEventExampleParticipationResults, competitionEventExampleWinnerResults, config, contestEventDisciplineCategories, contestEventExampleParticipant, contestEventExampleParticipant2, contestEventExampleParticipant3, contestEventExampleResults, convertHorizontalTextAlignmentToSvgTextAnchor, convertImportResultToDto, convertListResultToDto, convertVerticalTextAlignmentToSvgTextAnchor, correctPageIndex, defaultCertSorting, defaultDisciplineSorting, defaultEventSorting, defaultGroupSorting, defaultHelperSorting, defaultParticipantSorting, defaultResultSorting, delayAsync, disciplinePreselectionConvert, disciplinePreselectionValidationSchemas, escapeRegex, eventConvert, eventFormatToAgeGroupBoundariesMap, eventFormatToAgeGroupsMap, eventFormatToAllDisciplinesMap, eventFormatToCertTypeToCertTemplatePresets, eventFormatToCertTypeToExampleCertMap, eventFormatToDefaultDisciplinePreselectionMap, eventFormatToDisciplineCategoriesMap, eventFormatToExampleEventMap, eventValidationSchemas, examplePasswords, extractClassLevelRange, extractYear, flatten, formatAddress, formatAppError, formatAppErrorType, formatBool, formatCertGenerationErrorType, formatCertSortingPreset, formatCertTemplateElementType, formatCertTemplateHorizontalTextAlignment, formatCertTemplateVerticalTextAlignment, formatCertType, formatCertTypeToColor, formatClassLevelRange, formatDate, formatDisciplineEvaluationType, formatEventFormat, formatGender, formatGenderToColor, formatMeasuringType, formatOrder, formatParticipantStarterlistDisciplineLevelOfDetail, formatPaymentType, formatRankPrintType, formatResultValue, formatResultValueInputValue, formatSchoolType, formatUnit, generateApiRoutes, generateCertSorting, generateDownloadFileName, generateFullName, generateHydratedGroups, generateNanoId, generatePassword, generateSortingOptions, generateUsername, germanStates, getAllowedDisciplines, getCertTemplatePreset, getDiscipline, getDisciplineCategory, getDisciplineCategoryOfDiscipline, getEnvironment, getEventFormatObject, getMongooseReferenceId, getPreselectedDiscipline, groupConvert, groupValidationSchemas, helperAppRoutes, helperConvert, helperValidationSchemas, insertChar, mergeConfigs, migrationValidationSchemas, movingDisciplineCategoryIds, parseAndReviveJson, parseGender, parseResultValue, participantConvert, participantValidationSchemas, prefixObjectKeys, replaceChar, resultConvert, resultValidationSchemas, schoolAppRoutes, schoolConvert, schoolValidationSchemas, sortGroupsByDefaultSorting, specialDiscountConvert, specialDiscountValidationSchemas, stripeInvoiceConvert, transformToMinimalWhiteSpaceString, transformToNotEmptyString, transformToShortedString, transformToValidSearchValue, unitToFilledResultValueRegexPattern, unitToResultValueMaskMap, usernameLengthLimit, validationMessages, validationOptions, validationTemplates, version };
//# sourceMappingURL=common.js.map
